/**
 * AdminView - Secured Admin Dashboard
 * Features:
 * 1. PIN Security Gate (6-digit PIN required)
 * 2. User Management (existing webapp feature)
 * 3. Client List with Edit (from extension)
 *
 * Roles:
 * - ADMIN: Full access to all features
 * - MODERATOR: Read-only access to Users & Clients tabs
 * - CLIENT: No access
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { klikBiasa } from '../utils/klikBiasa';
import { namaPakejOrder, jenisOrder, adalahFlowUltra, adalahUpgrade } from '../utils/pakejOrder';
import { SUMBER_LABEL, adalahOrderManual, sumberOrder, padanSumber, dalamJulatTarikh, jumlahDibayar, pecahanSumber } from '../utils/sumberOrder';
import { PROMO_AI_PRO_ID } from '../utils/promoAiPro';
// Kuota = default pakej + slot tambahan (polisi 2026-09-15). KEMBAR: functions/kuotaPakej.js
import { HAD_KUOTA, HAD_SLOT_TAMBAHAN, bacaSlotTambahan, kuotaSelepasBayar, teksPecahanKuota } from '../utils/kuotaPakej';
import { httpsCallable } from 'firebase/functions';
import { functions, auth, db } from '../firebase';
import { doc, setDoc, getDoc, updateDoc, collection, getDocs, deleteDoc, addDoc, serverTimestamp, query, where, writeBatch, orderBy, limit as firestoreLimit, startAfter, documentId, getCountFromServer, getAggregateFromServer, sum, count } from 'firebase/firestore';
import {
    ShieldCheck, Users, Crown, Zap, RefreshCw, Search,
    ChevronDown, ChevronLeft, ChevronRight, Check, X, AlertTriangle,
    CreditCard, Clock, Calendar, Key, Settings,
    Lock, Copy, Plus, Trash2, Edit3,
    ChevronUp, Save, Link2, Mail,
    MoreVertical, Tag, Coins, Monitor,
    FileText, MessageCircle, XCircle, Loader2, Maximize2, Inbox,
    LayoutDashboard, Gift, TrendingUp, PieChart, UserPlus, UserMinus, Package, Facebook, Bot
} from 'lucide-react';
import { buildActivationDeeplink, buildActivationMessage, normalizeWaNumber, buildRenewalReminderDeeplink, buildRenewalReminderMessage } from '../utils/whatsappTemplate';
import EmailBlastTab from './EmailBlastTab';
import FlowUltraTab from './FlowUltraTab';
import { jualanManualFlow } from '../services/flowUltraService';
import FbHealthTab from './FbHealthTab';
import RobotTetapanTab from './RobotTetapanTab';   // ★ 2026-09-24 tetapan robot dari server (PLAN_ROBOT_DARI_SERVER.md)
import KempenRekodView from './KempenRekodView';

// ═══ Pipeline stats — date helpers (waktu KL, sepadan dgn getDayKeyMY di functions/index.js) ═══
const PIPE_MY_OFFSET_MS = 8 * 60 * 60 * 1000;
const klDayKey = (d = new Date()) => {
    const my = new Date(d.getTime() + PIPE_MY_OFFSET_MS);
    return `${my.getUTCFullYear()}-${String(my.getUTCMonth() + 1).padStart(2, '0')}-${String(my.getUTCDate()).padStart(2, '0')}`;
};
const klMonthStartKey = () => {
    const my = new Date(Date.now() + PIPE_MY_OFFSET_MS);
    return `${my.getUTCFullYear()}-${String(my.getUTCMonth() + 1).padStart(2, '0')}-01`;
};
// Day-key KL dari epoch ms (untuk kalendar renewal) — sepadan format klDayKey.
const klDayKeyOf = (ms) => {
    if (ms == null) return null;
    const my = new Date(ms + PIPE_MY_OFFSET_MS);
    return `${my.getUTCFullYear()}-${String(my.getUTCMonth() + 1).padStart(2, '0')}-${String(my.getUTCDate()).padStart(2, '0')}`;
};

// ═══ RISIKO PELANGGAN — ambang + pengelasan ═══
// Asas nombor (analisa 147 lesen yang dah tamat, Ogos 2026):
//   • Pelanggan yang TIDAK sambung berhenti generate purata 15 hari SEBELUM lesen tamat.
//     Sebab tu ambang senyap = 14 hari — ia menangkap mereka tepat sebelum terlambat.
//   • Kadar sambung ikut penggunaan: 0 guna = 4% · 1-20 = 7% · 21-100 = 18% · 101-500 = 25% · 500+ = 46%.
//     Sebab tu "guna sedikit" (<20) dikira berisiko walaupun mereka baru guna semalam.
const RISK_SILENT_DAYS = 14;   // senyap lebih ni  → Bahaya
const RISK_WARN_DAYS = 7;      // senyap 8-14 hari → Perlu perhatian
const RISK_LOW_RUNS = 20;      // guna kurang ni sepanjang hayat → Perlu perhatian
const RISK_LAPSED_DAYS = 30;   // lesen tamat dlm 30 hari lepas → masih berbaloi win-back
const RISK_MAX_ROWS = 1200;    // had baris disimpan dlm cache (elak had 1MB satu doc Firestore)

// ms daripada pelbagai bentuk timestamp Firestore (Timestamp | {seconds} | number | string)
const tsToMs = (v) => {
    if (!v) return null;
    if (typeof v === 'number') return v;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (v.seconds) return v.seconds * 1000;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d.getTime();
};

// Pulangkan { risk, reason }. risk: 'red' (bahaya) | 'amber' (perhatian) | 'green' (sihat).
const classifyRisk = (runs, lastRunMs, nowMs) => {
    if (!runs) return { risk: 'red', reason: 'Tak pernah generate' };
    const days = lastRunMs == null ? null : Math.floor((nowMs - lastRunMs) / 86400000);
    if (days == null) return { risk: 'red', reason: 'Tiada rekod guna' };
    if (days > RISK_SILENT_DAYS) return { risk: 'red', reason: `Senyap ${days} hari` };
    if (days > RISK_WARN_DAYS) return { risk: 'amber', reason: `Senyap ${days} hari` };
    if (runs < RISK_LOW_RUNS) return { risk: 'amber', reason: `Guna sedikit (${runs}×)` };
    return { risk: 'green', reason: 'Aktif' };
};
const RISK_ORDER = { red: 0, amber: 1, green: 2 };

// ═══ Ringkasan Bisnes — helper bulan (waktu KL) + definisi metrik graf ═══
const BIZ_MONTH_MY = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'];
const klMonthKeyOf = (ms) => { const d = new Date(ms + PIPE_MY_OFFSET_MS); return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`; };
const bizMonthLabel = (mo) => { const [y, m] = mo.split('-').map(Number); return `${BIZ_MONTH_MY[m - 1]} ${String(y).slice(2)}`; };
const bizMonthShort = (mo) => BIZ_MONTH_MY[parseInt(mo.split('-')[1], 10) - 1];
const bizMonthStartMs = (mo) => { const [y, m] = mo.split('-').map(Number); return Date.UTC(y, m - 1, 1) - PIPE_MY_OFFSET_MS; };
const bizNextMonth = (mo) => { let [y, m] = mo.split('-').map(Number); m++; if (m > 12) { m = 1; y++; } return `${y}-${String(m).padStart(2, '0')}`; };
const bizMonthEndMs = (mo) => bizMonthStartMs(bizNextMonth(mo)) - 1;
const bizMonthsFromJan = () => {
    const now = new Date(Date.now() + PIPE_MY_OFFSET_MS);
    const curY = now.getUTCFullYear(), curM = now.getUTCMonth() + 1;
    const list = []; let y = 2026, m = 1;
    while (y < curY || (y === curY && m <= curM)) { list.push(`${y}-${String(m).padStart(2, '0')}`); m++; if (m > 12) { m = 1; y++; } }
    return list;
};
// ═══ Pipeline: label + susun pecahan module untuk 3 kotak Dashboard ═══
const PIPELINE_MODULE_LABELS = {
    ai8saat: 'AI8SAAT',
    'ai16saat-half': 'AI16SAAT',   // id sebenar module AI16SAAT (registry modules/)
    ai16saat: 'AI16SAAT',          // id lama (jaga-jaga produk legacy)
    'ai16saat-full': 'Home Niche', // id lama Home Niche (pre-rename → homeniche)
    homeniche: 'Home Niche',
    goyang: 'GOYANG',
    smartcustom: 'Smart Custom',
    smart: 'Smart Custom',
    flownity: 'FLOWNITY',
    unknown: 'Lain-lain',
};
const pipelineModuleLabel = (id) => PIPELINE_MODULE_LABELS[id] || (id ? String(id).toUpperCase() : 'Lain-lain');
// byModule = { [id]: { paidCalls, trialCalls, adminCalls, total } }; pulangkan baris > 0 disusun turun.
// field = 'paidCalls' | 'trialCalls' | 'sum'. 'sum' = paid+trial SAHAJA (bukan field `total` doc yang
// termasuk adminCalls) supaya jumlah pecahan sentiasa padan dgn angka besar Jumlah di atasnya.
const pipelineModuleRows = (byModule, field) => {
    if (!byModule || typeof byModule !== 'object') return [];
    return Object.entries(byModule)
        .map(([id, v]) => ({
            id,
            count: field === 'sum'
                ? (((v && Number(v.paidCalls)) || 0) + ((v && Number(v.trialCalls)) || 0))
                : ((v && Number(v[field])) || 0),
        }))
        .filter((r) => r.count > 0)
        .sort((a, b) => b.count - a.count);
};

// Metrik yang graf trend boleh papar. Bulan tiada data = 0 (zero-fill).
const BIZ_METRICS = [
    { key: 'revenue', label: 'Duit Masuk', money: true, color: '#3B6D11', get: r => r.revenueSen },
    { key: 'signup', label: 'Signup Free', money: false, color: '#185FA5', get: r => r.signups },
    { key: 'paidActive', label: 'Paid Aktif', money: false, color: '#534AB7', get: r => r.paidActiveEOM },
    { key: 'renew', label: 'Renew', money: false, color: '#0F6E56', get: r => r.renewCount },
    { key: 'notRenew', label: 'Tak Renew', money: false, color: '#A32D2D', get: r => r.notRenewed },
    { key: 'fighter', label: 'Fighter (RM)', money: true, color: '#854F0B', get: r => r.fighterRevenueSen },
    // Baris cache LAMA tiada medan ini — guard || 0, kalau tidak graf memapar celah kosong
    // sehingga admin menekan Refresh Ringkasan Bisnes buat kali pertama selepas deploy.
    { key: 'smart', label: 'SMART (RM)', money: true, color: '#993556', get: r => r.smartRevenueSen || 0 },
];

// ═══ Kalendar Renewal — bulatan per hari: 🟡 akan tamat · 🟢 dah bayar renew · 🔴 tak sambung ═══
// Sumber: cache Ringkasan Bisnes (config/business_summary.renewalDaily). TIADA bacaan Firestore tambahan —
// dikira sekali masa admin tekan Refresh Ringkasan Bisnes. Kuning/merah ikut tarikh lesen tamat
// (masa depan = kuning, dah lepas = merah); hijau ikut hari pelanggan bayar sambung.
function RenewalCalendar({ daily }) {
    const WD = ['Ahd', 'Isn', 'Sel', 'Rab', 'Kha', 'Jum', 'Sab'];
    const todayKey = klDayKey();
    const [ty, tm] = todayKey.split('-').map(Number); // tahun & bulan semasa (KL)
    const [view, setView] = useState({ y: ty, m: tm }); // m: 1-based
    const firstDow = new Date(Date.UTC(view.y, view.m - 1, 1)).getUTCDay(); // 0 = Ahad
    const daysInMonth = new Date(Date.UTC(view.y, view.m, 0)).getUTCDate();
    const dk = (d) => `${view.y}-${String(view.m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const shift = (delta) => setView(v => {
        let m = v.m + delta, y = v.y;
        if (m < 1) { m = 12; y--; } else if (m > 12) { m = 1; y++; }
        return { y, m };
    });
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    // Jumlah bulan (ringkasan header) — kiraan + RM (sen)
    let sy = 0, sg = 0, sr = 0, sys = 0, sgs = 0, srs = 0;
    for (let d = 1; d <= daysInMonth; d++) { const t = daily && daily[dk(d)]; if (t) { sy += t.y || 0; sg += t.g || 0; sr += t.r || 0; sys += t.ys || 0; sgs += t.gs || 0; srs += t.rs || 0; } }
    const rmShort = (sen) => { const rm = (sen || 0) / 100; return rm >= 1000 ? 'RM' + (rm / 1000).toFixed(1) + 'k' : 'RM' + Math.round(rm); };
    const rmFull = (sen) => 'RM' + Math.round((sen || 0) / 100).toLocaleString();
    const Chip = ({ n, sen, dot, text }) => n > 0 ? (
        <span className={`inline-flex items-center gap-1 ${text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${dot}`}></span>{n}
            <span className="opacity-60 font-normal">{rmShort(sen)}</span>
        </span>
    ) : null;
    const isCurMonth = view.y === ty && view.m === tm;
    // ── Strip ringkasan: 7 hari depan (dari hari ini, tak ikut bulan paparan) + kadar renew bulan paparan ──
    let w7c = 0, w7s = 0;
    for (let i = 0; i < 7; i++) {
        const t = daily && daily[klDayKeyOf(Date.now() + i * 86400000)];
        if (t) { w7c += t.y || 0; w7s += t.ys || 0; }
    }
    const renewRate = (sg + sr) > 0 ? Math.round((sg / (sg + sr)) * 100) : null;
    // ── Drill-down: klik hari → senarai orang (fetch on-demand — beberapa doc sahaja, murah) ──
    const [dayModal, setDayModal] = useState(null); // { key, loading, error?, due: [...], paid: [...] } | null
    const openDay = async (key) => {
        setDayModal({ key, loading: true, due: [], paid: [] });
        try {
            const dayStart = new Date(`${key}T00:00:00+08:00`);
            const dayEnd = new Date(`${key}T23:59:59.999+08:00`);
            // 1) Lesen yang tamat pada tarikh ni (kuning/merah)
            const licSnap = await getDocs(query(collection(db, 'licenses'),
                where('expiresAt', '>=', dayStart), where('expiresAt', '<=', dayEnd)));
            const due = licSnap.docs.map(d => {
                const x = d.data();
                const exp = x.expiresAt?.toDate ? x.expiresAt.toDate() : new Date(x.expiresAt);
                return {
                    uid: d.id,
                    email: x.email || '(tiada email)',
                    expiresAt: exp,
                    isYearly: x.plan === 'yearly' || x.licType === 'yearly',
                    expired: exp.getTime() <= Date.now(),
                    whatsappNumber: ''
                };
            });
            // 2) No. WhatsApp dari users doc (chunk 30 — had 'in' Firestore)
            const uids = due.map(i => i.uid);
            for (let i = 0; i < uids.length; i += 30) {
                const chunk = uids.slice(i, i + 30);
                if (!chunk.length) continue;
                const uSnap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
                const map = {};
                uSnap.docs.forEach(d => { map[d.id] = d.data(); });
                due.forEach(item => { if (map[item.uid]) item.whatsappNumber = map[item.uid].whatsappNumber || ''; });
            }
            // 3) Bayaran renew pada tarikh ni (hijau) — range query 1 field sahaja (elak composite index), tapis client-side
            const paid = [];
            const ordSnap = await getDocs(query(collection(db, 'orders'),
                where('paidAt', '>=', dayStart), where('paidAt', '<=', dayEnd)));
            ordSnap.docs.forEach(d => {
                const x = d.data();
                if (x.status !== 'paid') return;
                if (!(x.isRenewal === true || (x.purchaseType || '').toLowerCase() === 'renewal')) return;
                if ((x.plan || '').toLowerCase() === 'flownity_addon') return;
                // Flow Ultra bukan lesen SESAAT — sama seperti bulatan hijau (fetchBusinessSummary).
                if (adalahFlowUltra(x)) return;
                paid.push({ email: x.email || '(tiada email)', sen: x.paidAmountSen || x.amountSen || 0 });
            });
            const subSnap = await getDocs(query(collection(db, 'payment_submissions'),
                where('activatedAt', '>=', dayStart), where('activatedAt', '<=', dayEnd)));
            subSnap.docs.forEach(d => {
                const x = d.data();
                if (x.status !== 'activated' || (x.type || 'new').toLowerCase() !== 'renewal') return;
                paid.push({ email: x.email || '(tiada email)', sen: (x.amountSen != null) ? x.amountSen : (typeof x.amount === 'number' ? (x.amount < 1000 ? Math.round(x.amount * 100) : x.amount) : 0) });
            });
            setDayModal({ key, loading: false, due, paid });
        } catch (err) {
            console.error('[RenewalCalendar] Fetch senarai hari gagal:', err);
            setDayModal(m => (m && m.key === key) ? { ...m, loading: false, error: true } : m);
        }
    };
    const modalDateLabel = (key) => { const [yy, mm, dd] = key.split('-').map(Number); return `${dd} ${BIZ_MONTH_MY[mm - 1]} ${yy}`; };
    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="flex items-center gap-1.5">
                    <button onClick={() => shift(-1)} className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" aria-label="Bulan sebelum"><ChevronLeft size={16} /></button>
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 w-24 text-center">{BIZ_MONTH_MY[view.m - 1]} {view.y}</span>
                    <button onClick={() => shift(1)} className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" aria-label="Bulan seterus"><ChevronRight size={16} /></button>
                    {!isCurMonth && (
                        <button onClick={() => setView({ y: ty, m: tm })} className="text-[11px] px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800">Hari ini</button>
                    )}
                </div>
                <div className="flex items-center gap-2.5 text-[11px] font-semibold flex-wrap">
                    <span className="text-amber-600 dark:text-amber-400">🟡 {sy} · {rmFull(sys)}</span>
                    <span className="text-emerald-600 dark:text-emerald-400">🟢 {sg} · {rmFull(sgs)}</span>
                    <span className="text-rose-600 dark:text-rose-400">🔴 {sr} · {rmFull(srs)}</span>
                </div>
            </div>
            {/* Strip ringkasan: 7 hari depan + kadar renew bulan paparan */}
            <div className="flex items-center gap-2 mb-3 flex-wrap text-[11px]">
                <span className="px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-900/15 text-amber-700 dark:text-amber-400 font-medium">
                    📅 7 hari depan: <b>{w7c}</b> lesen · <b>{rmFull(w7s)}</b> bakal tamat
                </span>
                {renewRate != null && (
                    <span className={`px-2 py-1 rounded-md font-medium ${renewRate >= 55 ? 'bg-emerald-50 dark:bg-emerald-900/15 text-emerald-700 dark:text-emerald-400' : renewRate >= 45 ? 'bg-amber-50 dark:bg-amber-900/15 text-amber-700 dark:text-amber-400' : 'bg-rose-50 dark:bg-rose-900/15 text-rose-700 dark:text-rose-400'}`}>
                        Kadar renew {BIZ_MONTH_MY[view.m - 1]}: <b>{renewRate}%</b> ({sg} sambung · {sr} tak)
                    </span>
                )}
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
                {WD.map(w => <div key={w} className="text-center text-[10px] text-slate-400 font-medium py-0.5">{w}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((d, i) => {
                    if (d == null) return <div key={`e${i}`} className="min-h-[60px]"></div>;
                    const key = dk(d);
                    const t = (daily && daily[key]) || null;
                    const isToday = key === todayKey;
                    const hasData = !!(t && (t.y || t.g || t.r));
                    return (
                        <div key={key} onClick={hasData ? () => openDay(key) : undefined} title={hasData ? 'Klik untuk senarai' : undefined} className={`min-h-[60px] rounded-lg border p-1 flex flex-col ${isToday ? 'border-indigo-400 dark:border-indigo-500 ring-1 ring-indigo-300 dark:ring-indigo-600' : 'border-slate-100 dark:border-slate-800'} bg-slate-50/50 dark:bg-slate-800/30 ${hasData ? 'cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-600 hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10' : ''}`}>
                            <span className={`text-[10px] leading-none mb-1 ${isToday ? 'font-bold text-indigo-600 dark:text-indigo-400' : 'text-slate-400'}`}>{d}</span>
                            <div className="flex flex-col gap-0.5 text-[10px] font-semibold">
                                <Chip n={t?.y || 0} sen={t?.ys || 0} dot="bg-amber-400" text="text-amber-600 dark:text-amber-400" />
                                <Chip n={t?.g || 0} sen={t?.gs || 0} dot="bg-emerald-500" text="text-emerald-600 dark:text-emerald-400" />
                                <Chip n={t?.r || 0} sen={t?.rs || 0} dot="bg-rose-500" text="text-rose-600 dark:text-rose-400" />
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-500 flex-wrap">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block"></span>Akan renew (bakal tamat)</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span>Dah renew (hari bayar)</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block"></span>Tak renew (tamat, tak sambung)</span>
                <span className="text-slate-400">· Klik kotak hari untuk senarai</span>
            </div>
            {/* Modal drill-down: senarai orang untuk hari yang diklik */}
            {dayModal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDayModal(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 w-full max-w-md max-h-[80vh] flex flex-col shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                            <span className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2"><Calendar size={15} className="text-indigo-500" /> {modalDateLabel(dayModal.key)}</span>
                            <button onClick={() => setDayModal(null)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400"><X size={16} /></button>
                        </div>
                        <div className="overflow-y-auto p-4 space-y-4">
                            {dayModal.loading ? (
                                <p className="text-sm text-slate-400 text-center py-6">Memuatkan…</p>
                            ) : dayModal.error ? (
                                <p className="text-sm text-rose-500 text-center py-6">Gagal muat data. Tutup dan cuba lagi.</p>
                            ) : (
                                <>
                                    {dayModal.due.length > 0 && (
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Lesen tamat pada tarikh ini ({dayModal.due.length})</p>
                                            <div className="space-y-2">
                                                {dayModal.due.map(item => {
                                                    const dayLeft = Math.ceil((item.expiresAt.getTime() - Date.now()) / 86400000);
                                                    const wa = buildRenewalReminderDeeplink({
                                                        userWhatsapp: item.whatsappNumber,
                                                        email: item.email,
                                                        expiresAt: item.expiresAt,
                                                        daysLeft: item.expired ? 0 : dayLeft
                                                    });
                                                    return (
                                                        <div key={item.uid} className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2">
                                                            <span className="shrink-0">{item.expired ? '🔴' : '🟡'}</span>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{item.email}</p>
                                                                <p className="text-[11px] text-slate-400 truncate">{item.isYearly ? 'Fighter · RM799' : 'Premium · RM99'} · {item.whatsappNumber || 'tiada no. WhatsApp'}</p>
                                                            </div>
                                                            {wa && (
                                                                <a href={wa} target="_blank" rel="noreferrer" className="shrink-0 flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium"><MessageCircle size={12} /> WhatsApp</a>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {dayModal.paid.length > 0 && (
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Bayar renew pada tarikh ini ({dayModal.paid.length})</p>
                                            <div className="space-y-2">
                                                {dayModal.paid.map((item, i) => (
                                                    <div key={i} className="flex items-center gap-2 bg-emerald-50/60 dark:bg-emerald-900/10 rounded-lg px-3 py-2">
                                                        <span className="shrink-0">🟢</span>
                                                        <p className="flex-1 min-w-0 text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{item.email}</p>
                                                        <span className="shrink-0 text-xs font-bold text-emerald-700 dark:text-emerald-400">{rmFull(item.sen)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {dayModal.due.length === 0 && dayModal.paid.length === 0 && (
                                        <p className="text-sm text-slate-400 text-center py-6">Tiada rekod untuk tarikh ini.</p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Carta trend bulanan interaktif — hover papar tooltip (nilai + bulan), garis panduan menegak, titik membesar.
function BizTrendChart({ monthly, metricKey, selMonth }) {
    const [hover, setHover] = useState(null);
    const gm = BIZ_METRICS.find(m => m.key === metricKey) || BIZ_METRICS[0];
    const series = monthly.map(r => ({ month: r.month, v: gm.get(r) || 0 }));
    const maxV = Math.max(...series.map(s => s.v), 1);
    const W = 680, H = 240, padL = 58, padR = 18, padT = 18, padB = 30;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    const n = series.length;
    const X = (i) => n <= 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW;
    const Y = (v) => padT + plotH - (v / maxV) * plotH;
    const rm = (sen) => 'RM ' + Math.round((sen || 0) / 100).toLocaleString();
    const fmtY = (v) => gm.money ? 'RM' + Math.round(v / 100000) + 'k' : String(Math.round(v));
    const fmtVal = (v) => gm.money ? rm(v) : Math.round(v).toLocaleString();
    const pts = series.map((s, i) => `${X(i)},${Y(s.v)}`).join(' ');
    const areaPts = `${X(0)},${Y(0)} ${pts} ${X(n - 1)},${Y(0)}`;
    const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => maxV * f);
    const gid = 'bizgrad-' + metricKey;
    const onMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        if (!rect.width) return;
        const px = ((e.clientX - rect.left) / rect.width) * W;
        let best = 0, bd = Infinity;
        for (let i = 0; i < n; i++) { const dd = Math.abs(X(i) - px); if (dd < bd) { bd = dd; best = i; } }
        setHover(best);
    };
    const hv = (hover != null && series[hover]) ? series[hover] : null;
    let box = null;
    if (hv) {
        const hx = X(hover), hy = Y(hv.v), bw = 122, bh = 42;
        const bx = Math.max(padL - 6, Math.min(hx - bw / 2, W - padR - bw));
        const by = (hy - bh - 12 < 2) ? hy + 14 : hy - bh - 12;
        box = { hx, hy, bx, by, bw, bh };
    }
    return (
        <div className="text-slate-400">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ cursor: 'crosshair' }}
                onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
                <defs>
                    <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={gm.color} stopOpacity="0.18" />
                        <stop offset="100%" stopColor={gm.color} stopOpacity="0" />
                    </linearGradient>
                </defs>
                {ticks.map((t, i) => (
                    <g key={'t' + i}>
                        <line x1={padL} y1={Y(t)} x2={W - padR} y2={Y(t)} stroke="currentColor" strokeOpacity="0.13" />
                        <text x={padL - 8} y={Y(t) + 3} fontSize="10" fill="currentColor" textAnchor="end">{fmtY(t)}</text>
                    </g>
                ))}
                <polygon points={areaPts} fill={`url(#${gid})`} />
                <polyline points={pts} fill="none" stroke={gm.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                {series.map((s, i) => (
                    <text key={'x' + i} x={X(i)} y={H - 9} fontSize="10" fill="currentColor" textAnchor="middle" opacity={(n > 14 && (i % 2)) ? 0 : 0.9}>{bizMonthShort(s.month)}</text>
                ))}
                {series.map((s, i) => (
                    <circle key={'d' + i} cx={X(i)} cy={Y(s.v)} r={s.month === selMonth ? 4 : 2.5} fill={gm.color} opacity={s.month === selMonth ? 1 : 0.5} />
                ))}
                {box && (
                    <g>
                        <line x1={box.hx} y1={padT} x2={box.hx} y2={padT + plotH} stroke="currentColor" strokeOpacity="0.35" strokeDasharray="3 3" />
                        <circle cx={box.hx} cy={box.hy} r="5.5" fill={gm.color} stroke="#fff" strokeWidth="2.5" />
                        <rect x={box.bx} y={box.by} width={box.bw} height={box.bh} rx="8" fill="#1f2937" />
                        <text x={box.bx + 12} y={box.by + 18} fontSize="13" fontWeight="600" fill="#ffffff">{fmtVal(hv.v)}</text>
                        <text x={box.bx + 12} y={box.by + 33} fontSize="10.5" fill="#cbd5e1">{bizMonthLabel(hv.month)}</text>
                    </g>
                )}
            </svg>
            <div className="text-xs text-slate-500 mt-1">
                {hv
                    ? <>Hover: <b style={{ color: gm.color }}>{fmtVal(hv.v)}</b> pada {bizMonthLabel(hv.month)}</>
                    : <>Gerakkan tetikus atas graf untuk lihat nilai · bulan tiada jualan = 0 (zero-fill).</>}
            </div>
        </div>
    );
}

// ═══ Row Action Menu (reusable dropdown for table actions) ═══
const RowActionMenu = ({ children }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', handler);
        document.addEventListener('keydown', onEsc);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('keydown', onEsc);
        };
    }, [open]);
    return (
        <div className="relative inline-block" ref={ref}>
            <button
                onClick={() => setOpen(o => !o)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title="Lagi"
            >
                <MoreVertical size={14} />
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 z-50 min-w-[170px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1">
                    {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
                </div>
            )}
        </div>
    );
};

const MenuItem = ({ onClick, icon: Icon, children, variant = 'default', disabled }) => {
    const variants = {
        default: 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800',
        danger: 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20',
        warning: 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20',
    };
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant] || variants.default}`}
        >
            {Icon && <Icon size={13} />}
            <span>{children}</span>
        </button>
    );
};
import { getUserRole, canAccessAdmin, hasPermission, PERMISSIONS, ROLES, ROLE_DISPLAY } from '../utils/roles';
import { useAdminPin } from './AdminPinGate';
import AdminRekodCiriPercuma from './AdminRekodCiriPercuma';
import { macamNombor, calonCarianWhatsapp, kemasNomborMy } from '../utils/nomborTelefon';

const adminListUsersFn = httpsCallable(functions, 'adminListUsers');
const adminUpdateUserFn = httpsCallable(functions, 'adminUpdateUser');
const adminResetChromeProfilesFn = httpsCallable(functions, 'adminResetChromeProfiles');
const adminApprovePaymentFn = httpsCallable(functions, 'adminApprovePayment');
const adminRejectSubmissionFn = httpsCallable(functions, 'adminRejectSubmission');
const adminGenerateResetLinksFn = httpsCallable(functions, 'generateAdminResetLinks');
const adminDeleteUserFn = httpsCallable(functions, 'adminDeleteUser');
const backfillCampaignCountsFn = httpsCallable(functions, 'backfillCampaignCounts');
const adminApplyCampaignPresetToUsersFn = httpsCallable(functions, 'adminApplyCampaignPresetToUsers');
const adminSendRenewalReminderFn = httpsCallable(functions, 'adminSendRenewalReminder');
const adminMarkReminderManuallySentFn = httpsCallable(functions, 'adminMarkReminderManuallySent');

const SUPER_ADMIN_EMAIL = 'masterzuheir@gmail.com';

const AdminView = ({ initialTab = 'dashboard' }) => {
    const { lockSession } = useAdminPin();
    const navigate = useNavigate();

    // ═══ EXISTING WEBAPP STATE ═══
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Carian tab User ikut corak Rekod Jualan: taip dlm kotak (usersSearchInput),
    // tekan Enter/Cari → term KOMITED masuk searchTerm → query terus ke server.
    const [searchTerm, setSearchTerm] = useState('');
    const [usersSearchInput, setUsersSearchInput] = useState('');
    const [filterTier, setFilterTier] = useState('all');
    const [updatingUser, setUpdatingUser] = useState(null);
    const [authUser, setAuthUser] = useState(null);
    const [authLinkResult, setAuthLinkResult] = useState('');
    const [authLinkType, setAuthLinkType] = useState('');
    const [authLinkLoading, setAuthLinkLoading] = useState(false);
    const [newEmailInput, setNewEmailInput] = useState('');
    const [userRole, setUserRole] = useState(ROLES.CLIENT);
    const [isAuthorized, setIsAuthorized] = useState(false);

    // ═══ USERS PAGINATION ═══
    const PAGE_SIZE = 50;
    const ADMIN_MOD_COUNT = 2; // derived from ADMIN_EMAILS + MODERATOR_EMAILS in roles.js
    const cursorStackRef = useRef([]); // cursorStack[N] = last doc of page N (used as startAfter for page N+1)
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [totalCounts, setTotalCounts] = useState({ total: 0, paid: 0 });
    // 'page' = paginated 50/halaman. 'all' = semua user dimuat (bila filter tier/sort aktif).
    // 'search' = hasil carian pintar (query terus ke server — corak Rekod Jualan).
    const [usersScope, setUsersScope] = useState('page');
    const [lastRefreshedUsers, setLastRefreshedUsers] = useState(null);

    // ═══ ADMIN TABS ═══
    /* Tab datang daripada ALAMAT (`/admin/system/{tab}`), jadi muat semula,
       bookmark dan butang Back semuanya berfungsi. Keadaan tempatan kekal
       supaya render tidak menunggu router, tetapi ia SENTIASA mengikut
       alamat — itulah tugas useEffect penyelaras di bawah. Tanpa penyelaras
       itu, butang Back menukar alamat tetapi skrin tidak bergerak. */
    const [activeAdminTab, setActiveAdminTab] = useState(initialTab);
    useEffect(() => { setActiveAdminTab(initialTab); }, [initialTab]);
    /* ⛔ Tukar tab = tukar ALAMAT, bukan setActiveAdminTab terus. Memanggil
       setState sahaja menukar skrin TANPA menukar alamat — dan alamat itulah
       yang pengguna bookmark, jadi bookmarknya membawa ke tab yang salah. */
    const bukaTab = (id) => navigate(`/admin/system/${id}`);

    // ═══ DASHBOARD — pipeline generation stats (today + julat tarikh) ═══
    const [pipelineToday, setPipelineToday] = useState(null);          // { paidCalls, trialCalls, adminCalls }
    const [pipelineStart, setPipelineStart] = useState(klMonthStartKey());
    const [pipelineEnd, setPipelineEnd] = useState(klDayKey());
    const [pipelineRange, setPipelineRange] = useState(null);          // { rows:[{date,paid,trial,total}], paid, trial, total, byModule, moduleDays }
    const [pipelineLoading, setPipelineLoading] = useState(false);

    // ═══ DASHBOARD — Ringkasan Bisnes (jualan bulanan + graf trend) ═══
    const [bizLoading, setBizLoading] = useState(false);
    const [bizMonthly, setBizMonthly] = useState(null);   // array {month, revenueSen, ...} dari Jan 2026 → bulan semasa
    const [bizPackages, setBizPackages] = useState(null); // { premium, fighter, lama } — pakej aktif semasa
    const [bizSelMonth, setBizSelMonth] = useState(null); // month key dipilih utk kotak
    const [bizGraphMetric, setBizGraphMetric] = useState('revenue');
    const [bizCachedAt, setBizCachedAt] = useState(null); // ms bila cache Ringkasan Bisnes dikira; null = belum ada cache
    const [bizRenewalDaily, setBizRenewalDaily] = useState(null); // { 'YYYY-MM-DD': {y,g,r} } — kalendar renewal (dikira sekali dgn Ringkasan Bisnes)

    // ═══ RISIKO — senarai kerja harian: siapa nak lari, siapa boleh upsell ═══
    // Sumber: licenses + pipelineUsage + users. Dikira HANYA bila admin tekan Refresh,
    // hasil disimpan dlm config/risk_summary (admin-only ikut rule config/{docId}).
    const [riskRows, setRiskRows] = useState(null);      // [{uid,email,wa,plan,expMs,daysLeft,runs,lastRunMs,risk,reason,lapsed}]
    const [riskCounts, setRiskCounts] = useState(null);  // {total,red,amber,green,neverUsed,expiring30,lapsed}
    const [riskLoading, setRiskLoading] = useState(false);
    const [riskCachedAt, setRiskCachedAt] = useState(null);
    const [riskTruncated, setRiskTruncated] = useState(0); // >0 = baris dipotong drpd cache (jangan senyap)
    const [riskFilter, setRiskFilter] = useState('red');
    const [riskSearch, setRiskSearch] = useState('');
    const [riskCopied, setRiskCopied] = useState('');

    // ═══ Kempen WhatsApp (Cloud API, mod Coexistence) ═══
    const [waPanelOpen, setWaPanelOpen] = useState(false);
    const [waCampaignName, setWaCampaignName] = useState('');
    const [waTemplate, setWaTemplate] = useState('peringatan_renewal');
    const [waLang, setWaLang] = useState('ms');
    const [waBusy, setWaBusy] = useState(false);
    const [waResult, setWaResult] = useState(null);

    // ═══ REKOD JUALAN (orders — Billplz gateway) ═══
    //   Paparan = paginated (server-side), KPI = server aggregation (tepat, tak ikut had paparan).
    const [salesRecords, setSalesRecords] = useState([]); // baris yang DIMUAT setakat ni (page demi page)
    const [salesLoading, setSalesLoading] = useState(false);
    const [salesLoadingMore, setSalesLoadingMore] = useState(false);
    const [salesCursor, setSalesCursor] = useState(null); // doc snapshot terakhir (startAfter)
    const [salesHasMore, setSalesHasMore] = useState(false);
    const [salesFilter, setSalesFilter] = useState('all'); // all | paid | pending | failed | delivery_pending
    const [salesSort, setSalesSort] = useState(null); // null (default: tarikh terkini) | { col: 'jenis'|'amaun'|'penghantaran', dir: 1|-1 } — sort atas baris yang dimuat
    // KPI dari server aggregation — { total, paidCount, paidSen, pendingCount, failedCount } | null
    const [salesStats, setSalesStats] = useState(null);
    const [salesStatsLoading, setSalesStatsLoading] = useState(false);
    // Tapisan tarikh (KPI + senarai ikut julat ni)
    const [salesDatePreset, setSalesDatePreset] = useState('all'); // all | month | lastmonth | custom
    const [salesFrom, setSalesFrom] = useState(''); // YYYY-MM-DD (custom)
    const [salesTo, setSalesTo] = useState('');   // YYYY-MM-DD (custom)
    // Carian email (mode berasingan — papar SEMUA order email tu, abai tapisan status/tarikh)
    const [salesSearchInput, setSalesSearchInput] = useState('');
    const [salesSearchTerm, setSalesSearchTerm] = useState(''); // '' = mod browse biasa
    const [salesExporting, setSalesExporting] = useState(false);
    // Card sumber (2026-10-25): 'all' | 'billplz' | 'manual' — tapisan client-side, tiada query semula.
    const [salesSource, setSalesSource] = useState('all');
    // SEMUA resit manual (Extend days → Paid renewal) — sedikit (~1 sehari), dimuat penuh; tapis tarikh di client.
    // gagal:true → card Manual + Billplz papar "—" (bukan angka salah).
    const [salesManual, setSalesManual] = useState({ rows: [], gagal: false });

    // ═══ PENDING PREMIUM SUBMISSIONS ═══
    const [pendingSubmissions, setPendingSubmissions] = useState([]);
    const [submissionsHistory, setSubmissionsHistory] = useState([]); // activated + rejected
    const [submissionsLoading, setSubmissionsLoading] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLimit, setHistoryLimit] = useState(50); // batch size — grows bila admin klik "Load more"
    const [historyHasMore, setHistoryHasMore] = useState(false);
    const [lastRefreshedPending, setLastRefreshedPending] = useState(null); // Date | null
    const [lastRefreshedHistory, setLastRefreshedHistory] = useState(null); // Date | null
    const [refreshingPending, setRefreshingPending] = useState(false);
    const [refreshingHistory, setRefreshingHistory] = useState(false);
    const [processingSubmissionId, setProcessingSubmissionId] = useState(null);
    const [historyCopyId, setHistoryCopyId] = useState(null); // ID baris yg baru di-copy (visual feedback)
    const [lightboxUrl, setLightboxUrl] = useState(null); // image URL untuk fullscreen view
    // Modal yang muncul selepas activate — tunjuk license + Copy/WhatsApp buttons.
    const [activatedModal, setActivatedModal] = useState(null); // { email, licenseKey, expiresAt, whatsappNumber }
    const [copyState, setCopyState] = useState('idle'); // 'idle' | 'copied'
    // Modal pre-activate — admin pilih campaign + modul + duration + maxProfiles sebelum aktifkan.
    // Shape: { submission, campaignId, allowedModules, durationDays, maxProfiles, showAdvanced } | null
    const [activatePendingModal, setActivatePendingModal] = useState(null);
    // ─── Expiring Soon: list user yang license tamat dalam 30 hari ───
    // Setiap item: { uid, email, whatsappNumber, expiresAt: Date, daysLeft: number }
    const [expiringSoon, setExpiringSoon] = useState([]);
    const [expiringSoonLoading, setExpiringSoonLoading] = useState(false);
    const [expiringFilter, setExpiringFilter] = useState('all'); // 'all' | '1' | '7' | '14' | '21' | 'expired'
    const [expiringFromDate, setExpiringFromDate] = useState(''); // '' = semua; else 'YYYY-MM-DD' — tapis "tamat dari" tarikh ni ke atas
    const [sendingReminderUid, setSendingReminderUid] = useState(null);
    // BARU: modal reminder — open per-row dengan copy buttons + send actions
    // Shape: { item: <expiringSoon row>, copyState: 'idle'|'number'|'text' } | null
    const [reminderModal, setReminderModal] = useState(null);

    // Fetch pending submissions (one-shot via getDocs — jimat kos berbanding subscribe).
    // Admin refresh manual guna butang 🔄 Refresh di header. Sort client-side untuk
    // elak composite index requirement (where + orderBy).
    const loadPendingSubmissions = useCallback(async ({ silent = false } = {}) => {
        if (!isAuthorized) return;
        if (!silent) setRefreshingPending(true);
        try {
            const q = query(
                collection(db, 'payment_submissions'),
                where('status', '==', 'pending')
            );
            const snap = await getDocs(q);
            const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            items.sort((a, b) => {
                const ta = a.submittedAt?.toMillis?.() || 0;
                const tb = b.submittedAt?.toMillis?.() || 0;
                return ta - tb; // ascending: yang lama dulu
            });
            setPendingSubmissions(items);
            setLastRefreshedPending(new Date());
        } catch (err) {
            console.error('[AdminView] pending submissions fetch error:', err);
        } finally {
            setSubmissionsLoading(false);
            setRefreshingPending(false);
        }
    }, [isAuthorized]);

    useEffect(() => {
        loadPendingSubmissions({ silent: true });
    }, [loadPendingSubmissions]);

    // Fetch history (activated + rejected). One-shot via getDocs. Admin refresh manual.
    // Perlu composite index payment_submissions(status, submittedAt desc). Sort akhir
    // client-side guna activatedAt/rejectedAt — orderBy server-side sekadar cap query.
    const loadHistorySubmissions = useCallback(async ({ silent = false } = {}) => {
        if (!isAuthorized) return;
        if (!silent) setRefreshingHistory(true);
        else setHistoryLoading(true);
        try {
            const q = query(
                collection(db, 'payment_submissions'),
                where('status', 'in', ['activated', 'rejected']),
                orderBy('submittedAt', 'desc'),
                firestoreLimit(historyLimit)
            );
            const snap = await getDocs(q);
            const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            items.sort((a, b) => {
                const ta = (a.activatedAt || a.rejectedAt)?.toMillis?.() || 0;
                const tb = (b.activatedAt || b.rejectedAt)?.toMillis?.() || 0;
                return tb - ta;
            });
            setSubmissionsHistory(items);
            setHistoryHasMore(snap.size === historyLimit);
            setLastRefreshedHistory(new Date());
        } catch (err) {
            console.error('[AdminView] history fetch error:', err);
        } finally {
            setHistoryLoading(false);
            setRefreshingHistory(false);
        }
    }, [isAuthorized, historyLimit]);

    useEffect(() => {
        loadHistorySubmissions({ silent: true });
    }, [loadHistorySubmissions]);

    // ─── Fetch Expiring Soon: licenses tamat dalam 30 hari ───
    // One-time fetch when admin tab active. Refresh via "Refresh" button.
    const fetchExpiringSoon = async () => {
        if (!isAuthorized) return;
        setExpiringSoonLoading(true);
        try {
            const now = new Date();
            const thirtyDaysLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
            // Query licenses expiring dalam 30 hari ke depan + yang dah expired dalam 90 hari belakang
            // (90 hari supaya admin boleh scroll & tapis tarikh lampau, cth chase renewal yang dah lepas)
            const pastWindowStart = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            const licSnap = await getDocs(query(
                collection(db, 'licenses'),
                where('expiresAt', '>=', pastWindowStart),
                where('expiresAt', '<=', thirtyDaysLater),
                orderBy('expiresAt', 'asc')
            ));

            const items = [];
            const uids = [];
            licSnap.docs.forEach(d => {
                const lic = d.data();
                if (!lic.email || !lic.licenseKey) return;
                const exp = lic.expiresAt?.toDate ? lic.expiresAt.toDate() : new Date(lic.expiresAt);
                const daysLeft = Math.ceil((exp - now) / (24 * 60 * 60 * 1000));
                items.push({
                    uid: d.id,
                    email: lic.email,
                    whatsappNumber: '', // fill later from users doc
                    expiresAt: exp,
                    daysLeft
                });
                uids.push(d.id);
            });

            // Chunked fetch users docs untuk dapat whatsappNumber + lastReminderSent
            for (let i = 0; i < uids.length; i += 30) {
                const chunk = uids.slice(i, i + 30);
                if (chunk.length === 0) continue;
                const uSnap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
                const map = {};
                uSnap.docs.forEach(d => { map[d.id] = d.data(); });
                items.forEach(item => {
                    if (chunk.includes(item.uid)) {
                        const u = map[item.uid];
                        if (u) {
                            item.whatsappNumber = u.whatsappNumber || '';
                            item.lastReminderSentAt7d = u.lastReminderSentAt7d?.toDate?.()
                                || (u.lastReminderSentAt7d ? new Date(u.lastReminderSentAt7d) : null);
                            item.lastReminderSentAt1d = u.lastReminderSentAt1d?.toDate?.()
                                || (u.lastReminderSentAt1d ? new Date(u.lastReminderSentAt1d) : null);
                            item.lastReminderManualAt = u.lastReminderManualAt?.toDate?.()
                                || (u.lastReminderManualAt ? new Date(u.lastReminderManualAt) : null);
                        }
                    }
                });
            }

            setExpiringSoon(items);
        } catch (err) {
            console.error('[AdminView] Fetch expiring soon failed:', err);
        } finally {
            setExpiringSoonLoading(false);
        }
    };

    // Trigger fetch bila tab Pending Premium aktif
    useEffect(() => {
        if (!isAuthorized) return;
        if (activeAdminTab === 'pending-premium') {
            fetchExpiringSoon();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthorized, activeAdminTab]);

    // Handler: hantar email reminder manual untuk satu user
    const handleSendReminderEmail = async (uid) => {
        setSendingReminderUid(uid);
        try {
            const res = await adminSendRenewalReminderFn({ uid });
            const r = res.data;
            alert(`✅ Email reminder dihantar (${r.daysLeft} hari lagi).`);
            // Refresh list supaya lastReminderSent updated
            fetchExpiringSoon();
        } catch (err) {
            console.error('[AdminView] Send reminder failed:', err);
            alert('Gagal hantar email: ' + (err?.message || 'unknown'));
        } finally {
            setSendingReminderUid(null);
        }
    };

    // BARU: tandakan (atau undo) reminder dah dihantar secara manual
    const handleMarkReminderManually = async (uid, undo = false) => {
        setSendingReminderUid(uid);
        try {
            await adminMarkReminderManuallySentFn({ uid, undo });
            fetchExpiringSoon();
            setReminderModal(null);
        } catch (err) {
            console.error('[AdminView] Mark reminder failed:', err);
            alert('Gagal: ' + (err?.message || 'unknown'));
        } finally {
            setSendingReminderUid(null);
        }
    };

    // Default modul Premium plan (fallback bila campaign takda preset).
    // Sumber kebenaran: backend hardcode sama (functions/index.js PREMIUM_DEFAULT).
    const PREMIUM_DEFAULT_MODULES = ['ai8saat', 'homeniche', 'ai16saat-half'];

    // Pilihan pakej untuk modal Aktifkan. Bila admin pilih pakej → auto-isi harga +
    // tempoh + profil + jenis lesen. Harga (priceSen) mengalir ke rekod lesen DAN
    // kiraan komisen affiliate (backend adminApprovePayment guna priceSen ni).
    // 'custom' = admin taip harga sendiri (untuk bundle/promo/bayaran luar biasa).
    // priceSen padan BILLPLZ_PLANS di functions/index.js (premium/fighter).
    const ACTIVATION_PACKAGES = [
        { id: 'premium_new',   label: 'Premium — Baru (RM199)',    priceSen: 19900, durationDays: 30,  maxProfiles: 3, licType: 'monthly' },
        { id: 'premium_renew', label: 'Premium — Renew (RM99)',    priceSen: 9900,  durationDays: 30,  maxProfiles: 3, licType: 'monthly' },
        { id: 'fighter',       label: 'Fighter — Tahunan (RM799)', priceSen: 79900, durationDays: 365, maxProfiles: 6, licType: 'yearly'  },
        { id: 'custom',        label: 'Custom (isi harga sendiri)', priceSen: null,  durationDays: 30,  maxProfiles: 3, licType: 'monthly' },
    ];

    // Cadangan kuota bila pakej/kempen dipilih: default pakej + slot tambahan lesen sedia ada
    // (utils/kuotaPakej.js — KEMBAR functions/kuotaPakej.js, polisi 2026-09-15).
    // ⛔ Dahulu butang pakej MEMAKSA default (3/6) → slot add-on/slot admin hilang bila lulus manual.
    // Admin masih boleh tukar nombor di "Setting Lanjutan" untuk kes khas (cth first batch 5).
    const cadangKuotaPakej = (kuotaDefault, lesenSedia) => {
        const hasil = kuotaSelepasBayar(kuotaDefault, lesenSedia || null);
        return { maxProfiles: hasil.maxProfiles, kuotaNota: teksPecahanKuota(kuotaDefault, hasil) };
    };

    // Buka modal pre-activate. Default values: takda campaign (paksa pilih),
    // modul Premium default, 30 hari, 3 profile.
    // BARU: untuk RENEWAL submission, async pre-fill campaign + modul dari user existing
    // (admin tak perlu ingat setting lama — auto-populate).
    const handleActivateSubmission = async (submission) => {
        // Default pakej ikut jenis submission: renewal → Premium Renew, selainnya → Premium Baru.
        // Admin boleh tukar ke Fighter/Custom dalam modal.
        const defaultPkg = submission.type === 'renewal'
            ? ACTIVATION_PACKAGES.find(p => p.id === 'premium_renew')
            : ACTIVATION_PACKAGES.find(p => p.id === 'premium_new');
        setActivatePendingModal({
            submission,
            campaignId: '',
            allowedModules: [...PREMIUM_DEFAULT_MODULES],
            packageId: defaultPkg.id,
            priceSen: defaultPkg.priceSen,
            licType: defaultPkg.licType,
            customPriceRM: '',
            durationDays: defaultPkg.durationDays,
            maxProfiles: defaultPkg.maxProfiles,
            lesenSedia: null,   // diisi selepas lesen dibaca (renewal) — asas cadangan kuota
            kuotaNota: '',
            showAdvanced: false,
            mode: 'pending'  // 'pending' (dari Pending Premium) | 'manual' (dari Users tab)
        });

        // Renewal — async pre-fill dari user + license existing
        if (submission.type === 'renewal' && submission.uid) {
            try {
                const [userSnap, licenseSnap] = await Promise.all([
                    getDoc(doc(db, 'users', submission.uid)),
                    getDoc(doc(db, 'licenses', submission.uid))
                ]);
                const uData = userSnap.exists() ? userSnap.data() : null;
                const lData = licenseSnap.exists() ? licenseSnap.data() : null;
                setActivatePendingModal(prev => prev ? {
                    ...prev,
                    campaignId: uData?.campaign || '',
                    allowedModules: (() => {
                        const mods = lData?.allowedModules;
                        if (!mods || mods.length === 0) return prev.allowedModules;
                        const aliases = { 'ai16saat-full': 'ai16saat-half' };
                        const validIds = new Set(modulesList.map(m => m.id));
                        if (!mods.includes('*')) {
                            const mapped = mods.map(m => aliases[m] || m);
                            return [...new Set(mapped.filter(m => validIds.has(m)))];
                        }
                        const explicit = modulesList.filter(m => !m.isPremiumAddon).map(m => m.id);
                        return [...new Set([...explicit, ...mods.filter(m => m !== '*')])];
                    })(),
                    // Kuota = default pakej + slot tambahan (dahulu: salin nombor lesen lama).
                    ...(lData ? {
                        lesenSedia: { maxProfiles: lData.maxProfiles, slotTambahan: lData.slotTambahan },
                        ...(prev.packageId === 'custom' ? {} : cadangKuotaPakej(
                            ACTIVATION_PACKAGES.find(p => p.id === prev.packageId)?.maxProfiles || 3,
                            { maxProfiles: lData.maxProfiles, slotTambahan: lData.slotTambahan })),
                    } : {})
                } : prev);
            } catch (err) {
                console.warn('[AdminView] Pre-fill renewal data failed:', err);
            }
        }
    };

    // Apabila admin pilih campaign dari dropdown — auto-fill preset kalau ada.
    // Kalau campaign takda preset (defaultAllowedModules dll), kekal value semasa.
    const handleSelectCampaign = (campaignId) => {
        const campaign = campaigns.find(c => c.id === campaignId);
        setActivatePendingModal(prev => {
            if (!prev) return prev;
            const hasModulesPreset = campaign && Array.isArray(campaign.defaultAllowedModules) && campaign.defaultAllowedModules.length > 0;
            const hasDurationPreset = campaign && Number.isInteger(campaign.defaultDurationDays);
            const hasProfilesPreset = campaign && Number.isInteger(campaign.defaultMaxProfiles) && campaign.defaultMaxProfiles >= 1;
            return {
                ...prev,
                campaignId,
                allowedModules: hasModulesPreset ? [...campaign.defaultAllowedModules] : prev.allowedModules,
                durationDays: hasDurationPreset ? campaign.defaultDurationDays : prev.durationDays,
                // Preset kempen = default; slot tambahan pelanggan dibawa di atasnya.
                ...(hasProfilesPreset ? cadangKuotaPakej(campaign.defaultMaxProfiles, prev.lesenSedia) : {})
            };
        });
    };

    // Apabila admin pilih pakej — auto-isi harga + tempoh + profil + jenis lesen.
    // 'custom' kekalkan tempoh/profil semasa & buka input harga manual (priceSen dari customPriceRM).
    const handleSelectPackage = (packageId) => {
        const pkg = ACTIVATION_PACKAGES.find(p => p.id === packageId);
        if (!pkg) return;
        setActivatePendingModal(prev => {
            if (!prev) return prev;
            if (pkg.id === 'custom') {
                // Kekal tempoh/profil semasa; harga ikut input customPriceRM (parse bila submit)
                const rm = parseFloat(prev.customPriceRM);
                return {
                    ...prev,
                    packageId: 'custom',
                    priceSen: Number.isFinite(rm) && rm > 0 ? Math.round(rm * 100) : null,
                    licType: prev.licType || 'monthly'
                };
            }
            return {
                ...prev,
                packageId: pkg.id,
                priceSen: pkg.priceSen,
                licType: pkg.licType,
                durationDays: pkg.durationDays,
                ...cadangKuotaPakej(pkg.maxProfiles, prev.lesenSedia)
            };
        });
    };

    // Input harga custom (RM) — simpan customPriceRM + kira priceSen (sen).
    const handleCustomPriceChange = (rmStr) => {
        setActivatePendingModal(prev => {
            if (!prev) return prev;
            const rm = parseFloat(rmStr);
            return {
                ...prev,
                customPriceRM: rmStr,
                priceSen: Number.isFinite(rm) && rm > 0 ? Math.round(rm * 100) : null
            };
        });
    };

    // Toggle satu modul dalam checklist.
    const handleToggleActivateModule = (moduleId) => {
        setActivatePendingModal(prev => {
            if (!prev) return prev;
            const has = prev.allowedModules.includes(moduleId);
            return {
                ...prev,
                allowedModules: has
                    ? prev.allowedModules.filter(m => m !== moduleId)
                    : [...prev.allowedModules, moduleId]
            };
        });
    };

    // Confirm — call backend dengan setting dari modal.
    // Handle dua mode: 'pending' (Pending Premium tab) | 'manual' (Users tab).
    const handleConfirmActivate = async () => {
        if (!activatePendingModal) return;
        const { submission, campaignId, allowedModules, durationDays, maxProfiles, mode, priceSen, licType, packageId } = activatePendingModal;
        if (!campaignId) {
            alert('Sila pilih campaign dulu.');
            return;
        }
        if (!Array.isArray(allowedModules) || allowedModules.length === 0) {
            alert('Sila pilih sekurang-kurangnya satu modul.');
            return;
        }
        // Harga wajib sah — ni yang tentukan komisen affiliate. Custom mesti > 0.
        if (!Number.isInteger(priceSen) || priceSen < 100) {
            alert(packageId === 'custom'
                ? 'Sila isi harga custom yang sah (minimum RM1.00).'
                : 'Harga pakej tidak sah. Sila pilih pakej semula.');
            return;
        }
        // Manual mode (Users tab) — guna uid sebagai key untuk loading state sebab takda submission.id
        const loadingKey = submission.id || `manual_${submission.uid}`;
        if (mode === 'manual') {
            setUpdatingUser(submission.uid);
        } else {
            setProcessingSubmissionId(loadingKey);
        }
        try {
            const res = await adminApprovePaymentFn({
                uid: submission.uid,
                submissionId: submission.id || null,  // null untuk manual mode
                campaign: campaignId,
                allowedModules,
                durationDays,
                maxProfiles,
                // BARU: harga pakej dipilih admin — tentukan rekod bayaran + komisen affiliate.
                priceSen,
                licType,
                // Manual mode: hantar isRenewal explicit (backend takda submission nak baca type)
                // Pending mode: backend baca dari submission.type — tak perlu hantar
                ...(mode === 'manual' && submission.type === 'renewal' ? { isRenewal: true } : {})
            });
            const r = res.data;
            // BARU: track isRenewal so post-activate WhatsApp template build with correct vibe
            const wasRenewal = r.isRenewal === true || submission.type === 'renewal' || (mode === 'manual' && submission.type === 'renewal');
            setActivatedModal({
                email: r.email || submission.email,
                licenseKey: r.licenseKey,
                expiresAt: r.expiresAt,
                whatsappNumber: r.whatsappNumber || submission.whatsappNumber || '',
                isRenewal: wasRenewal,
                durationDays
            });
            setCopyState('idle');
            // Manual mode: optimistic update Users list (sebab takda subscription auto-refresh)
            if (mode === 'manual') {
                setUsers(prev => prev.map(u => u.uid === submission.uid ? {
                    ...u,
                    tier: 'paid',
                    campaign: campaignId,
                    license: {
                        licenseKey: r.licenseKey,
                        expiresAt: r.expiresAt,
                        maxProfiles: r.maxProfiles,
                        // Server pulangkan slotTambahan (null = belum direkod) — kekalkan nilai
                        // sedia ada kalau server lama tidak menghantarnya.
                        slotTambahan: r.slotTambahan !== undefined ? r.slotTambahan : (u.license?.slotTambahan ?? null),
                        allowedModules: r.allowedModules,
                        activeProfiles: []
                    }
                } : u));
            }
            setActivatePendingModal(null); // close pre-activate modal
            // Refresh lists (subscription dah ditukar ke getDocs — mutation tak auto-propagate).
            if (mode !== 'manual') {
                loadPendingSubmissions({ silent: true });
                loadHistorySubmissions({ silent: true });
            }
        } catch (err) {
            console.error('[AdminView] Activate failed:', err);
            alert('Gagal aktifkan: ' + (err?.message || 'unknown'));
        } finally {
            if (mode === 'manual') {
                setUpdatingUser(null);
            } else {
                setProcessingSubmissionId(null);
            }
        }
    };

    // Copy WhatsApp template ke clipboard. Works regardless of customer wasap.
    const handleCopyWasapMessage = async () => {
        if (!activatedModal) return;
        const msg = buildActivationMessage({
            email: activatedModal.email,
            licenseKey: activatedModal.licenseKey,
            expiresAt: activatedModal.expiresAt,
            durationDays: activatedModal.durationDays || 30,
            isRenewal: activatedModal.isRenewal === true
        });
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(msg);
            } else {
                // Fallback untuk insecure context / older browsers
                const ta = document.createElement('textarea');
                ta.value = msg;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopyState('copied');
            setTimeout(() => setCopyState('idle'), 2500);
        } catch (err) {
            console.error('[AdminView] Copy wasap failed:', err);
            window.prompt('Copy manual ayat WhatsApp:', msg);
        }
    };

    const handleRejectSubmission = async (submission) => {
        if (!window.confirm(`Reject submission daripada ${submission.email}?\n\nUser kena hantar bukti baru. Anda boleh wasap dia manual.`)) {
            return;
        }
        setProcessingSubmissionId(submission.id);
        try {
            await adminRejectSubmissionFn({ submissionId: submission.id });
            // Refresh lists selepas reject (sebelum ni subscription auto-update; sekarang manual).
            loadPendingSubmissions({ silent: true });
            loadHistorySubmissions({ silent: true });
        } catch (err) {
            console.error('[AdminView] Reject submission failed:', err);
            alert('Gagal reject: ' + (err?.message || 'unknown'));
        } finally {
            setProcessingSubmissionId(null);
        }
    };

    const formatSubmissionDate = (ts) => {
        if (!ts) return '—';
        const d = ts?.toDate ? ts.toDate() : new Date(ts.seconds ? ts.seconds * 1000 : ts);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleString('ms-MY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    };

    // Relative time formatter — untuk label "Last updated 3 minit lepas" di refresh button.
    const formatRelativeTime = (date) => {
        if (!date) return '—';
        const sec = Math.floor((Date.now() - date.getTime()) / 1000);
        if (sec < 5) return 'baru sahaja';
        if (sec < 60) return `${sec} saat lepas`;
        const min = Math.floor(sec / 60);
        if (min < 60) return `${min} minit lepas`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr} jam lepas`;
        const day = Math.floor(hr / 24);
        return `${day} hari lepas`;
    };

    const isPdfProof = (sub) => sub?.proofContentType === 'application/pdf';

    // Copy ayat WhatsApp dari history row (activated submissions ada licenseKey + expiresAt tersimpan).
    const handleCopyHistoryRow = async (sub) => {
        if (!sub?.licenseKey) return;
        const expiresAtIso = sub.expiresAt?.toDate ? sub.expiresAt.toDate().toISOString() : sub.expiresAt;
        const msg = buildActivationMessage({
            email: sub.email,
            licenseKey: sub.licenseKey,
            expiresAt: expiresAtIso,
            durationDays: 30,
            isRenewal: sub.type === 'renewal'
        });
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(msg);
            } else {
                const ta = document.createElement('textarea');
                ta.value = msg;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setHistoryCopyId(sub.id);
            setTimeout(() => setHistoryCopyId(null), 2500);
        } catch (err) {
            console.error('[AdminView] history copy failed:', err);
            window.prompt('Copy manual:', msg);
        }
    };

    const formatHistoryDate = (sub) => {
        const ts = sub.activatedAt || sub.rejectedAt;
        if (!ts) return '—';
        const d = ts?.toDate ? ts.toDate() : new Date(ts.seconds ? ts.seconds * 1000 : ts);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const [isCopyingLesen, setIsCopyingLesen] = useState(false); // ★ Salin senarai LESEN untuk ChatGPT

    // ═══ EXTENSION: CLIENT LIST STATE ═══
    // ═══ USERS: EXTENDED STATE ═══
    const [editingUser, setEditingUser] = useState(null);
    const [editingWhatsappUser, setEditingWhatsappUser] = useState(null);
    const [savingWhatsapp, setSavingWhatsapp] = useState(false);
    const [userSort, setUserSort] = useState({ field: null, dir: 'asc' }); // field: 'expires' | 'profiles'
    // ═══ REFERRAL EDIT STATE (manual fix for missed affiliate attribution) ═══
    const [editingReferral, setEditingReferral] = useState(null); // { uid, email, currentRefCode } | null
    const [referralInput, setReferralInput] = useState('');
    const [savingReferral, setSavingReferral] = useState(false);
    const [referralError, setReferralError] = useState('');
    const [backfillRunning, setBackfillRunning] = useState(false);
    const [backfillPreview, setBackfillPreview] = useState(null); // dryRun result | null
    const [backfillApplying, setBackfillApplying] = useState(false);


    // ═══ MODULE LIST STATE (for allowedModules UI) ═══
    const [modulesList, setModulesList] = useState([]);

    // ═══ BULK ACTION STATE ═══
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [showBulkEditModules, setShowBulkEditModules] = useState(false);
    const [bulkAllowedModules, setBulkAllowedModules] = useState(['ai8saat']);

    // ═══ CAMPAIGN TAGGING STATE ═══
    const [campaigns, setCampaigns] = useState([]);
    const [filterCampaign, setFilterCampaign] = useState('all'); // 'all' | 'none' | <campaignId>
    const [campaignCountsState, setCampaignCountsState] = useState({ none: 0, byId: {} });
    const [backfillCountsLoading, setBackfillCountsLoading] = useState(false);
    const [showCampaignManager, setShowCampaignManager] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null); // { id?, name, description }
    const [showBulkAssignCampaign, setShowBulkAssignCampaign] = useState(false);
    const [bulkCampaignId, setBulkCampaignId] = useState(''); // '' = unset/None

    // ═══ CAMPAIGN PRESET STATE ═══
    // Edit preset campaign (default modul + duration + maxProfiles). Save ke campaign doc
    // supaya modal Aktifkan boleh auto-fill. Optional: propagate ke user existing dalam campaign.
    const [showSetCampaignModulesModal, setShowSetCampaignModulesModal] = useState(false);
    const [targetCampaignForModules, setTargetCampaignForModules] = useState(null); // { id, name }
    const [campaignModulesPick, setCampaignModulesPick] = useState(['ai8saat']);
    const [campaignPresetDuration, setCampaignPresetDuration] = useState(30);
    const [campaignPresetMaxProfiles, setCampaignPresetMaxProfiles] = useState(3);
    const [campaignPresetApplyExisting, setCampaignPresetApplyExisting] = useState(false);
    const [campaignModulesSaving, setCampaignModulesSaving] = useState(false);

    // ═══ DRAG TO SELECT REFS ═══
    const isDragging = useRef(false);
    const dragAction = useRef('select');
    const dragSet = useRef(new Set());

    // Check role
    useEffect(() => {
        const email = auth.currentUser?.email;
        const role = getUserRole(email);
        setUserRole(role);
        setIsAuthorized(canAccessAdmin(role));
    }, []);

    // ═══ AUTH LINKS HANDLER ═══
    const handleGenerateAuthLink = async (action, currentEmail) => {
        setAuthLinkLoading(true);
        setAuthLinkResult('');
        setAuthLinkType(action);
        try {
            const result = await adminGenerateResetLinksFn({ action, currentEmail, newEmail: action === 'email' ? newEmailInput : undefined });
            if (action === 'password') {
                setAuthLinkResult(result.data.link);
            } else {
                setAuthLinkResult(result.data.message);
                // Also update local users array to reflect new email immediately
                setUsers(prev => prev.map(u => u.email === currentEmail ? { ...u, email: newEmailInput } : u));
            }
        } catch (err) {
            console.error('Auth Link Error:', err);
            setAuthLinkResult(`Ralat: ${err.message}`);
        } finally {
            setAuthLinkLoading(false);
        }
    };

    // ═══ HELPER KONGSI TAB USER (SATU takrifan — dipakai laluan page / all / carian) ═══
    // Lesen untuk senarai uid — chunk 30 (had 'in' Firestore), semua chunk ditembak
    // SERENTAK. Dulu setiap chunk menunggu chunk sebelumnya siap: 1,700+ akaun =
    // ~58 round-trip bergilir — penyumbang besar "refresh lama" bila muat semua user.
    const lesenUntukUids = async (uids) => {
        const jobs = [];
        for (let i = 0; i < uids.length; i += 30) {
            const chunk = uids.slice(i, i + 30);
            if (chunk.length === 0) continue;
            jobs.push(getDocs(query(collection(db, 'licenses'), where(documentId(), 'in', chunk))));
        }
        const licenseMap = {};
        (await Promise.all(jobs)).forEach(snap => snap.docs.forEach(d => {
            licenseMap[d.id] = {
                licenseKey: d.data().licenseKey || null,
                expiresAt: d.data().expiresAt?.seconds
                    ? new Date(d.data().expiresAt.seconds * 1000)
                    : null,
                type: d.data().type || null,
                maxProfiles: d.data().maxProfiles || 0,
                // null = belum direkod. WAJIB disalin — popup Aktifkan + Edit Client membacanya.
                slotTambahan: bacaSlotTambahan(d.data()),
                activeProfiles: d.data().activeProfiles || [],
                allowedModules: d.data().allowedModules || [],
            };
        }));
        return licenseMap;
    };

    // Doc user → baris jadual (tier + lesen + createdAt ms).
    const bentukBarisUser = (userDocs, licenseMap) => userDocs.map(d => {
        const license = licenseMap[d.id] || null;
        const role = getUserRole(d.data().email);
        let tier;
        if ([ROLES.ADMIN, ROLES.MODERATOR].includes(role)) tier = 'admin';
        else if (license) tier = 'paid';
        else tier = 'free';
        return {
            uid: d.id,
            ...d.data(),
            tier,
            license,
            createdAt: d.data().createdAt?.seconds
                ? d.data().createdAt.seconds * 1000
                : d.data().createdAt || null,
        };
    });

    // ═══ USER MANAGEMENT — paginated (50/page, ordered by createdAt desc) ═══
    // opts.campaignFilter: kalau specific campaign ID, query server-side dgn
    // where('campaign', '==', X). Perlu composite index users(campaign, createdAt desc).
    const fetchUsersPage = async (pageIdx = 0, opts = {}) => {
        const { campaignFilter = null } = opts;
        setLoading(true);
        setError(null);
        try {
            // Reset cursor stack when going back to page 0 (forces fresh fetch).
            // Juga reset bila campaign filter berubah (cursor lama tak valid utk query baru).
            if (pageIdx === 0 || cursorStackRef.current.__campaignFilter !== campaignFilter) {
                cursorStackRef.current = [];
                cursorStackRef.current.__campaignFilter = campaignFilter;
            }

            // Build query: tambah where() kalau campaign filter aktif (selain 'all'/'none')
            const constraints = [];
            if (campaignFilter) {
                constraints.push(where('campaign', '==', campaignFilter));
            }
            constraints.push(orderBy('createdAt', 'desc'));
            const cursor = pageIdx > 0 ? cursorStackRef.current[pageIdx - 1] : null;
            if (cursor) constraints.push(startAfter(cursor));
            constraints.push(firestoreLimit(PAGE_SIZE));

            const q = query(collection(db, 'users'), ...constraints);
            const usersSnap = await getDocs(q);
            const userDocs = usersSnap.docs;

            // Lazy-fetch licenses ONLY for current page's UIDs
            const licenseMap = await lesenUntukUids(userDocs.map(d => d.id));
            const userList = bentukBarisUser(userDocs, licenseMap);

            setUsers(userList);
            setCurrentPage(pageIdx);
            setHasMore(userDocs.length === PAGE_SIZE);
            setUsersScope('page');
            setLastRefreshedUsers(new Date());

            // Save last doc as cursor for next page
            if (userDocs.length > 0) {
                cursorStackRef.current[pageIdx] = userDocs[userDocs.length - 1];
            }
        } catch (err) {
            console.error('Failed to fetch users:', err);
            setError(err.message || 'Gagal mendapatkan senarai pengguna.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch SEMUA user (tanpa pagination) — dipakai bila search/filter aktif supaya
    // carian merentas seluruh koleksi, bukan hanya halaman semasa.
    const fetchAllUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const usersSnap = await getDocs(
                query(collection(db, 'users'), orderBy('createdAt', 'desc'))
            );
            const userDocs = usersSnap.docs;
            const licenseMap = await lesenUntukUids(userDocs.map(d => d.id));
            const userList = bentukBarisUser(userDocs, licenseMap);

            setUsers(userList);
            setUsersScope('all');
            setHasMore(false);
            cursorStackRef.current = [];
            setLastRefreshedUsers(new Date());
        } catch (err) {
            console.error('Failed to fetch all users:', err);
            setError(err.message || 'Gagal mendapatkan semua pengguna.');
        } finally {
            setLoading(false);
        }
    };

    // ═══ CARIAN PINTAR TAB USER — corak SAMA seperti Rekod Jualan (runSalesEmailSearch) ═══
    // Satu kotak, tekan Enter/Cari → beberapa query KECIL ditembak serentak terus ke
    // server, hasil digabung (dedupe ikut uid). Kenapa bukan fetchAllUsers macam dulu:
    //   (a) muat SEMUA 1,700+ akaun + lesen hanya untuk tapis satu email = "refresh lama";
    //   (b) taip semasa senarai masih loading → guard `loading` dlm effect lama melangkau
    //       fetch itu TERUS → carian tersangkut menapis 50 akaun page semasa → akaun yang
    //       wujud dilapor "tiada dalam system" (kes izzalsyazrul@yahoo.com, 2026-08-28).
    // Bonus: query ini TIDAK susun ikut createdAt, jadi akaun tanpa field itu (halimunan
    // dalam senarai browse — lihat insiden 2026-07-29) tetap dijumpai oleh carian.
    //   • Nombor  → whatsappNumber, semua format (+60 / 60 / 0 / tanpa prefix)
    //   • Teks/@  → email "bermula dengan" + nama (beberapa versi huruf)
    //   • ID      → UID (doc id users) + license key (padanan tepat, koleksi licenses)
    // Tiada composite index perlu: semua single-field (in / range / equality) auto index.
    const runUsersSmartSearch = async (rawTerm) => {
        const term = (rawTerm || '').trim();
        if (!term) { setSearchTerm(''); return; }
        setLoading(true);
        setError(null);
        try {
            const col = collection(db, 'users');
            const CAP = firestoreLimit(300);
            const jobs = [];

            if (macamNombor(term)) {
                // Semua bentuk lazim + format paparan WhatsApp ("60 19-760 2796") — lihat utils/nomborTelefon.js
                jobs.push(getDocs(query(col, where('whatsappNumber', 'in', calonCarianWhatsapp(term)), CAP)));
            } else {
                const lo = term.toLowerCase();
                // Email "bermula dengan" (prefix range). Hampir semua disimpan huruf kecil,
                // tapi cuba juga bentuk asal taipan kalau berbeza (murah, 1 query).
                // ⚠️ Penutup julat = aksara U+F8FF yang TAK KELIHATAN di dlm '' pada 3 baris
                // bawah (corak sama carian Rekod Jualan). Ia BUKAN string kosong — jangan
                // "kemaskan" jadi '' betul-betul, nanti prefix range jadi padanan tepat.
                jobs.push(getDocs(query(col, where('email', '>=', lo), where('email', '<=', lo + ''), CAP)));
                if (term !== lo) {
                    jobs.push(getDocs(query(col, where('email', '>=', term), where('email', '<=', term + ''), CAP)));
                }
                // Nama "bermula dengan" — beberapa versi huruf (nama disimpan ikut asal).
                const titleCase = term.replace(/\b\w/g, (c) => c.toUpperCase());
                const nameVariants = Array.from(new Set([term, lo, term.toUpperCase(), titleCase]));
                nameVariants.forEach((nv) => {
                    jobs.push(getDocs(query(col, where('name', '>=', nv), where('name', '<=', nv + ''), CAP)));
                });
            }

            // UID = doc id users (auto-id Firebase Auth ~28 aksara). Guard elak path tak sah.
            if (/^[A-Za-z0-9]{15,}$/.test(term)) {
                jobs.push(getDoc(doc(db, 'users', term)));
            }

            // License key — padanan tepat pada koleksi licenses (doc id licenses = uid pemilik).
            const licByKeyJob = getDocs(query(collection(db, 'licenses'), where('licenseKey', '==', term), firestoreLimit(5)))
                .catch(() => null);

            // Tembak semua serentak; abai query yang gagal (allSettled), gabung + dedupe ikut uid.
            const settled = await Promise.allSettled(jobs);
            const byId = new Map();
            for (const res of settled) {
                if (res.status !== 'fulfilled' || !res.value) continue;
                const v = res.value;
                if (Array.isArray(v.docs)) {                 // QuerySnapshot
                    v.docs.forEach((d) => byId.set(d.id, d));
                } else if (typeof v.exists === 'function' && v.exists()) { // DocumentSnapshot
                    byId.set(v.id, v);
                }
            }
            const licSnap = await licByKeyJob;
            if (licSnap && licSnap.docs.length) {
                const extra = await Promise.all(
                    licSnap.docs.filter(d => !byId.has(d.id)).map(d => getDoc(doc(db, 'users', d.id)))
                );
                extra.forEach(s => { if (s.exists()) byId.set(s.id, s); });
            }

            const userDocs = Array.from(byId.values());
            const licenseMap = await lesenUntukUids(userDocs.map(d => d.id));
            const rows = bentukBarisUser(userDocs, licenseMap);
            // Terbaru dulu; akaun tanpa createdAt jatuh ke hujung TAPI tetap dipapar.
            rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

            setUsers(rows);
            setUsersScope('search');
            setHasMore(false);
            setCurrentPage(0);
            setLastRefreshedUsers(new Date());
        } catch (err) {
            console.error('Users smart search failed:', err);
            setError(err.message || 'Carian gagal.');
        } finally {
            setLoading(false);
        }
    };

    // Backward-compat alias for existing callers (refresh current page after edits)
    const fetchUsers = () => {
        if (usersScope === 'search' && searchTerm) return runUsersSmartSearch(searchTerm);
        if (usersScope === 'all') return fetchAllUsers();
        const campaignFilter = cursorStackRef.current.__campaignFilter || null;
        return fetchUsersPage(currentPage, { campaignFilter });
    };

    // ═══ TOTAL COUNTS — using server-side aggregation (1 read each) ═══
    const fetchTotalCounts = async () => {
        try {
            const [usersCount, licensesCount] = await Promise.all([
                getCountFromServer(collection(db, 'users')),
                getCountFromServer(collection(db, 'licenses')),
            ]);
            setTotalCounts({
                total: usersCount.data().count,
                paid: licensesCount.data().count,
            });
        } catch (err) {
            console.warn('Failed to fetch total counts:', err);
        }
    };

    // ═══ DASHBOARD: pipeline generation stats (today + julat tarikh) ═══
    // Baca dokumen agregat global pipeline_stats/{YYYY-MM-DD}. Admin/Mod dibenarkan
    // baca terus oleh firestore.rules (sama corak campaign_counts). Total dipapar = Paid + Trial.
    const fetchPipelineStats = async (start = pipelineStart, end = pipelineEnd) => {
        setPipelineLoading(true);
        try {
            // Hari ini (1 dokumen)
            const todaySnap = await getDoc(doc(db, 'pipeline_stats', klDayKey()));
            const t = todaySnap.exists() ? todaySnap.data() : {};
            setPipelineToday({
                paidCalls: t.paidCalls || 0,
                trialCalls: t.trialCalls || 0,
                adminCalls: t.adminCalls || 0,
                byModule: t.byModule || {},
            });

            // Julat tarikh (query atas field `date`)
            const qy = query(
                collection(db, 'pipeline_stats'),
                where('date', '>=', start),
                where('date', '<=', end),
                orderBy('date')
            );
            const snap = await getDocs(qy);
            const rows = [];
            let paid = 0, trial = 0;
            // Pecahan module untuk julat = jumlah byModule merentas hari (data dah ada dlm doc yang
            // sama → TIADA read tambahan). moduleDays = berapa hari dlm julat yang betul-betul ada
            // pecahan (rekod byModule bermula 2026-07-25) — untuk amaran "pecahan tak liputi semua hari".
            const byModule = {};
            let moduleDays = 0;
            snap.forEach(d => {
                const x = d.data();
                const p = x.paidCalls || 0, tr = x.trialCalls || 0;
                rows.push({ date: x.date || d.id, paid: p, trial: tr, total: p + tr });
                paid += p; trial += tr;
                const bm = x.byModule;
                if (bm && typeof bm === 'object' && Object.keys(bm).length > 0) {
                    moduleDays++;
                    Object.entries(bm).forEach(([id, v]) => {
                        if (!v || typeof v !== 'object') return;
                        const acc = byModule[id] || (byModule[id] = { paidCalls: 0, trialCalls: 0 });
                        acc.paidCalls += Number(v.paidCalls) || 0;
                        acc.trialCalls += Number(v.trialCalls) || 0;
                    });
                }
            });
            setPipelineRange({ rows, paid, trial, total: paid + trial, byModule, moduleDays });
        } catch (err) {
            console.warn('Failed to fetch pipeline stats:', err);
            setPipelineToday(prev => prev || { paidCalls: 0, trialCalls: 0, adminCalls: 0 });
            setPipelineRange({ rows: [], paid: 0, trial: 0, total: 0, byModule: {}, moduleDays: 0 });
        } finally {
            setPipelineLoading(false);
        }
    };

    // ═══ DASHBOARD: Ringkasan Bisnes — kira siri bulanan (Jan 2026 → semasa) dari rekod sedia ada ═══
    // Sumber: orders (Billplz paid) + payment_submissions (manual activated) + licenses + count signup users.
    // Semua client-side, admin dibenarkan baca oleh firestore.rules. Tiada tracking baru. Zero-fill = bulan tiada data → 0.
    const fetchBusinessSummary = async () => {
        setBizLoading(true);
        try {
            const months = bizMonthsFromJan();
            const idx = Object.fromEntries(months.map((mo, i) => [mo, i]));
            const rows = months.map(mo => ({
                month: mo, revenueSen: 0, baruSen: 0, renewSen: 0, addonSen: 0,
                premiumNewCount: 0, fighterNewCount: 0, renewCount: 0, addonCount: 0,
                fighterRevenueSen: 0, notRenewed: 0, paidActiveEOM: 0, signups: 0, upgradeToFighter: 0,
                fighterNewSen: 0, fighterRenewCount: 0, fighterRenewSen: 0,
                baruPlainCount: 0, baruPlainSen: 0, baruFlowCount: 0, baruFlowSen: 0,
                renewPlainCount: 0, renewPlainSen: 0, renewComboCount: 0, renewComboSen: 0,
                // Pakej SMART RM99 — kotak sendiri supaya kiraan Premium kekal benar.
                smartRevenueSen: 0, smartNewCount: 0, smartNewSen: 0, smartRenewCount: 0, smartRenewSen: 0,
                // Flow Ultra RM100 (akaun Flow berdiri sendiri) — SEMUA bayaran dikira "Renew Flow".
                flowRenewCount: 0, flowRenewSen: 0,
                // Upgrade = SMART naik Premium (RM100 naik taraf + RM199 pintu Premium) — kotak sendiri.
                upgradeCount: 0, upgradeSen: 0,
            }));
            const toMs = (ts) => !ts ? null : (typeof ts.toMillis === 'function' ? ts.toMillis() : (ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime()));
            const now = Date.now();
            // Kalendar renewal: kira sekali di sini (piggyback scan yang sama) — 'YYYY-MM-DD'(KL) → { y, g, r, ys, gs, rs }.
            // y = lesen bakal tamat (kuning), g = bayar renew (hijau), r = tamat tak sambung (merah). *s = jumlah sen (RM).
            // RM hijau = bayaran sebenar; RM kuning/merah = anggaran harga sambung (Premium 9900 / Fighter 79900 sen).
            const daily = {};
            const bumpDaily = (ms, field, sen) => { const k = klDayKeyOf(ms); if (!k) return; const o = daily[k] || (daily[k] = { y: 0, g: 0, r: 0, ys: 0, gs: 0, rs: 0 }); o[field]++; o[field + 's'] += (sen || 0); };
            const planByUid = {}; // uid → { plan, ms } pakej terkini (utk pecahan pakej aktif)
            // Upgrade premium→Fighter: jejak order Fighter PERTAMA per uid vs sejarah berbayar sebelum tu (order/lesen/submission lama)
            const ordFighterMin = {}, ordNonFighterMin = {}, licActByUid = {}, subActByUid = {};

            // ── Billplz orders (paid) ──
            const ordSnap = await getDocs(query(collection(db, 'orders'), where('status', '==', 'paid')));
            ordSnap.forEach(d => {
                const x = d.data();
                const ms = toMs(x.paidAt) ?? toMs(x.createdAt); if (ms == null) return;
                const flowUltra = adalahFlowUltra(x);
                // Kalendar: bayar sambung (renew) → bulatan hijau pada hari bayar (RM sebenar). Add-on bukan renew.
                // Flow Ultra BUKAN lesen SESAAT — kalendar ini (dan kadar renew) untuk lesen SESAAT sahaja.
                if (!flowUltra && (x.isRenewal === true || (x.purchaseType || '').toLowerCase() === 'renewal') && (x.plan || '').toLowerCase() !== 'flownity_addon') bumpDaily(ms, 'g', x.paidAmountSen || x.amountSen || 0);
                const r = rows[idx[klMonthKeyOf(ms)]];
                const plan = (x.plan || '').toLowerCase();
                const sen = x.paidAmountSen || x.amountSen || 0;
                if (r) {
                    r.revenueSen += sen;
                    const isRenew = x.isRenewal === true || (x.purchaseType || '').toLowerCase() === 'renewal';
                    if (flowUltra) {
                        // Flow Ultra RM100 dari halaman /flow. Keputusan user 2026-09-15: SEMUA bayaran
                        // dikira "Renew Flow", termasuk yang server tanda 'new'. Tanpa cabang ini ia jatuh
                        // ke kotak Premium Baru/Renew (38 bayaran, RM3,800 tersasar sehingga 13 Sept 2026).
                        // Add-on Flow dalam bil Premium (flowAddon:true) bukan padanan — kekal Premium "+Flow".
                        r.flowRenewCount++; r.flowRenewSen += sen;
                    }
                    // Upgrade (keputusan user 2026-10-09): SMART naik Premium — naik taraf RM100 (dahulu
                    // dalam kotak Add-on) DAN pintu Premium RM199 (dahulu dalam kotak Renew bulanan,
                    // kerana server menandanya renew). SEBELUM cabang add-on / renew di bawah.
                    else if (adalahUpgrade(x)) { r.upgradeSen += sen; r.upgradeCount++; }
                    else if (plan === 'flownity_addon') { r.addonSen += sen; r.addonCount++; }
                    else if (plan === 'fighter') {
                        // Fighter = pakej TAHUNAN; diasingkan dari metrik bulanan (Baru/Renew) supaya tak distort.
                        // isRenew (ada lesen masa checkout) = pelanggan LAMA naik taraf; else = pelanggan BARU terus Fighter.
                        r.fighterRevenueSen += sen;
                        if (isRenew) { r.fighterRenewCount++; r.fighterRenewSen += sen; }
                        else { r.fighterNewCount++; r.fighterNewSen += sen; }
                    }
                    else if (plan === 'smart') {
                        // Pakej SMART = langganan bulanan RM99 yang BUKAN Premium. Tanpa cabang
                        // ini ia jatuh ke kotak "Langgan Baru (Premium)" / "Renew (bulanan)",
                        // dan kiraan Premium jadi lebih besar daripada yang sebenar.
                        // Duitnya tetap masuk revenueSen di atas — hanya kiraan yang diasingkan.
                        r.smartRevenueSen += sen;
                        if (isRenew) { r.smartRenewCount++; r.smartRenewSen += sen; }
                        else { r.smartNewCount++; r.smartNewSen += sen; }
                    }
                    else if (isRenew) {
                        // Renew bulanan (Premium) — combo = Premium + FLOWNITY dlm satu bil (dikira 1 renew)
                        r.renewSen += sen; r.renewCount++;
                        if (x.flownityAddon === true) { r.renewComboCount++; r.renewComboSen += sen; }
                        else { r.renewPlainCount++; r.renewPlainSen += sen; }
                    }
                    else {
                        // Langgan baru bulanan (Premium) — +Flow = Premium + add-on Flow dlm satu bil
                        r.baruSen += sen; r.premiumNewCount++;
                        if (x.flowAddon === true) { r.baruFlowCount++; r.baruFlowSen += sen; }
                        else { r.baruPlainCount++; r.baruPlainSen += sen; }
                    }
                }
                if (x.uid && (plan === 'premium' || plan === 'fighter' || plan === 'smart')) {
                    const p = planByUid[x.uid]; if (!p || ms > p.ms) planByUid[x.uid] = { plan, ms };
                }
                if (x.uid && plan) {
                    if (plan === 'fighter') ordFighterMin[x.uid] = Math.min(ordFighterMin[x.uid] ?? Infinity, ms);
                    else ordNonFighterMin[x.uid] = Math.min(ordNonFighterMin[x.uid] ?? Infinity, ms);
                }
            });

            // ── Flow Ultra MANUAL (transfer, borang Add customer) — ikut TARIKH BAYAR (paidAt). Rekod lama
            //    tanpa paidAt tidak dikira (jualan Sheet lama). Dashboard SAHAJA — Rekod Jualan kekal Billplz.
            (await jualanManualFlow().catch(e => { console.warn("[Dashboard] jualan Flow manual gagal dibaca:", e?.message); return []; })).forEach(({ ms, sen }) => {
                const r = rows[idx[klMonthKeyOf(ms)]]; if (!r) return;
                r.revenueSen += sen; r.flowRenewCount++; r.flowRenewSen += sen;
            });

            // ── Manual payment_submissions (activated) — amount dlm SEN (guard nilai kecil = RM lama) ──
            const subSnap = await getDocs(query(collection(db, 'payment_submissions'), where('status', '==', 'activated')));
            subSnap.forEach(d => {
                const x = d.data();
                const ms = toMs(x.activatedAt); if (ms == null) return;
                // Kalendar: aktivasi manual jenis renewal → bulatan hijau pada hari aktif (RM sebenar).
                if ((x.type || 'new').toLowerCase() === 'renewal') bumpDaily(ms, 'g', (x.amountSen != null) ? x.amountSen : (typeof x.amount === 'number' ? (x.amount < 1000 ? Math.round(x.amount * 100) : x.amount) : 0));
                if (x.uid) subActByUid[x.uid] = Math.min(subActByUid[x.uid] ?? Infinity, ms);
                const r = rows[idx[klMonthKeyOf(ms)]]; if (!r) return;
                const sen = (x.amountSen != null) ? x.amountSen
                    : (typeof x.amount === 'number' ? (x.amount < 1000 ? Math.round(x.amount * 100) : x.amount) : 0);
                r.revenueSen += sen;
                if ((x.type || 'new').toLowerCase() === 'renewal') { r.renewSen += sen; r.renewCount++; r.renewPlainCount++; r.renewPlainSen += sen; }
                else { r.baruSen += sen; r.premiumNewCount++; r.baruPlainCount++; r.baruPlainSen += sen; }
            });

            // ── Licenses: tak-renew (ikut bulan expiry yg dah lepas) + paid aktif hujung bulan + pakej semasa ──
            const licSnap = await getDocs(collection(db, 'licenses'));
            const pkg = { premium: 0, fighter: 0, smart: 0, lama: 0 };
            licSnap.forEach(d => {
                const x = d.data();
                const exp = toMs(x.expiresAt);
                const act = toMs(x.activatedAt) ?? toMs(x.createdAt);
                // Kalendar: tarikh tamat masa depan = kuning (bakal renew), dah lepas = merah (tak sambung).
                // RM anggaran ikut pakej: Fighter (tahunan) RM799, selain itu Premium (bulanan) RM99.
                if (exp) {
                    const jp = planByUid[d.id]?.plan;
                    const renewSen = (jp === 'fighter' || x.plan === 'yearly' || x.licType === 'yearly') ? 79900 : 9900;
                    bumpDaily(exp, exp > now ? 'y' : 'r', renewSen);
                }
                if (act != null) licActByUid[d.id] = act;
                if (exp && exp <= now) { const r = rows[idx[klMonthKeyOf(exp)]]; if (r) r.notRenewed++; }
                for (const mo of months) {
                    const r = rows[idx[mo]];
                    const eom = bizMonthEndMs(mo);
                    if (eom > now) { if (exp && exp > now && act && act <= now) r.paidActiveEOM++; }
                    else { if (act && act <= eom && exp && exp > eom) r.paidActiveEOM++; }
                }
                if (exp && exp > now) {
                    const jp = planByUid[d.id]?.plan;
                    // ⛔ SMART mesti disemak SEBELUM 'lama'. Tanpa baris ini setiap pelanggan
                    //    SMART dikira "Lama (pra-Billplz)" — pelanggan yang baru sahaja membayar.
                    if (jp === 'fighter') pkg.fighter++;
                    else if (jp === 'smart') pkg.smart++;
                    else if (jp === 'premium') pkg.premium++;
                    else pkg.lama++;
                }
            });

            // ── Signup free per bulan (count aggregation — 1 read/bulan) ──
            await Promise.all(months.map(async (mo) => {
                try {
                    const start = new Date(bizMonthStartMs(mo));
                    const end = new Date(bizMonthStartMs(bizNextMonth(mo)));
                    const c = await getCountFromServer(query(collection(db, 'users'),
                        where('createdAt', '>=', start), where('createdAt', '<', end)));
                    rows[idx[mo]].signups = c.data().count;
                } catch (e) { /* biar 0 */ }
            }));

            // ── Upgrade premium→Fighter: user beli Fighter (baru ATAU sambung) sedangkan dah pelanggan berbayar sebelum tu ──
            // Dikira pada bulan order Fighter PERTAMA user tu. Sejarah dikesan via order/lesen/submission lama.
            Object.entries(ordFighterMin).forEach(([u, fMin]) => {
                const priorOrder = ordNonFighterMin[u] != null && ordNonFighterMin[u] < fMin;
                const priorLic = licActByUid[u] != null && licActByUid[u] < fMin - 60000;
                const priorSub = subActByUid[u] != null && subActByUid[u] < fMin;
                if (priorOrder || priorLic || priorSub) {
                    const r = rows[idx[klMonthKeyOf(fMin)]];
                    if (r) r.upgradeToFighter++;
                }
            });

            setBizMonthly(rows);
            setBizPackages(pkg);
            setBizRenewalDaily(daily);
            setBizSelMonth(prev => prev || months[months.length - 1]);
            // ★ COST-FIX (Cara A): simpan hasil kira dalam SATU doc kecil supaya buka tab
            // Dashboard baca cache ni sahaja — BUKAN scan semula orders+submissions+licenses.
            // Hanya Refresh (fungsi ni) yang kira semula + tulis balik cache.
            try {
                await setDoc(doc(db, 'config', 'business_summary'),
                    { monthly: rows, packages: pkg, renewalDaily: daily, computedAt: serverTimestamp() });
                setBizCachedAt(Date.now());
            } catch (cacheErr) { console.warn('Gagal simpan cache Ringkasan Bisnes:', cacheErr); }
        } catch (err) {
            console.warn('Failed to fetch business summary:', err);
        } finally {
            setBizLoading(false);
        }
    };

    // ═══ DASHBOARD: muat Ringkasan Bisnes dari CACHE (1 bacaan murah) ═══
    // Buka tab Dashboard panggil ni — BUKAN fetchBusinessSummary (yang scan penuh).
    // Angka terkini dijana semula HANYA bila admin tekan Refresh.
    const loadBusinessSummaryCache = async () => {
        setBizLoading(true);
        try {
            const snap = await getDoc(doc(db, 'config', 'business_summary'));
            if (snap.exists()) {
                const d = snap.data();
                if (Array.isArray(d.monthly)) setBizMonthly(d.monthly);
                if (d.packages) setBizPackages(d.packages);
                setBizRenewalDaily(d.renewalDaily && typeof d.renewalDaily === 'object' ? d.renewalDaily : {});
                setBizSelMonth(prev => prev || (Array.isArray(d.monthly) && d.monthly.length ? d.monthly[d.monthly.length - 1].month : null));
                const cAt = d.computedAt && typeof d.computedAt.toMillis === 'function' ? d.computedAt.toMillis()
                    : (d.computedAt?.seconds ? d.computedAt.seconds * 1000 : null);
                setBizCachedAt(cAt);
            } else {
                setBizCachedAt(null); // belum ada cache → UI papar "Tekan Refresh untuk muat"
            }
        } catch (err) {
            console.warn('Gagal muat cache Ringkasan Bisnes:', err);
        } finally {
            setBizLoading(false);
        }
    };

    // ═══ RISIKO: kira semula penuh (butang Refresh sahaja) ═══
    // Kos ~1,500 bacaan sekali tekan: licenses (semua) + pipelineUsage (semua)
    // + users (HANYA pemegang lesen, bukan scan penuh 1,600 doc).
    // SENGAJA tidak auto-jalan bila tab dibuka — buka tab baca cache sahaja (1 bacaan).
    const fetchRiskData = async () => {
        setRiskLoading(true);
        try {
            const now = Date.now();

            // 1) Lesen — yang masih aktif + yang baru tamat (masih berbaloi win-back)
            const licSnap = await getDocs(collection(db, 'licenses'));
            const cand = [];
            licSnap.forEach(d => {
                const x = d.data();
                const expMs = tsToMs(x.expiresAt);
                if (expMs == null) return;
                const daysLeft = Math.ceil((expMs - now) / 86400000);
                if (daysLeft < -RISK_LAPSED_DAYS) return; // dah lama tamat — bukan sasaran kerja harian
                const isYearly = [x.type, x.plan, x.licType].includes('yearly');
                cand.push({ uid: d.id, expMs, daysLeft, lapsed: daysLeft < 0, plan: isYearly ? 'Fighter' : 'Premium' });
            });

            // 2) Penggunaan sebenar — pipelineUsage ditulis Cloud Function setiap kali generate
            const puSnap = await getDocs(collection(db, 'pipelineUsage'));
            const useMap = {};
            puSnap.forEach(d => {
                const x = d.data();
                useMap[d.id] = {
                    runs: (x.paidRuns || 0) + (x.trialRuns || 0),
                    last: tsToMs(x.lastRunAt),
                    email: x.email || ''
                };
            });

            // 3) Email + no. WhatsApp — chunk 30 (had 'in' Firestore), pemegang lesen sahaja
            const uMap = {};
            const uids = cand.map(c => c.uid);
            for (let i = 0; i < uids.length; i += 30) {
                const chunk = uids.slice(i, i + 30);
                if (!chunk.length) continue;
                const uSnap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
                uSnap.forEach(d => {
                    const x = d.data();
                    uMap[d.id] = { email: x.email || '', wa: x.whatsappNumber || '' };
                });
            }

            // 4) Gabung + kelaskan risiko
            let rows = cand.map(c => {
                const u = useMap[c.uid] || { runs: 0, last: null, email: '' };
                const p = uMap[c.uid] || {};
                const { risk, reason } = classifyRisk(u.runs, u.last, now);
                return {
                    uid: c.uid,
                    email: p.email || u.email || '(tiada email)',
                    wa: p.wa || '',
                    plan: c.plan,
                    expMs: c.expMs,
                    daysLeft: c.daysLeft,
                    lapsed: c.lapsed,
                    runs: u.runs,
                    lastRunMs: u.last,
                    risk, reason
                };
            });

            // Susun: paling bahaya dahulu, kemudian paling hampir tamat
            rows.sort((a, b) => (RISK_ORDER[a.risk] - RISK_ORDER[b.risk]) || (a.daysLeft - b.daysLeft));

            const counts = {
                total: rows.length,
                red: rows.filter(r => r.risk === 'red').length,
                amber: rows.filter(r => r.risk === 'amber').length,
                green: rows.filter(r => r.risk === 'green').length,
                neverUsed: rows.filter(r => !r.runs).length,
                expiring30: rows.filter(r => r.daysLeft >= 0 && r.daysLeft <= 30).length,
                lapsed: rows.filter(r => r.lapsed).length
            };

            // Had saiz doc: simpan yang paling mendesak dahulu, dan LAPOR berapa dipotong
            // (jangan senyap — kalau tidak admin sangka senarai ni lengkap).
            const truncated = Math.max(0, rows.length - RISK_MAX_ROWS);
            if (truncated > 0) rows = rows.slice(0, RISK_MAX_ROWS);

            setRiskRows(rows); setRiskCounts(counts); setRiskTruncated(truncated); setRiskCachedAt(Date.now());
            try {
                await setDoc(doc(db, 'config', 'risk_summary'), { rows, counts, truncated, computedAt: serverTimestamp() });
            } catch (cacheErr) { console.warn('Gagal simpan cache Risiko:', cacheErr); }
        } catch (err) {
            console.warn('Gagal kira Risiko Pelanggan:', err);
        } finally {
            setRiskLoading(false);
        }
    };

    // ═══ RISIKO: buka tab = baca cache sahaja (1 bacaan murah) ═══
    const loadRiskCache = async () => {
        setRiskLoading(true);
        try {
            const snap = await getDoc(doc(db, 'config', 'risk_summary'));
            if (snap.exists()) {
                const d = snap.data();
                setRiskRows(Array.isArray(d.rows) ? d.rows : []);
                setRiskCounts(d.counts || null);
                setRiskTruncated(d.truncated || 0);
                setRiskCachedAt(tsToMs(d.computedAt));
            } else {
                setRiskCachedAt(null); // belum pernah dikira → UI ajak tekan Refresh
            }
        } catch (err) {
            console.warn('Gagal muat cache Risiko:', err);
        } finally {
            setRiskLoading(false);
        }
    };

    // Baris yang SEDANG dipapar (ikut penapis + carian). Jadual DAN eksport guna senarai
    // yang sama supaya "Salin"/"Eksport" sentiasa padan dengan apa yang admin nampak.
    const riskVisible = (() => {
        if (!riskRows) return [];
        const q = riskSearch.trim().toLowerCase();
        return riskRows.filter(r => {
            if (riskFilter === 'red' && r.risk !== 'red') return false;
            if (riskFilter === 'amber' && r.risk !== 'amber') return false;
            if (riskFilter === 'green' && r.risk !== 'green') return false;
            if (riskFilter === 'never' && r.runs) return false;
            if (riskFilter === 'exp7' && !(r.daysLeft >= 0 && r.daysLeft <= 7)) return false;
            if (riskFilter === 'lapsed' && !r.lapsed) return false;
            if (q && !((r.email || '').toLowerCase().includes(q) || (r.wa || '').includes(q))) return false;
            return true;
        });
    })();

    const copyRiskNumbers = async () => {
        const unique = [...new Set(riskVisible.map(r => normalizeWaNumber(r.wa)).filter(Boolean))];
        try {
            await navigator.clipboard.writeText(unique.join(', '));
            setRiskCopied(`${unique.length} nombor disalin`);
        } catch { setRiskCopied('Gagal salin'); }
        setTimeout(() => setRiskCopied(''), 2500);
    };

    const exportRiskCsv = () => {
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const dmy = (ms) => ms ? new Date(ms).toLocaleDateString('ms-MY', { timeZone: 'Asia/Kuala_Lumpur' }) : '';
        const lines = [['Email', 'WhatsApp', 'Pakej', 'Status', 'Sebab', 'Tarikh tamat', 'Hari lagi', 'Jumlah generate', 'Kali akhir guna'].map(esc).join(',')];
        riskVisible.forEach(r => lines.push([
            r.email,
            normalizeWaNumber(r.wa) || r.wa || '',
            r.plan,
            r.risk === 'red' ? 'Bahaya' : r.risk === 'amber' ? 'Perhatian' : 'Sihat',
            r.reason,
            dmy(r.expMs),
            r.daysLeft,
            r.runs,
            r.lastRunMs ? dmy(r.lastRunMs) : 'Tak pernah'
        ].map(esc).join(',')));
        // BOM supaya Excel buka aksara Melayu dengan betul
        const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `sesaat-risiko-${klDayKey()}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    // ═══ Hantar kempen WhatsApp kepada senarai yang SEDANG dipapar ═══
    // Had 50 seorang penghantaran (padan had server). Klik semula untuk batch seterusnya —
    // server menolak pendua dalam kempen sama, jadi tiada sesiapa dimesej dua kali.
    const WA_BATCH = 50;
    const waHandleOf = (email) => (email || '').split('@')[0] || 'sahabat';
    const sendWaCampaign = async (dryRun) => {
        if (!waTemplate.trim()) { setWaResult({ error: 'Nama template diperlukan.' }); return; }
        const pool = riskVisible.filter(r => r.wa);
        const targets = pool.slice(0, WA_BATCH);
        if (!targets.length) { setWaResult({ error: 'Tiada penerima yang ada nombor WhatsApp dalam penapis semasa.' }); return; }
        if (!dryRun) {
            const ok = window.confirm(
                `Hantar template "${waTemplate.trim()}" kepada ${targets.length} orang?\n\n` +
                `Mesej pemasaran pukal boleh menyebabkan penerima menyekat nombor anda, ` +
                `dan itu menjejaskan reputasi nombor WhatsApp sokongan anda untuk semua pelanggan.\n\nTeruskan?`
            );
            if (!ok) return;
        }
        setWaBusy(true); setWaResult(null);
        try {
            // ID kempen stabil (nama, atau penapis + tarikh) supaya klik berulang
            // menyambung batch — bukan mencipta kempen baharu tanpa perlindungan pendua.
            const slug = waCampaignName.trim()
                ? waCampaignName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40)
                : `${riskFilter}_${klDayKey()}`;
            const fn = httpsCallable(functions, 'sendWaTemplate');
            const res = await fn({
                campaignId: `wa_${slug}`,
                campaignName: waCampaignName.trim() || `Risiko: ${riskFilter}`,
                templateName: waTemplate.trim(),
                languageCode: waLang,
                dryRun,
                recipients: targets.map(r => ({
                    uid: r.uid, wa: r.wa, email: r.email,
                    params: [waHandleOf(r.email), String(Math.max(0, r.daysLeft))]
                }))
            });
            setWaResult({ ...res.data, baki: Math.max(0, pool.length - targets.length) });
        } catch (e) {
            setWaResult({ error: e?.message || 'Gagal menghantar.' });
        } finally { setWaBusy(false); }
    };

    // Bila panel admin mula & tab default = Dashboard, muat data Dashboard sekali (auto).
    // (Dulu data hanya dimuat bila tab diklik → buka terus Dashboard akan kosong.)
    /* ⚠️ Dengar activeAdminTab, BUKAN [] (mount sahaja) dan BUKAN onClick tab.
       Sejak setiap tab ada alamatnya sendiri, pengguna boleh mendarat terus
       pada mana-mana tab daripada bookmark atau butang Back — muatan yang
       bergantung pada klik akan meninggalkan skrin KOSONG di situ. */
    useEffect(() => {
        if (!isAuthorized) return;
        if (activeAdminTab === 'dashboard') { fetchPipelineStats(); loadBusinessSummaryCache(); }
        if (activeAdminTab === 'users') {
            // Mod carian aktif (term komited masih ada) → segarkan hasil carian,
            // bukan senarai page — kalau tidak fetchUsersPage menimpanya.
            if (searchTerm) runUsersSmartSearch(searchTerm); else fetchUsersPage(0);
            fetchTotalCounts();
        }
        if (activeAdminTab === 'risk') loadRiskCache();
        // 'pending-premium' + 'sales' ada useEffect sendiri (reaktif pada tapisan).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthorized, activeAdminTab]);

    // ═══ REKOD JUALAN — julat tarikh (waktu KL) ═══
    // Pulang { start, end } sebagai JS Date, atau null (semua masa). createdAt disimpan Timestamp.
    const salesDateRange = () => {
        const mkStart = (ymd) => new Date(`${ymd}T00:00:00+08:00`);
        const mkEnd = (ymd) => new Date(`${ymd}T23:59:59.999+08:00`);
        if (salesDatePreset === 'all') return null;
        if (salesDatePreset === 'custom') {
            if (!salesFrom && !salesTo) return null;
            return { start: salesFrom ? mkStart(salesFrom) : null, end: salesTo ? mkEnd(salesTo) : null };
        }
        const nowMy = new Date(Date.now() + PIPE_MY_OFFSET_MS);
        let y = nowMy.getUTCFullYear(), m = nowMy.getUTCMonth(); // m: 0-based
        if (salesDatePreset === 'lastmonth') { m -= 1; if (m < 0) { m = 11; y -= 1; } }
        const mm = String(m + 1).padStart(2, '0');
        const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
        return { start: mkStart(`${y}-${mm}-01`), end: mkEnd(`${y}-${mm}-${String(lastDay).padStart(2, '0')}`) };
    };
    const salesDateConstraints = () => {
        const r = salesDateRange();
        const cons = [];
        if (r?.start) cons.push(where('createdAt', '>=', r.start));
        if (r?.end) cons.push(where('createdAt', '<=', r.end));
        return cons;
    };

    const SALES_PAGE = 50;

    // KPI tepat dari server aggregation (count + sum) — ikut julat tarikh semasa,
    // BUKAN dikira dari baris yang dimuat (sebab tu had 200 lama bagi angka salah).
    const fetchSalesStats = async () => {
        setSalesStatsLoading(true);
        const base = collection(db, 'orders');
        const dc = salesDateConstraints();
        // Setiap query ditangkap berasingan — kalau satu gagal (cth index belum siap),
        // yang lain tetap papar angka server yang betul (bukan semua jatuh ke fallback).
        const safe = async (fn) => { try { return await fn(); } catch (e) { console.warn('sales stat gagal:', e?.code || e?.message); return null; } };
        const [total, paid, pend, fail] = await Promise.all([
            safe(async () => (await getCountFromServer(query(base, ...dc))).data().count),
            safe(async () => { const d = (await getAggregateFromServer(query(base, where('status', '==', 'paid'), ...dc), { c: count(), sen: sum('paidAmountSen') })).data(); return { count: d.c, sen: d.sen || 0 }; }),
            safe(async () => (await getCountFromServer(query(base, where('status', '==', 'pending'), ...dc))).data().count),
            safe(async () => (await getCountFromServer(query(base, where('status', '==', 'failed'), ...dc))).data().count),
        ]);
        setSalesStats({
            total,
            paidCount: paid ? paid.count : null,
            paidSen: paid ? paid.sen : null,
            pendingCount: pend,
            failedCount: fail,
        });
        setSalesStatsLoading(false);
    };

    // Muat satu page order (server-side filter status + tarikh). reset=true → mula semula.
    const fetchSalesPage = async ({ reset = false } = {}) => {
        if (reset) setSalesLoading(true); else setSalesLoadingMore(true);
        try {
            const base = collection(db, 'orders');
            const cons = [];
            if (salesFilter === 'paid' || salesFilter === 'pending' || salesFilter === 'failed') {
                cons.push(where('status', '==', salesFilter));
            } else if (salesFilter === 'delivery_pending') {
                cons.push(where('status', '==', 'paid')); // undelivered ditapis client-side di render
            }
            cons.push(...salesDateConstraints());
            const cursor = reset ? null : salesCursor;
            const qy = query(base, ...cons, orderBy('createdAt', 'desc'),
                ...(cursor ? [startAfter(cursor)] : []), firestoreLimit(SALES_PAGE));
            const snap = await getDocs(qy);
            const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            if (snap.docs.length) setSalesCursor(snap.docs[snap.docs.length - 1]);
            setSalesHasMore(snap.docs.length === SALES_PAGE);
            setSalesRecords(prev => reset ? rows : [...prev, ...rows]);
        } catch (err) {
            console.warn('Failed to fetch sales page:', err);
            if (reset) { setSalesRecords([]); setSalesHasMore(false); }
        } finally {
            setSalesLoading(false); setSalesLoadingMore(false);
        }
    };

    // Resit manual — query satu medan (index auto, tanpa composite index); julat tarikh ditapis di render.
    const fetchSalesManual = async () => {
        try {
            const snap = await getDocs(query(collection(db, 'orders'), where('provider', '==', 'manual')));
            const ms = (ts) => (ts?.toMillis ? ts.toMillis() : 0);
            const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
            setSalesManual({ rows, gagal: false });
        } catch (err) {
            console.warn('Failed to fetch manual sales:', err);
            setSalesManual({ rows: [], gagal: true });
        }
    };

    const fetchSalesInitial = () => {
        setSalesCursor(null);
        fetchSalesPage({ reset: true });
        fetchSalesStats();
        fetchSalesManual();
    };
    const loadMoreSales = () => fetchSalesPage({ reset: false });
    const refreshSales = () => { if (salesSearchTerm) runSalesEmailSearch(salesSearchTerm); else fetchSalesInitial(); };

    // Mod carian PINTAR — satu kotak, sistem kenal jenis input & tembak carian betul serentak,
    // kemudian gabung hasil (dedupe ikut doc id). Papar SEMUA order sepadan (few rows),
    // stats dikira client-side. Nama fungsi kekal (dipanggil di refreshSales + useEffect).
    //   • Nombor  → no. WhatsApp, semua format (+60 / 60 / 0 / tanpa prefix, abai jarak/sengkang)
    //   • Teks/@  → email "bermula dengan" (prefix range) + nama pelanggan (cuba beberapa versi huruf)
    //   • ID      → Bill ID Billplz (padanan tepat) + Order ID (doc id, kalau nampak macam ID)
    // Tiada composite index perlu: semua single-field (in / range / equality) guna auto index.
    const runSalesEmailSearch = async (rawTerm) => {
        const term = (rawTerm || '').trim();
        if (!term) { setSalesSearchTerm(''); return; }
        setSalesLoading(true); setSalesStatsLoading(true);
        setSalesCursor(null); setSalesHasMore(false);
        try {
            const col = collection(db, 'orders');
            const CAP = firestoreLimit(300);
            const jobs = [];

            // Selalu cuba padanan tepat Bill ID Billplz (murah, kadang penyelamat masa reconcile bayaran).
            jobs.push(getDocs(query(col, where('billplzBillId', '==', term), CAP)));

            if (macamNombor(term)) {
                // Buang jarak/sengkang/+/kod negara → bina beberapa bentuk lazim (+ gaya WhatsApp), cari sekaligus.
                jobs.push(getDocs(query(col, where('whatsappNumber', 'in', calonCarianWhatsapp(term)), CAP)));
            } else {
                // Email "bermula dengan" (prefix range). Email hampir semua disimpan huruf kecil.
                const lo = term.toLowerCase();
                jobs.push(getDocs(query(col, where('email', '>=', lo), where('email', '<=', lo + ''), CAP)));
                // Nama "bermula dengan" — cuba beberapa versi huruf supaya forgiving (nama disimpan ikut asal).
                const titleCase = term.replace(/\b\w/g, (c) => c.toUpperCase());
                const nameVariants = Array.from(new Set([term, lo, term.toUpperCase(), titleCase]));
                nameVariants.forEach((nv) => {
                    jobs.push(getDocs(query(col, where('name', '>=', nv), where('name', '<=', nv + ''), CAP)));
                });
            }

            // Order ID = doc id (auto-id ~20 aksara alfanumerik). Guard elak path tak sah.
            if (/^[A-Za-z0-9]{15,}$/.test(term)) {
                jobs.push(getDoc(doc(db, 'orders', term)));
            }

            // Tembak semua serentak; abai query yang gagal (allSettled), gabung + dedupe ikut id.
            const settled = await Promise.allSettled(jobs);
            const byId = new Map();
            for (const res of settled) {
                if (res.status !== 'fulfilled' || !res.value) continue;
                const v = res.value;
                if (Array.isArray(v.docs)) {                 // QuerySnapshot
                    v.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
                } else if (typeof v.exists === 'function' && v.exists()) { // DocumentSnapshot
                    byId.set(v.id, { id: v.id, ...v.data() });
                }
            }

            const ms = (ts) => (ts?.toMillis ? ts.toMillis() : (ts?.seconds ? ts.seconds * 1000 : 0));
            const rows = Array.from(byId.values()).sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
            setSalesRecords(rows);
            const paid = rows.filter(o => o.status === 'paid');
            setSalesStats({
                total: rows.length,
                paidCount: paid.length,
                paidSen: paid.reduce((s, o) => s + (o.paidAmountSen || o.amountSen || 0), 0),
                pendingCount: rows.filter(o => o.status === 'pending').length,
                failedCount: rows.filter(o => o.status === 'failed').length,
            });
        } catch (err) {
            console.warn('Sales smart search failed:', err);
            setSalesRecords([]); setSalesStats(null);
        } finally {
            setSalesLoading(false); setSalesStatsLoading(false);
        }
    };

    // Auto-load bila tab Rekod Jualan dibuka / tapisan / tarikh / carian berubah.
    useEffect(() => {
        if (activeAdminTab !== 'sales') return;
        if (salesSearchTerm) runSalesEmailSearch(salesSearchTerm);
        else fetchSalesInitial();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAdminTab, salesFilter, salesDatePreset, salesFrom, salesTo, salesSearchTerm]);

    // Tugasan penghantaran manual satu order (Flow Ultra / Akaun Flow / Bind MCN).
    // Skop komponen supaya dikongsi render + export (dulu ditakrif dalam render sahaja).
    const getSalesOrderTasks = (o) => {
        const tasks = [];
        // "Flow Ultra" = penghantaran Akaun Google Flow Ultra (sharing) — HANYA pembeli add-on Flow RM100.
        // (Dulu ada tag "Akaun Flow" berasingan yang track kerja sama → digabung ke satu tag "Flow Ultra" ini.)
        if (o.flowAddon) tasks.push({ key: 'flowDelivered', label: 'Flow Ultra', done: !!o.flowDelivered });
        // Bonus 50 orang terawal (GPTS/Gems TikTok Sales Sniper + TikTok POV Sniper).
        // Server menanda flowBonus:true pada order yang sempat merebut slot. Tanpa
        // tugasan ini, pemenang tidak kelihatan di mana-mana dan bonus tak dihantar.
        if (o.flowBonus === true) tasks.push({ key: 'flowBonusDelivered', label: 'Bonus TSS+TPS', done: !!o.flowBonusDelivered });
        if (o.mcnBindDelivered !== undefined) tasks.push({ key: 'mcnBindDelivered', label: 'Bind MCN', done: !!o.mcnBindDelivered });
        // Promo Kembali Premium (/promo): admin hantar link pengaktifan Google AI Pro melalui WhatsApp.
        // ⚠️ aiProDelivered + aiProDeliveredAt WAJIB ada dalam whitelist firestore.rules /orders.
        if (o.promo === PROMO_AI_PRO_ID) tasks.push({ key: 'aiProDelivered', label: 'Google AI Pro', done: !!o.aiProDelivered });
        return tasks;
    };

    // Export ke CSV — ambil SEMUA baris yang sepadan tapisan semasa (bukan yang dimuat je),
    // dengan page melalui server (cap 5000 utk elak muat-turun gergasi).
    const exportSalesCSV = async () => {
        setSalesExporting(true);
        try {
            let rows;
            if (salesSearchTerm) {
                rows = salesRecords; // mod carian email — dah semua order utk email tu
            } else if (salesSource === 'manual') {
                // Card Manual: resit manual sudah dimuat penuh — tapis tarikh + status di sini (sama seperti senarai).
                if (salesManual.gagal) { alert('Rekod manual gagal dimuat. Tekan Refresh dan cuba lagi.'); return; }
                const r = salesDateRange();
                rows = salesManual.rows.filter(o => dalamJulatTarikh(o, r));
                if (salesFilter === 'paid' || salesFilter === 'pending' || salesFilter === 'failed') rows = rows.filter(o => o.status === salesFilter);
                else if (salesFilter === 'delivery_pending') rows = rows.filter(o => o.status === 'paid' && getSalesOrderTasks(o).some(t => !t.done));
            } else {
                const base = collection(db, 'orders');
                const cons = [];
                if (salesFilter === 'paid' || salesFilter === 'pending' || salesFilter === 'failed') cons.push(where('status', '==', salesFilter));
                else if (salesFilter === 'delivery_pending') cons.push(where('status', '==', 'paid'));
                cons.push(...salesDateConstraints());
                rows = [];
                let cur = null; const CAP = 5000, BATCH = 500;
                while (rows.length < CAP) {
                    const qy = query(base, ...cons, orderBy('createdAt', 'desc'), ...(cur ? [startAfter(cur)] : []), firestoreLimit(BATCH));
                    const snap = await getDocs(qy);
                    if (!snap.docs.length) break;
                    rows.push(...snap.docs.map(d => ({ id: d.id, ...d.data() })));
                    cur = snap.docs[snap.docs.length - 1];
                    if (snap.docs.length < BATCH) break;
                }
                if (salesFilter === 'delivery_pending') rows = rows.filter(o => getSalesOrderTasks(o).some(t => !t.done));
            }
            rows = rows.filter(o => padanSumber(o, salesSource)); // card Semua / Billplz / Manual
            if (!rows.length) { alert('Tiada rekod untuk dieksport.'); return; }
            const header = ['Order ID', 'Tarikh', 'Nama', 'Email', 'WhatsApp', 'Jenis', 'Pakej', 'Amaun (RM)', 'Status', 'Sumber', 'Flow Ultra', 'Bind MCN', 'Google AI Pro', 'Channel', 'Ref Afiliat', 'Bill ID'];
            const fmt = (ts) => {
                try { const d = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null); return d ? d.toLocaleString('ms-MY') : ''; } catch { return ''; }
            };
            const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
            const lines = rows.map(o => [
                o.id, fmt(o.createdAt), o.name, o.email, o.whatsappNumber,
                jenisOrder(o),
                namaPakejOrder(o),
                ((o.amountSen || 0) / 100).toFixed(2), o.status,
                SUMBER_LABEL[sumberOrder(o)],
                o.flowAddon ? (o.flowDelivered ? 'Ya' : 'Belum') : '-',
                o.mcnBindDelivered !== undefined ? (o.mcnBindDelivered ? 'Ya' : 'Belum') : '-',
                o.promo === PROMO_AI_PRO_ID ? (o.aiProDelivered ? 'Ya' : 'Belum') : '-',
                o.paymentChannel || '',
                o.refCode || '', o.billplzBillId || ''
            ].map(esc).join(','));
            const csv = [header.map(esc).join(','), ...lines].join('\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `rekod-jualan-${klDayKey()}.csv`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.warn('Export CSV failed:', err);
            alert('Gagal eksport CSV: ' + (err?.message || err));
        } finally {
            setSalesExporting(false);
        }
    };

    // Tanda tugasan penghantaran order sudah dihantar (admin serah manual via WhatsApp).
    // field: 'flowDelivered' (Flow Ultra) | 'flowAccountDelivered' (Akaun Flow) | 'mcnBindDelivered' (Bind MCN)
    // — field + field+'At' WAJIB ada dalam whitelist hasOnly() firestore.rules /orders.
    const markOrderTask = async (orderId, field) => {
        try {
            await updateDoc(doc(db, 'orders', orderId), { [field]: true, [field + 'At']: serverTimestamp() });
            setSalesRecords(prev => prev.map(o => o.id === orderId ? { ...o, [field]: true } : o));
            setSalesManual(prev => ({ ...prev, rows: prev.rows.map(o => o.id === orderId ? { ...o, [field]: true } : o) }));
        } catch (err) {
            console.warn('Failed to mark order task:', err);
            alert('Gagal tanda dihantar. Cuba lagi.');
        }
    };

    // ═══ FETCH MODULES LIST (for allowedModules checkboxes) ═══
    // One-shot via getDocs. Modules jarang berubah — admin re-load page kalau
    // tambah modul baru.
    const loadModules = useCallback(async () => {
        try {
            const snap = await getDocs(collection(db, 'modules'));
            const mods = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            // SMART (smartcustom) = module registry-only (TIADA doc Firestore) — tambah manual
            // supaya muncul dalam checkbox preset campaign / Aktifkan / edit user.
            if (!mods.some(m => m.id === 'smartcustom')) {
                mods.push({ id: 'smartcustom', name: 'SMART', isPremiumAddon: true, adminOnly: false, order: 5 });
            }
            /* FB Reels (v4) = CIRI, bukan formula. Sengaja TIADA doc Firestore dan TIADA
               entri registry — kalau ada, ia akan muncul sebagai kad formula dalam
               komposer workspace. Ia hidup semata-mata sebagai kebenaran dalam
               licenses/{uid}.allowedModules, dan ditolak masuk di sini supaya admin ada
               kotak tandanya (corak SAMA seperti smartcustom di atas).
               isPremiumAddon:true = wildcard '*' TIDAK memberinya — lapisan kedua;
               wildcard sendiri sudah dibuang daripada semua lesen 2026-08-30. */
            if (!mods.some(m => m.id === 'fbreels')) {
                mods.push({ id: 'fbreels', name: 'FB Reels', isPremiumAddon: true, adminOnly: false, order: 6 });
            }
            mods.sort((a, b) => (a.order || 0) - (b.order || 0));
            setModulesList(mods);
        } catch (err) {
            console.warn('Failed to fetch modules:', err);
        }
    }, []);

    useEffect(() => {
        loadModules();
    }, [loadModules]);

    // ═══ FETCH CAMPAIGNS (for user tagging) ═══
    const loadCampaigns = useCallback(async () => {
        if (!isAuthorized) return;
        try {
            const snap = await getDocs(collection(db, 'campaigns'));
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setCampaigns(list);
        } catch (err) {
            console.warn('Failed to fetch campaigns:', err);
        }
    }, [isAuthorized]);

    useEffect(() => {
        loadCampaigns();
    }, [loadCampaigns]);

    // ═══ FETCH CAMPAIGN COUNTS (aggregate counters — server-maintained) ═══
    // Sumber count untuk badge dropdown. Maintained oleh adminUpdateUser (transactional)
    // + backfillCampaignCounts (one-shot rebuild). Doc '__none__' = user tanpa tag.
    //
    // Auto-heal: SETIAP kali admin dashboard load (sekali per session), trigger
    // backfill di background. Sebab counter boleh drift dari new signup atau
    // pre-deploy assignments. Function idempotent — safe untuk panggil berulang.
    const loadCampaignCounts = useCallback(async () => {
        if (!isAuthorized) return;
        try {
            const snap = await getDocs(collection(db, 'campaign_counts'));
            const counts = { none: 0, byId: {}, initialized: !snap.empty };
            snap.docs.forEach(d => {
                const c = Math.max(0, d.data()?.count || 0);
                if (d.id === '__none__') counts.none = c;
                else counts.byId[d.id] = c;
            });
            setCampaignCountsState(counts);
        } catch (err) {
            console.warn('Failed to fetch campaign counts:', err);
        }
    }, [isAuthorized]);

    useEffect(() => {
        if (!isAuthorized) return;

        // Trigger backfill sekali per session (sessionStorage flag).
        const sessionKey = 'campaign_counts_synced_session';
        if (!sessionStorage.getItem(sessionKey)) {
            sessionStorage.setItem(sessionKey, '1');
            console.log('[AdminView] Auto-sync campaign counts (sekali per session)...');
            backfillCampaignCountsFn({}).then((r) => {
                console.log('[AdminView] Auto-sync selesai:', r?.data);
                // Selepas backfill, refresh counts sekali lagi untuk dapat nilai terkini.
                loadCampaignCounts();
            }).catch((err) => {
                console.warn('[AdminView] Auto-sync gagal:', err?.message);
                sessionStorage.removeItem(sessionKey); // boleh retry next reload
            });
        }

        loadCampaignCounts();
    }, [isAuthorized, loadCampaignCounts]);

    // ═══ CAMPAIGN CRUD HANDLERS ═══
    const handleSaveCampaign = async () => {
        if (!editingCampaign) return;
        const name = (editingCampaign.name || '').trim();
        if (!name) {
            alert('Nama campaign tidak boleh kosong.');
            return;
        }
        const description = (editingCampaign.description || '').trim();
        try {
            if (editingCampaign.id) {
                await updateDoc(doc(db, 'campaigns', editingCampaign.id), {
                    name, description, updatedAt: serverTimestamp()
                });
            } else {
                await addDoc(collection(db, 'campaigns'), {
                    name, description,
                    createdAt: serverTimestamp(),
                    createdBy: auth.currentUser?.email || null,
                });
            }
            setEditingCampaign(null);
            // Refresh list (subscription dah ditukar ke getDocs).
            loadCampaigns();
        } catch (err) {
            alert('Ralat simpan campaign: ' + err.message);
        }
    };

    const handleDeleteCampaign = async (campaignId, campaignName) => {
        const affected = users.filter(u => u.campaign === campaignId);
        const msg = affected.length > 0
            ? `Padam campaign "${campaignName}"?\n\n${affected.length} pengguna berkait akan kehilangan tag ini (data user lain tidak terjejas).`
            : `Padam campaign "${campaignName}"?`;
        if (!window.confirm(msg)) return;
        try {
            await deleteDoc(doc(db, 'campaigns', campaignId));
            // Clear campaign field on affected users (best-effort via Cloud Function)
            for (const u of affected) {
                try {
                    await adminUpdateUserFn({ uid: u.uid, campaign: null });
                } catch (clearErr) {
                    console.warn('[Campaign] Failed to clear campaign on user', u.uid, clearErr);
                }
            }
            setUsers(prev => prev.map(u => u.campaign === campaignId ? { ...u, campaign: null } : u));
            // Refresh campaigns + counts selepas delete.
            loadCampaigns();
            loadCampaignCounts();
        } catch (err) {
            alert('Ralat padam campaign: ' + err.message);
        }
    };

    // ═══ USER CAMPAIGN UPDATE ═══
    const handleUpdateUserCampaign = async (uid, campaignId) => {
        setUpdatingUser(uid);
        try {
            await adminUpdateUserFn({ uid, campaign: campaignId || null });
            setUsers(prev => prev.map(u => u.uid === uid ? { ...u, campaign: campaignId || null } : u));
            // Counter campaign_counts dah update server-side — refresh untuk reflect kat UI.
            loadCampaignCounts();
        } catch (err) {
            alert('Ralat update campaign: ' + err.message);
        } finally {
            setUpdatingUser(null);
        }
    };

    const handleBulkAssignCampaign = async () => {
        if (selectedUsers.size === 0) return;
        const targetLabel = bulkCampaignId
            ? (campaigns.find(c => c.id === bulkCampaignId)?.name || bulkCampaignId)
            : 'None (kosongkan tag)';
        if (!window.confirm(`Tetapkan campaign "${targetLabel}" untuk ${selectedUsers.size} pengguna terpilih?`)) return;
        const uids = Array.from(selectedUsers);
        try {
            for (const uid of uids) {
                await adminUpdateUserFn({ uid, campaign: bulkCampaignId || null });
            }
            setUsers(prev => prev.map(u => uids.includes(u.uid) ? { ...u, campaign: bulkCampaignId || null } : u));
            setShowBulkAssignCampaign(false);
            setSelectedUsers(new Set());
            // Counter campaign_counts dah update server-side — refresh.
            loadCampaignCounts();
            alert(`Campaign berjaya ditetapkan untuk ${uids.length} pengguna.`);
        } catch (err) {
            alert('Ralat bulk assign: ' + err.message);
        }
    };

    useEffect(() => {
        if (isAuthorized) {
            // Auto-fetch first page of users + total counts on mount
            if (hasPermission(userRole, PERMISSIONS.VIEW_CLIENTS)) {
                fetchUsersPage(0);
                fetchTotalCounts();
            }
        }
        // Disable exhaustive-deps: fetch fns are stable closures, tak perlu masuk deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthorized, userRole]);

    // ═══ AUTO-SWITCH SCOPE bila filter aktif ═══
    // Strategy:
    //  - Carian → runUsersSmartSearch (query terus ke server, corak Rekod Jualan) — effect bawah
    //  - Campaign filter (specific ID) → paginated server-side (where + cursor)
    //  - Tier filter / 'none' / sort → load all (fallback, sebab susah index)
    //  - Tiada filter → paginated default (createdAt desc)
    // ⚠️ searchTerm SENGAJA dikeluarkan dari isHeavyFilter (2026-08-28): dulu carian
    // mencetus fetchAllUsers (muat SEMUA 1,700+ akaun = "refresh lama"), dan kalau admin
    // taip semasa senarai masih loading, guard `loading` di bawah melangkau fetch itu
    // TERUS (dulu `loading` tiada dlm deps) → carian tersangkut menapis 50 akaun page
    // semasa → akaun yang wujud dilapor "tiada dalam system".
    const isHeavyFilter = filterTier !== 'all' || filterCampaign === 'none' || !!userSort.field;
    const isCampaignFiltered = filterCampaign !== 'all' && filterCampaign !== 'none';
    useEffect(() => {
        if (!isAuthorized) return;
        if (!hasPermission(userRole, PERMISSIONS.VIEW_CLIENTS)) return;
        if (searchTerm) return; // mod carian diurus oleh effect carian sendiri (bawah)
        if (loading) return; // selamat: `loading` ada dlm deps, effect ulang bila fetch selesai

        if (isHeavyFilter) {
            // Tier / sort / 'none' bucket — kena fetch all (no efficient index)
            if (usersScope !== 'all') fetchAllUsers();
        } else if (isCampaignFiltered) {
            // Specific campaign — paginated dgn where()
            const needRefetch = usersScope !== 'page'
                || cursorStackRef.current.__campaignFilter !== filterCampaign;
            if (needRefetch) fetchUsersPage(0, { campaignFilter: filterCampaign });
        } else {
            // No filter — default paginated. Scope 'search' pun jatuh ke sini bila
            // carian dikosongkan (sebab tu searchTerm ada dlm deps).
            const needRefetch = usersScope !== 'page'
                || cursorStackRef.current.__campaignFilter != null;
            if (needRefetch) fetchUsersPage(0);
        }
        // Disable exhaustive-deps: fetch fns are stable closures, tak perlu masuk deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHeavyFilter, isCampaignFiltered, filterCampaign, usersScope, searchTerm, loading, isAuthorized, userRole]);

    // ═══ CARIAN — tembak setiap kali term KOMITED berubah (Enter/Cari ditekan) ═══
    // SENGAJA tiada guard `loading`: itulah punca bug lama "taip semasa loading =
    // carian tak pernah dijalankan". Carian baharu sentiasa menang.
    useEffect(() => {
        if (!isAuthorized) return;
        if (!hasPermission(userRole, PERMISSIONS.VIEW_CLIENTS)) return;
        if (!searchTerm) return;
        runUsersSmartSearch(searchTerm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, isAuthorized, userRole]);

    // Tukar filter/search → balik ke halaman pertama (untuk client-side pagination mod 'all').
    useEffect(() => { setCurrentPage(0); }, [filterTier, searchTerm]);

    const handleUpdateTier = async (uid, newTier) => {
        setUpdatingUser(uid);
        try {
            await adminUpdateUserFn({ uid, tier: newTier });
            setUsers(prev => prev.map(u => u.uid === uid ? { ...u, tier: newTier } : u));
        } catch (err) {
            alert('Gagal update: ' + err.message);
        } finally {
            setUpdatingUser(null);
        }
    };

    // Aktifkan manual dari Users tab — guna modal pre-activate yang sama dgn Pending Premium.
    // Beza: submissionId null (takda payment_submissions record), mode='manual' (untuk UI badge),
    // pre-fill dari user existing (preserve campaign + modul kalau dah ada).
    // Auto-detect renewal: kalau user dah ada license existing, treat as renewal (RM99).
    const handleApprovePayment = (user) => {
        const isRenewal = !!user.license;
        const pseudoSubmission = {
            id: null,
            uid: user.uid,
            email: user.email,
            amount: isRenewal ? 9900 : 19900,
            type: isRenewal ? 'renewal' : 'new',
            submittedAt: null,
            whatsappNumber: user.whatsappNumber || '',
            proofImageUrl: null
        };
        // Default pakej ikut auto-detect: user dah ada lesen → Premium Renew, else Premium Baru.
        // Admin WAJIB semak & tukar ke Fighter/Custom kalau bayaran sebenar lain (elak komisen salah).
        const defaultPkg = isRenewal
            ? ACTIVATION_PACKAGES.find(p => p.id === 'premium_renew')
            : ACTIVATION_PACKAGES.find(p => p.id === 'premium_new');
        setActivatePendingModal({
            submission: pseudoSubmission,
            campaignId: user.campaign || '',
            allowedModules: (() => {
                const mods = user.license?.allowedModules;
                if (!mods || mods.length === 0) return [...PREMIUM_DEFAULT_MODULES];
                const aliases = { 'ai16saat-full': 'ai16saat-half' };
                const validIds = new Set(modulesList.map(m => m.id));
                if (!mods.includes('*')) {
                    const mapped = mods.map(m => aliases[m] || m);
                    return [...new Set(mapped.filter(m => validIds.has(m)))];
                }
                const explicit = modulesList.filter(m => !m.isPremiumAddon).map(m => m.id);
                return [...new Set([...explicit, ...mods.filter(m => m !== '*')])];
            })(),
            packageId: defaultPkg.id,
            priceSen: defaultPkg.priceSen,
            licType: defaultPkg.licType,
            customPriceRM: '',
            durationDays: defaultPkg.durationDays,
            // Kuota = default pakej + slot tambahan (dahulu: salin nombor lesen lama).
            lesenSedia: user.license ? { maxProfiles: user.license.maxProfiles, slotTambahan: user.license.slotTambahan } : null,
            ...cadangKuotaPakej(defaultPkg.maxProfiles, user.license
                ? { maxProfiles: user.license.maxProfiles, slotTambahan: user.license.slotTambahan } : null),
            showAdvanced: false,
            mode: 'manual'  // 'pending' (dari Pending Premium) | 'manual' (dari Users tab)
        });
    };

    const handleResetUsage = async (uid) => {
        setUpdatingUser(uid);
        try {
            await adminUpdateUserFn({ uid, action: 'resetUsage' });
            setUsers(prev => prev.map(u => u.uid === uid
                ? { ...u, dailyTranscribeUsed: 0, dailyRewriteUsed: 0 }
                : u
            ));
        } catch (err) {
            alert('Gagal reset: ' + err.message);
        } finally {
            setUpdatingUser(null);
        }
    };

    // Reset trial coins → 5 (Feature: admin manual reset percubaan percuma)
    const handleResetTrialCoins = async (uid) => {
        setUpdatingUser(uid);
        try {
            await adminUpdateUserFn({ uid, action: 'resetTrialCoins' });
            setUsers(prev => prev.map(u => u.uid === uid ? { ...u, pipelineCoins: 5 } : u));
        } catch (err) {
            alert('Gagal reset coins: ' + err.message);
        } finally {
            setUpdatingUser(null);
        }
    };

    // Reset pipeline cap → 0 (Feature: admin manual reset had 100/hari paid user)
    const handleResetPipeline = async (uid) => {
        setUpdatingUser(uid);
        try {
            await adminUpdateUserFn({ uid, action: 'resetPipeline' });
            setUsers(prev => prev.map(u => u.uid === uid ? { ...u, dailyPipelineUsed: 0 } : u));
        } catch (err) {
            alert('Gagal reset pipeline: ' + err.message);
        } finally {
            setUpdatingUser(null);
        }
    };

    // Bulk reset Chrome profile → kosongkan activeProfiles untuk user terpilih
    const handleBulkResetProfiles = async () => {
        if (selectedUsers.size === 0) return;
        if (!window.confirm(`Reset Chrome profile untuk ${selectedUsers.size} pengguna terpilih?\n\nSemua profile aktif mereka akan dikosongkan — mereka boleh daftar browser baharu selepas ini.`)) return;
        try {
            const res = await adminResetChromeProfilesFn({ uids: Array.from(selectedUsers) });
            setSelectedUsers(new Set());
            alert(`Selesai. ${res.data.cleared} direset, ${res.data.skipped} dilangkau (tiada lesen).`);
        } catch (err) {
            alert('Ralat reset profile: ' + err.message);
        }
    };

    // ═══ DELETE USER (hard delete — email boleh re-register) ═══
    const [deleteUserTarget, setDeleteUserTarget] = useState(null); // { uid, email }
    const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    const openDeleteUserModal = (user) => {
        setDeleteUserTarget({ uid: user.uid, email: user.email });
        setDeleteConfirmInput('');
    };

    const closeDeleteUserModal = () => {
        if (deleteLoading) return;
        setDeleteUserTarget(null);
        setDeleteConfirmInput('');
    };

    const handleConfirmDeleteUser = async () => {
        if (!deleteUserTarget) return;
        const { uid, email } = deleteUserTarget;
        if (deleteConfirmInput.trim().toLowerCase() !== (email || '').trim().toLowerCase()) {
            alert('Email tidak sepadan. Sila taip email user dengan tepat.');
            return;
        }
        setDeleteLoading(true);
        try {
            const res = await adminDeleteUserFn({ uid, confirmEmail: deleteConfirmInput.trim() });
            const s = res?.data?.summary || {};
            setUsers(prev => prev.filter(u => u.uid !== uid));
            alert(
                `Berjaya delete user: ${email}\n\n` +
                `• Firestore doc: ${s.firestoreDocDeleted ? 'ok' : '-'}\n` +
                `• Auth account: ${s.authDeleted ? 'ok (email boleh register semula)' : '-'}\n` +
                `• Products: ${s.productsDeleted}\n` +
                `• Transcriptions: ${s.transcriptionsDeleted}\n` +
                `• Spaces: ${s.spacesDeleted}\n` +
                `• License keys reset: ${s.licenseKeysReset}\n` +
                `• License doc: ${s.licensesDeleted ? 'deleted' : '-'}\n` +
                `• Subscription: ${s.subscriptionsDeleted ? 'deleted' : '-'}\n` +
                `• Affiliate profile: ${s.affiliatesDeleted ? 'deleted' : '-'}\n` +
                `• Referrals cleaned: ${s.referralsDeleted}`
            );
            setDeleteUserTarget(null);
            setDeleteConfirmInput('');
        } catch (err) {
            alert('Gagal delete user: ' + (err?.message || 'unknown error'));
        } finally {
            setDeleteLoading(false);
        }
    };

    // ═══ COPY LESEN BELUM MASUK GPT (Rekod Jualan) ═══
    //   Kumpul license key buyer PAID yang order dia BELUM ditanda "Dalam GPT" (o.lesenInGpt != true).
    //   Order simpan uid je (bukan key) → ambil key dari licenses/{uid}. Langkau expired & pendua.
    //   Copy senarai bersih → user paste di HUJUNG "SENARAI LESEN SAH" GPT → confirm → auto-tick order.
    const copyLesenChatGPT = async () => {
        setIsCopyingLesen(true);
        try {
            // Semua lesen → map ikut uid (doc id = uid) + status aktif/expired
            const licSnap = await getDocs(collection(db, 'licenses'));
            const now = Date.now();
            const licByUid = {};
            licSnap.forEach(d => {
                const data = d.data();
                const key = (data.licenseKey || '').trim();
                if (!key) return;
                const expMs = data.expiresAt?.toMillis ? data.expiresAt.toMillis()
                    : (data.expiresAt?.seconds ? data.expiresAt.seconds * 1000 : 0);
                licByUid[d.id] = { key, active: expMs > now };
            });

            // Order PAID + belum tick → ambil lesen ikut uid, langkau expired/tiada, buang pendua.
            // Query SEMUA order paid (bukan yang dimuat di paparan je) supaya tiada buyer tercicir.
            const paidSnap = await getDocs(query(collection(db, 'orders'), where('status', '==', 'paid')));
            const targets = paidSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => !o.lesenInGpt && o.uid);
            const seen = new Set();
            const copyKeys = [];
            const orderIdsToMark = [];
            let skippedExpired = 0, skippedNoLic = 0;
            for (const o of targets) {
                const lic = licByUid[o.uid];
                if (!lic) { skippedNoLic++; continue; }
                if (!lic.active) { skippedExpired++; continue; }
                orderIdsToMark.push(o.id);
                if (!seen.has(lic.key)) { seen.add(lic.key); copyKeys.push(lic.key); }
            }
            copyKeys.sort();

            if (!copyKeys.length) {
                alert('Takda lesen baru untuk dicopy.'
                    + (skippedExpired ? `\n${skippedExpired} order dilangkau (lesen expired).` : '')
                    + (skippedNoLic ? `\n${skippedNoLic} order takda lesen.` : ''));
                return;
            }

            await navigator.clipboard.writeText(copyKeys.join('\n'));

            const ok = confirm(
                `${copyKeys.length} lesen baru disalin`
                + (skippedExpired ? ` (${skippedExpired} expired dilangkau)` : '') + `.\n\n`
                + `Paste di HUJUNG blok "SENARAI LESEN SAH" dalam ChatGPT Instructions.\n\n`
                + `Dah paste? OK = tandakan ${orderIdsToMark.length} order ni "Dalam GPT". Cancel = biar belum tick.`
            );
            if (ok) {
                const { writeBatch } = await import('firebase/firestore');
                for (let i = 0; i < orderIdsToMark.length; i += 400) {
                    const chunk = orderIdsToMark.slice(i, i + 400);
                    const batch = writeBatch(db);
                    chunk.forEach(id => batch.update(doc(db, 'orders', id), { lesenInGpt: true, lesenInGptAt: serverTimestamp() }));
                    await batch.commit();
                }
                setSalesRecords(prev => prev.map(o => orderIdsToMark.includes(o.id) ? { ...o, lesenInGpt: true } : o));
            }
        } catch (err) {
            console.error('copyLesenChatGPT failed:', err);
            alert('Gagal salin lesen: ' + (err?.message || err));
        } finally {
            setIsCopyingLesen(false);
        }
    };

    // Toggle tanda "Dalam GPT" pada satu order (manual tick/untick).
    // field: 'lesenInGpt' (+ 'lesenInGptAt') — WAJIB whitelist dalam firestore.rules /orders.
    const toggleOrderGptFlag = async (orderId, current) => {
        try {
            await updateDoc(doc(db, 'orders', orderId), { lesenInGpt: !current, lesenInGptAt: serverTimestamp() });
            setSalesRecords(prev => prev.map(o => o.id === orderId ? { ...o, lesenInGpt: !current } : o));
            setSalesManual(prev => ({ ...prev, rows: prev.rows.map(o => o.id === orderId ? { ...o, lesenInGpt: !current } : o) }));
        } catch (err) {
            console.warn('Failed to toggle GPT flag:', err);
            alert('Gagal tanda. Cuba lagi.');
        }
    };

    const handleSaveWhatsapp = async () => {
        if (!editingWhatsappUser) return;
        // Kemaskan jadi "+60…" — nombor bertampal "60 19-760 2796" tak dijumpai carian server.
        const newWa = kemasNomborMy(editingWhatsappUser._new ?? editingWhatsappUser.whatsappNumber ?? '');
        setSavingWhatsapp(true);
        try {
            await updateDoc(doc(db, 'users', editingWhatsappUser.uid), { whatsappNumber: newWa });
            setUsers(prev => prev.map(u => u.uid === editingWhatsappUser.uid ? { ...u, whatsappNumber: newWa } : u));
            setEditingWhatsappUser(null);
        } catch (err) {
            console.error('Gagal kemaskini WhatsApp:', err);
            alert('Gagal kemaskini WhatsApp. Cuba lagi.');
        } finally {
            setSavingWhatsapp(false);
        }
    };

    const handleUpdateClient = async (uid, newExpiry, newMaxProfiles, newAllowedModules, newWhatsapp, newSlotTambahan) => {
        try {
            const updateData = { expiresAt: newExpiry };
            if (newMaxProfiles !== undefined) updateData.maxProfiles = newMaxProfiles;
            // undefined = admin tidak menyentuh slot tambahan (nilai sudah disemak di butang Simpan).
            if (newSlotTambahan !== undefined) updateData.slotTambahan = newSlotTambahan;
            if (newAllowedModules !== undefined) updateData.allowedModules = newAllowedModules;
            await updateDoc(doc(db, 'licenses', uid), updateData);

            // Auto-sync: Set user tier to 'paid' when their license is updated/active
            // Also update whatsappNumber if changed
            const userUpdate = { tier: 'paid', paidExpiry: newExpiry }; // sync paparan client (users.paidExpiry) dgn tarikh gate (licenses.expiresAt)
            if (newWhatsapp !== undefined && newWhatsapp !== null) {
                userUpdate.whatsappNumber = kemasNomborMy(newWhatsapp);
            }
            await updateDoc(doc(db, 'users', uid), userUpdate).catch(e => console.warn('Sync users doc failed:', e));
            setEditingUser(null);
            fetchUsers();
        } catch (err) {
            console.error('Error updating client:', err);
            alert('Gagal kemaskini.');
        }
    };

    const handleRemoveProfile = async (uid, profileIdToRemove) => {
        try {
            const licenseDocRef = doc(db, 'licenses', uid);
            const licenseSnap = await getDoc(licenseDocRef);
            if (licenseSnap.exists()) {
                const current = licenseSnap.data().activeProfiles || [];
                const updated = current.filter(p => p !== profileIdToRemove);
                await updateDoc(licenseDocRef, { activeProfiles: updated });
                
                // Update local state locally to prevent heavy full table refetch
                if (editingUser?.uid === uid) {
                    setEditingUser({ ...editingUser, license: { ...editingUser.license, activeProfiles: updated } });
                }
                setUsers(prevUsers => prevUsers.map(u => {
                    if (u.uid === uid) {
                        return { ...u, license: { ...u.license, activeProfiles: updated } };
                    }
                    return u;
                }));
            }
        } catch (err) {
            console.error('Error removing profile:', err);
            alert('Gagal mengeluarkan profile.');
        }
    };

    const handleDeleteLicense = async (uid, licenseKey) => {
        const confirmMsg = licenseKey
            ? `Padam license untuk user ini?\n\nUID: ${uid}\nKey: ${licenseKey}\n\nLicense key akan di-reset dan boleh digunakan semula.`
            : `Padam license untuk user ini?\n\nUID: ${uid}`;
        if (!confirm(confirmMsg)) return;
        try {
            // 1. Delete license document
            await deleteDoc(doc(db, 'licenses', uid));

            // Auto-sync: Revert user tier to 'free' when their license is deleted
            await updateDoc(doc(db, 'users', uid), { tier: 'free' }).catch(e => console.warn('Sync users.tier failed:', e));

            // 2. Reset the license key to unused (so it can be reused)
            if (licenseKey && licenseKey !== 'ADMIN-ACCESS') {
                try {
                    const keyDocRef = doc(db, 'licenseKeys', licenseKey);
                    const keySnap = await getDoc(keyDocRef);
                    if (keySnap.exists()) {
                        await updateDoc(keyDocRef, {
                            used: false,
                            usedBy: null,
                            usedByEmail: null,
                            usedAt: null
                        });
                    }
                } catch (keyErr) {
                    console.warn('Could not reset license key:', keyErr);
                }
            }

            alert('License berjaya dipadam!');
            setEditingUser(null);
            fetchUsers();
        } catch (err) {
            console.error('Error deleting license:', err);
            alert('Gagal padam license: ' + err.message);
        }
    };

    // Filters
    // Sort merentas SEMUA user: bila admin klik header sort, kita muat semua user
    // dulu (via isHeavyFilter → fetchAllUsers), susun dalam memori, kemudian
    // paginate client-side (50/halaman) supaya pagination kekal.
    const toggleSort = (field) => {
        setCurrentPage(0); // balik ke halaman pertama bila tukar sort
        setUserSort(prev => prev.field === field
            ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { field, dir: 'asc' }
        );
    };

    const sortIcon = (field) => {
        if (userSort.field !== field) return 'unfold_more';
        return userSort.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
    };

    // Mod carian ('search'): baris datang TERUS dari query server (corak Rekod Jualan)
    // — jangan tapis lagi di client (tapisan tier/campaign pun dipadamkan dlm UI).
    // Salinan array supaya blok sort di bawah tak mutate state `users`.
    // Mod lain: tapis client-side macam biasa (searchTerm sentiasa '' di sini sebab
    // mod carian dah diasingkan, tapi syarat matchesSearch dikekalkan sbg jaring).
    const searchLc = searchTerm.toLowerCase();
    const searchDigits = searchTerm.replace(/\D/g, ''); // buang +, jarak, sengkang, kurungan
    const filteredUsers = usersScope === 'search' ? [...users] : users.filter(u => {
        const matchesSearch = !searchTerm ||
            u.email?.toLowerCase().includes(searchLc) ||
            u.name?.toLowerCase().includes(searchLc) ||
            u.uid?.toLowerCase().includes(searchLc) ||
            u.license?.licenseKey?.toLowerCase().includes(searchLc) ||
            u.whatsappNumber?.toLowerCase().includes(searchLc) ||
            // Padanan nombor abai-format: banding angka sahaja kedua-dua belah (min 3 digit).
            (searchDigits.length >= 3 && (u.whatsappNumber || '').replace(/\D/g, '').includes(searchDigits));
        const isExpiredLicense = u.tier === 'paid'
            && u.license?.expiresAt
            && new Date(u.license.expiresAt) < new Date();
        const matchesTier =
            filterTier === 'all' ? true
            : filterTier === 'expired' ? isExpiredLicense
            : u.tier === filterTier;
        const matchesCampaign =
            filterCampaign === 'all' ||
            (filterCampaign === 'none' && !u.campaign) ||
            (filterCampaign !== 'none' && filterCampaign !== 'all' && u.campaign === filterCampaign);
        return matchesSearch && matchesTier && matchesCampaign;
    });

    // Sort Users manually via userSort
    if (userSort.field) {
        const getExpiry = (c) => {
            if (!c.license?.expiresAt) return 0;
            return new Date(c.license.expiresAt).getTime();
        };
        const getProfiles = (c) => c.license?.activeProfiles?.length || 0;
        const getTierVal = (c) => {
            const t = c.tier || 'free';
            return t === 'admin' ? 3 : t === 'paid' ? 2 : 1;
        };
        const getValue = userSort.field === 'expires' ? getExpiry : userSort.field === 'profiles' ? getProfiles : getTierVal;
        const dir = userSort.dir === 'asc' ? 1 : -1;

        filteredUsers.sort((a, b) => (getValue(a) - getValue(b)) * dir);
    }

    // Pagination client-side untuk mod 'all' (semua user dalam memori) supaya
    // pagination 50/halaman kekal walaupun selepas sort merentas semua user.
    // Mod 'page' (Firestore cursor) sudah pulang 50 sahaja, jadi guna terus.
    const totalAllPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
    const allModePage = Math.min(currentPage, totalAllPages - 1);
    const pagedUsers = usersScope === 'all'
        ? filteredUsers.slice(allModePage * PAGE_SIZE, allModePage * PAGE_SIZE + PAGE_SIZE)
        : filteredUsers;

    // Stats from server-side counters (1 read each via aggregation, not full collection scan)
    const stats = {
        total: totalCounts.total,
        free: Math.max(0, totalCounts.total - totalCounts.paid - ADMIN_MOD_COUNT),
        paid: totalCounts.paid,
        admin: ADMIN_MOD_COUNT,
    };

    // Counter doc adalah source of truth — dimaintain oleh adminUpdateUser server-side.
    // Fallback: kalau counter belum initialized (auto-backfill tengah berjalan), derive
    // dari `users` array. Drift kecik OK sebab UI badge je — bukan critical path.
    const campaignCounts = campaignCountsState.initialized
        ? campaignCountsState
        : users.reduce((acc, u) => {
            if (!u.campaign) acc.none += 1;
            else acc.byId[u.campaign] = (acc.byId[u.campaign] || 0) + 1;
            return acc;
        }, { none: 0, byId: {}, initialized: false });

    // ─── Expiring: senarai asas selepas tapis "Tamat dari" (kalau admin pilih tarikh) ───
    // expiringFromDate kosong → guna semua. Ada → tapis expiresAt >= mula tarikh dipilih (waktu tempatan).
    const expiringBase = expiringFromDate
        ? expiringSoon.filter(e => e.expiresAt && e.expiresAt >= new Date(expiringFromDate + 'T00:00:00'))
        : expiringSoon;
    // Had min/max kotak tarikh = julat tarikh sebenar dalam data yang dimuat (elak pilih tarikh kosong).
    const _expDates = expiringSoon.map(e => e.expiresAt).filter(Boolean);
    const _toDateInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const expiringMinDate = _expDates.length ? _toDateInput(new Date(Math.min(..._expDates))) : '';
    const expiringMaxDate = _expDates.length ? _toDateInput(new Date(Math.max(..._expDates))) : '';

    // ═══ BULK SELECTION HANDLERS ═══
    useEffect(() => {
        dragSet.current = new Set(selectedUsers);
    }, [selectedUsers]);

    useEffect(() => {
        const handleGlobalMouseUp = () => { 
            if (isDragging.current) {
                isDragging.current = false; 
                setSelectedUsers(new Set(dragSet.current)); // Terus flush perubahan kepada React apabila klik dilepaskan
            }
        };
        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }, []);

    const handleToggleAllUsers = (e) => {
        if (e.target.checked) {
            // Hanya pilih pengguna di page semasa (max 50) — elak select-all tersasar ke ratusan pengguna
            // (bahaya untuk bulk action seperti Reset Chrome Profiles). Nak lebih? select-all page demi page.
            setSelectedUsers(new Set(pagedUsers.map(u => u.uid)));
        } else {
            setSelectedUsers(new Set());
        }
    };

    const applyDragSelection = (uid, willSelect) => {
        if (willSelect === dragSet.current.has(uid)) return;

        const rowEl = document.getElementById(`row-${uid}`);
        const chkEl = document.getElementById(`chk-${uid}`);

        if (willSelect) {
            dragSet.current.add(uid);
            if (rowEl) {
                rowEl.classList.add('bg-indigo-50/50');
                rowEl.classList.add('dark:bg-indigo-900/10');
            }
            if (chkEl) chkEl.checked = true;
        } else {
            dragSet.current.delete(uid);
            if (rowEl) {
                rowEl.classList.remove('bg-indigo-50/50');
                rowEl.classList.remove('dark:bg-indigo-900/10');
            }
            if (chkEl) chkEl.checked = false;
        }
    };

    const handleMouseDownRow = (e, uid) => {
        if (e.button !== 0) return; // Only allow left-clicks
        // Only engage drag-select when user starts drag on the checkbox column.
        // Clicks on other cells let native text selection work (so admin boleh copy email, license key, UID, dll).
        if (!e.target.closest('[data-drag-select="row"]')) return;
        e.preventDefault(); // Prevent ghost-drag on the checkbox cell only

        isDragging.current = true;
        const willSelect = !dragSet.current.has(uid);
        dragAction.current = willSelect ? 'select' : 'deselect';

        applyDragSelection(uid, willSelect);
    };

    const handleMouseEnterRow = (uid) => {
        if (!isDragging.current) return;
        applyDragSelection(uid, dragAction.current === 'select');
    };

    // To allow individual clicks when not dragging, use handleMouseDownRow, it handles both initial click and sets up drag.
    // We can remove handleToggleUserSelection because handleMouseDownRow already does the toggle on initial click.

    const handleBulkUpdateModules = async () => {
        if (selectedUsers.size === 0) return;

        const validUids = [];
        const invalidUids = [];

        // Asingkan pengguna Free-Tier atau tiada lesen
        Array.from(selectedUsers).forEach(uid => {
            const u = users.find(user => user.uid === uid);
            if (u && u.license && u.tier !== 'free') {
                validUids.push(uid);
            } else {
                invalidUids.push(uid);
            }
        });

        if (invalidUids.length > 0) {
            if (!confirm(`Ralat Keselamatan SESAAT.\n\nTerdapat ${invalidUids.length} pengguna 'Free-Tier' atau tanpa lesen dalam pilihan anda.\nPengguna ini akan diabaikan secara automatik untuk mengelakkan salah beri akses.\n\nTeruskan OVERWRITE modul untuk ${validUids.length} pengguna Paid/Admin sahaja?`)) {
                return;
            }
        } else {
            if (!confirm(`Sahkan OVERWRITE module untuk ${validUids.length} pengguna? Modul asal mereka akan digantikan secara total.`)) return;
        }

        if (validUids.length === 0) {
            alert('Proses terbatal. Tiada pengguna Paid/Admin yang sah dikesan dalam pilihan anda.');
            return;
        }

        try {
            for (let i = 0; i < validUids.length; i += 400) {
                const chunk = validUids.slice(i, i + 400);
                const chunkBatch = writeBatch(db);
                chunk.forEach(uid => {
                    const ref = doc(db, 'licenses', uid);
                    // Gunakan update sahaja (ketat) untuk memastikan tiada lesen palsu direka
                    chunkBatch.update(ref, { allowedModules: bulkAllowedModules });
                });
                await chunkBatch.commit();
            }

            // Sync visual UI instantly
            setUsers(prev => prev.map(u => {
                if (validUids.includes(u.uid)) {
                    return { ...u, license: { ...(u.license || {}), allowedModules: bulkAllowedModules } };
                }
                return u;
            }));

            // Optional: Uncheck the ones that are successfully updated, keeping the invalid ones checked so admin sees who they are
            const remainingSet = new Set(selectedUsers);
            validUids.forEach(uid => remainingSet.delete(uid));
            setSelectedUsers(remainingSet);
            // Since we manipulate UI via applyDragSelection (DOM), sync it back
            Array.from(selectedUsers).forEach(uid => {
                if (validUids.includes(uid)) applyDragSelection(uid, false);
            });

            setShowBulkEditModules(false);
            alert(`Akses module berjaya dikemaskini untuk ${validUids.length} pengguna.`);
        } catch (err) {
            console.error('Error during bulk update:', err);
            alert('Ralat Firebase: ' + err.message);
        }
    };

    // Simpan preset campaign (default modul + duration + maxProfiles) ke campaign doc.
    // Optional: propagate ke user existing dlm campaign (via cloud function).
    // Cloud function akan skip expired user automatically (Pilihan C).
    const handleSaveCampaignPreset = async () => {
        if (!targetCampaignForModules) return;

        const campaignId = targetCampaignForModules.id;
        const campaignName = targetCampaignForModules.name;

        if (campaignModulesPick.length === 0) {
            alert('Pilih sekurang-kurangnya satu modul.');
            return;
        }
        if (!Number.isInteger(campaignPresetDuration) || campaignPresetDuration < 1 || campaignPresetDuration > 365) {
            alert('Tempoh license mesti antara 1-365 hari.');
            return;
        }
        if (!Number.isInteger(campaignPresetMaxProfiles) || campaignPresetMaxProfiles < 1 || campaignPresetMaxProfiles > 10) {
            alert('Max profile mesti antara 1-10.');
            return;
        }

        setCampaignModulesSaving(true);
        try {
            // 1. Save preset ke campaign doc
            await updateDoc(doc(db, 'campaigns', campaignId), {
                defaultAllowedModules: campaignModulesPick,
                defaultDurationDays: campaignPresetDuration,
                defaultMaxProfiles: campaignPresetMaxProfiles
            });

            // 2. Optional: apply ke user existing via cloud function (skip expired)
            let applyMsg = '';
            if (campaignPresetApplyExisting) {
                try {
                    const res = await adminApplyCampaignPresetToUsersFn({ campaignId });
                    const r = res.data;
                    applyMsg = `\n\n${r.affected} user aktif dikemaskini.`;
                    if (r.skippedExpired > 0) {
                        applyMsg += ` ${r.skippedExpired} user expired di-skip (kena renew + Aktifkan balik).`;
                    }
                    // Optimistic local update untuk user yang affected
                    setUsers(prev => prev.map(u => {
                        if (u.campaign === campaignId && u.tier === 'paid' && u.license) {
                            const exp = new Date(u.license.expiresAt);
                            if (exp > new Date()) {
                                return {
                                    ...u,
                                    license: {
                                        ...u.license,
                                        allowedModules: campaignModulesPick,
                                        maxProfiles: campaignPresetMaxProfiles
                                    }
                                };
                            }
                        }
                        return u;
                    }));
                } catch (applyErr) {
                    console.error('[AdminView] Apply preset to existing failed:', applyErr);
                    applyMsg = `\n\n⚠️ Apply ke user existing gagal: ${applyErr?.message || 'unknown'}. Preset tetap disimpan.`;
                }
            }

            // Reset state + close modal
            setShowSetCampaignModulesModal(false);
            setTargetCampaignForModules(null);
            setCampaignModulesPick(['ai8saat']);
            setCampaignPresetDuration(30);
            setCampaignPresetMaxProfiles(3);
            setCampaignPresetApplyExisting(false);

            alert(`✅ Preset campaign "${campaignName}" disimpan.${applyMsg}`);
        } catch (err) {
            console.error('[AdminView] Save campaign preset failed:', err);
            alert('Ralat simpan preset: ' + err.message);
        } finally {
            setCampaignModulesSaving(false);
        }
    };

    // ═══ NOT AUTHORIZED ═══
    if (!isAuthorized) {
        return null; // Handled by AdminPinGate wrapper
    }

    // ═══ ADMIN TABS CONFIG — filtered by role ═══
    const isFullAdmin = userRole === ROLES.ADMIN;
    const allTabs = [
        { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={16} />, permission: PERMISSIONS.VIEW_USERS },
        { id: 'users', label: 'Users', icon: <Users size={16} />, permission: PERMISSIONS.VIEW_USERS },
        { id: 'risk', label: 'Risk', icon: <AlertTriangle size={16} />, permission: PERMISSIONS.VIEW_USERS },
        { id: 'pending-premium', label: 'Pending Premium', icon: <Inbox size={16} />, permission: PERMISSIONS.MANAGE_PAYMENT_SUBMISSIONS, badge: pendingSubmissions.length },
        { id: 'sales', label: 'Sales', icon: <CreditCard size={16} />, permission: PERMISSIONS.MANAGE_PAYMENT_SUBMISSIONS },
        { id: 'email', label: 'Email Blast', icon: <Mail size={16} />, permission: PERMISSIONS.MANAGE_EMAIL },
        { id: 'flow', label: 'Flow', icon: <Key size={16} />, permission: PERMISSIONS.MANAGE_FLOW_ULTRA },
        // Kesihatan automation FB Reels — laporan BACA SAHAJA daripada
        // dokumen ringkasan yang ditulis server. Lihat FbHealthTab.jsx.
        { id: 'fbhealth', label: 'FB Reels', icon: <Facebook size={16} />, permission: PERMISSIONS.VIEW_ERROR_LOGS },
        // Tetapan robot TikTok / FB / Flow yang extension baca dari server (config/robot_tetapan). RobotTetapanTab.jsx.
        { id: 'robot', label: 'Robot settings', icon: <Bot size={16} />, permission: PERMISSIONS.EDIT_CONFIG },
        // Rekod kempen promosi — buku rekod bertarikh, BUKAN suis kawalan.
        // Baca nota kepala KempenRekodView.jsx sebelum mengubah apa-apa.
        { id: 'kempen', label: 'Kempen', icon: <Calendar size={16} />, permission: PERMISSIONS.EDIT_CONFIG },
    ];
    const tabs = allTabs.filter(tab => hasPermission(userRole, tab.permission));

    const tierBadge = (user) => {
        // Paid tapi lesen dah tamat tempoh → status 'expired' (guna check sama macam kolum Expiry)
        const isExpiredPaid = user.tier === 'paid'
            && user.license?.expiresAt
            && new Date(user.license.expiresAt) < new Date();
        const status = isExpiredPaid ? 'expired' : (user.tier || 'free');
        const styles = {
            admin: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
            paid: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
            free: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
            expired: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
        };
        const icons = {
            admin: <ShieldCheck size={12} />, paid: <Crown size={12} />,
            free: <Zap size={12} />, expired: <Clock size={12} />
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${styles[status] || styles.free}`}>
                {icons[status] || icons.free}
                {status.toUpperCase()}
            </span>
        );
    };

    // ═══ MAIN RENDER ═══
    return (
        <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
                <div className="px-4 sm:px-6 py-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${userRole === ROLES.ADMIN ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-indigo-200 dark:shadow-none' : 'bg-gradient-to-br from-purple-500 to-fuchsia-600 shadow-purple-200 dark:shadow-none'}`}>
                                <ShieldCheck size={20} className="text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-slate-800 dark:text-white">Admin Dashboard</h1>
                                <p className="text-xs text-slate-500">Urus pengguna, lesen & konfigurasi</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Role Badge */}
                            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${ROLE_DISPLAY[userRole]?.bgClass || 'bg-slate-100 text-slate-600'}`}>
                                <span className="material-symbols-outlined text-[14px]">{ROLE_DISPLAY[userRole]?.icon || 'person'}</span>
                                {ROLE_DISPLAY[userRole]?.label || 'Client'}
                            </span>
                            {/* Session Badge */}
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                                <Lock size={12} />
                                Sesi Aktif
                            </span>
                            {/* Lock Button */}
                            <button
                                onClick={() => lockSession()}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors text-sm font-medium"
                            >
                                <Lock size={14} />
                                Kunci
                            </button>
                        </div>
                    </div>

                    {/* Tabs — pada telefon label disorok (ikon sahaja) dan baris
                        ini boleh di-scroll mendatar bila tab bertambah. */}
                    <div className="flex items-center gap-1 mt-4 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 overflow-x-auto sesaat-m-scroll">
                        {tabs.map(tab => (
                            /* Pautan SEBENAR, bukan butang — tanpa href Chrome langsung
                               tidak menawarkan "Open link in new tab", dan klik tengah
                               mati (peraturan sama seperti baris menu sidebar).
                               ⚠️ Muatan data tab TIDAK dicetuskan di sini lagi: ia hidup
                                  dalam useEffect yang mendengar activeAdminTab, supaya
                                  tab yang dibuka terus daripada bookmark pun dimuat. */
                            <a
                                key={tab.id}
                                href={`/admin/system/${tab.id}`}
                                onClick={(e) => { if (klikBiasa(e)) { e.preventDefault(); bukaTab(tab.id); } }}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeAdminTab === tab.id
                                    ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                            >
                                {tab.icon}
                                <span className="hidden sm:inline">{tab.label}</span>
                                {tab.badge > 0 && (
                                    <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-bold">
                                        {tab.badge}
                                    </span>
                                )}
                            </a>
                        ))}
                    </div>
                </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6">
                {/* ═══ TAB: DASHBOARD ═══ */}
                {activeAdminTab === 'dashboard' && (
                    <div className="space-y-6">
                        {/* ═══ RINGKASAN BISNES ═══ */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <h2 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        <PieChart size={18} className="text-indigo-500" /> Ringkasan Bisnes
                                    </h2>
                                    <p className="text-xs text-slate-500 mt-0.5">Jualan, renewal &amp; pakej — pilih bulan.</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={bizSelMonth || ''}
                                        onChange={e => setBizSelMonth(e.target.value)}
                                        className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200"
                                    >
                                        {(bizMonthly || []).slice().reverse().map(r => (
                                            <option key={r.month} value={r.month}>{bizMonthLabel(r.month)}</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => fetchBusinessSummary()}
                                        disabled={bizLoading}
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
                                    >
                                        <RefreshCw size={14} className={bizLoading ? 'animate-spin' : ''} /> Refresh
                                    </button>
                                    {bizCachedAt && (
                                        <span className="text-[11px] text-slate-400 whitespace-nowrap">
                                            Dikemaskini: {new Date(bizCachedAt).toLocaleString('ms-MY', { timeZone: 'Asia/Kuala_Lumpur', dateStyle: 'short', timeStyle: 'short' })}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {!bizMonthly ? (
                                <div className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-800 text-center text-sm text-slate-400">
                                    {bizLoading ? 'Memuatkan ringkasan…' : 'Tekan Refresh untuk muat.'}
                                </div>
                            ) : (() => {
                                const rm = (sen) => 'RM ' + Math.round((sen || 0) / 100).toLocaleString();
                                const selIdx = Math.max(0, bizMonthly.findIndex(r => r.month === bizSelMonth));
                                const sel = bizMonthly[selIdx] || bizMonthly[bizMonthly.length - 1];
                                const prev = selIdx > 0 ? bizMonthly[selIdx - 1] : null;
                                const newTot = sel.premiumNewCount; // Langgan Baru = Premium bulanan sahaja (Fighter diasingkan ke 2 kotak sendiri)
                                const renewDenom = sel.renewCount + sel.notRenewed;
                                const renewRate = renewDenom > 0 ? Math.round((sel.renewCount / renewDenom) * 100) : null;
                                const rrColor = renewRate == null ? 'text-slate-400' : renewRate >= 55 ? 'text-emerald-600' : renewRate >= 45 ? 'text-amber-600' : 'text-rose-600';
                                const rrDot = renewRate == null ? '' : renewRate >= 55 ? '🟢' : renewRate >= 45 ? '🟡' : '🔴';
                                return (
                                    <>
                                        {/* Baris 1: Duit Masuk + Pakej Aktif */}
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                            <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                                <div className="flex items-center gap-2 mb-1"><Coins size={16} className="text-emerald-500" /><span className="text-xs text-slate-500">Duit Masuk</span></div>
                                                <div className="flex items-baseline gap-3 flex-wrap">
                                                    <span className="text-2xl font-bold text-slate-800 dark:text-white">{rm(sel.revenueSen)}</span>
                                                    {prev && (<span className={`text-xs ${sel.revenueSen >= prev.revenueSen ? 'text-emerald-600' : 'text-rose-600'}`}>{sel.revenueSen >= prev.revenueSen ? '▲' : '▼'} vs {bizMonthShort(prev.month)} {rm(prev.revenueSen)}</span>)}
                                                </div>
                                                <div className="flex gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                                                    <span>Baru: <b className="text-slate-700 dark:text-slate-200">{rm(sel.baruSen)}</b></span>
                                                    <span>Renew: <b className="text-slate-700 dark:text-slate-200">{rm(sel.renewSen)}</b></span>
                                                    <span>Add-on: <b className="text-slate-700 dark:text-slate-200">{rm(sel.addonSen)}</b></span>
                                                    <span>Fighter: <b className="text-slate-700 dark:text-slate-200">{rm(sel.fighterRevenueSen)}</b></span>
                                                    {/* SMART + Renew Flow: tanpa dua baris ini pecahan tidak sama dengan jumlah besar.
                                                        Cache LAMA tiada flowRenewSen → || 0 sehingga Refresh pertama. */}
                                                    <span>SMART: <b className="text-slate-700 dark:text-slate-200">{rm(sel.smartRevenueSen || 0)}</b></span>
                                                    <span>Renew Flow: <b className="text-slate-700 dark:text-slate-200">{rm(sel.flowRenewSen || 0)}</b></span>
                                                    <span>Upgrade: <b className="text-slate-700 dark:text-slate-200">{rm(sel.upgradeSen || 0)}</b></span>
                                                </div>
                                            </div>
                                            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                                <div className="flex items-center gap-2 mb-2"><Crown size={16} className="text-indigo-500" /><span className="text-xs text-slate-500">Pakej Aktif (semasa)</span></div>
                                                <div className="space-y-1 text-sm">
                                                    <div className="flex justify-between"><span className="text-slate-500">⭐ Premium</span><b className="text-slate-800 dark:text-white">{bizPackages?.premium ?? 0}</b></div>
                                                    <div className="flex justify-between"><span className="text-slate-500">🥊 Fighter</span><b className="text-slate-800 dark:text-white">{bizPackages?.fighter ?? 0}</b></div>
                                                    <div className="flex justify-between"><span className="text-slate-500">⚡ SMART</span><b className="text-slate-800 dark:text-white">{bizPackages?.smart ?? 0}</b></div>
                                                    <div className="flex justify-between"><span className="text-slate-400">📜 Lama (pra-Billplz)</span><b className="text-slate-500">{bizPackages?.lama ?? 0}</b></div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Baris 2: Jualan bulan ini (9 kotak) */}
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Jualan bulan ini</p>
                                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                                <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                                    <div className="flex items-center gap-1.5 mb-1"><UserPlus size={15} className="text-blue-500" /><span className="text-xs text-slate-500">Langgan Baru (Premium)</span></div>
                                                    <p className="text-xl font-bold text-slate-800 dark:text-white">{newTot}</p>
                                                    <p className="text-[11px] text-slate-400 mt-0.5">Biasa {sel.baruPlainCount || 0} · +Flow {sel.baruFlowCount || 0}</p>
                                                </div>
                                                <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                                    <div className="flex items-center gap-1.5 mb-1"><RefreshCw size={15} className="text-teal-500" /><span className="text-xs text-slate-500">Renew (bulanan)</span></div>
                                                    <p className="text-xl font-bold text-slate-800 dark:text-white">{sel.renewCount}</p>
                                                    <p className="text-[11px] text-slate-400 mt-0.5">Biasa {sel.renewPlainCount || 0} · +FLOWNITY {sel.renewComboCount || 0}</p>
                                                    {prev && <p className="text-[11px] text-slate-400">{bizMonthShort(prev.month)}: {prev.renewCount}</p>}
                                                </div>
                                                <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                                    <div className="flex items-center gap-1.5 mb-1"><Package size={15} className="text-amber-500" /><span className="text-xs text-slate-500">Add-on / Lain</span></div>
                                                    <p className="text-xl font-bold text-slate-800 dark:text-white">{sel.addonCount}</p>
                                                    <p className="text-[11px] text-slate-400 mt-0.5">{rm(sel.addonSen)}</p>
                                                </div>
                                                <div className="bg-violet-50 dark:bg-violet-900/15 rounded-xl p-4 border border-violet-100 dark:border-violet-900/30">
                                                    <div className="flex items-center gap-1.5 mb-1"><Crown size={15} className="text-violet-500" /><span className="text-xs text-violet-700 dark:text-violet-400">Fighter — User Baru</span></div>
                                                    <p className="text-xl font-bold text-violet-900 dark:text-violet-300">{sel.fighterNewCount || 0}</p>
                                                    <p className="text-[11px] text-violet-600 dark:text-violet-400/70 mt-0.5">{rm(sel.fighterNewSen)} · terus ambil Fighter</p>
                                                </div>
                                                <div className="bg-indigo-50 dark:bg-indigo-900/15 rounded-xl p-4 border border-indigo-100 dark:border-indigo-900/30">
                                                    <div className="flex items-center gap-1.5 mb-1"><TrendingUp size={15} className="text-indigo-500" /><span className="text-xs text-indigo-700 dark:text-indigo-400">Fighter — User Lama</span></div>
                                                    <p className="text-xl font-bold text-indigo-900 dark:text-indigo-300">{sel.fighterRenewCount || 0}</p>
                                                    <p className="text-[11px] text-indigo-600 dark:text-indigo-400/70 mt-0.5">{rm(sel.fighterRenewSen)} · naik taraf dari Premium</p>
                                                </div>
                                                {/* SMART — dua kotak, corak sama Fighter. Cache LAMA tiada medan
                                                    ini, jadi setiap bacaan diguard || 0 supaya kotak memapar 0,
                                                    bukan "NaN", sebelum Refresh pertama selepas deploy. */}
                                                <div className="bg-cyan-50 dark:bg-cyan-900/15 rounded-xl p-4 border border-cyan-100 dark:border-cyan-900/30">
                                                    <div className="flex items-center gap-1.5 mb-1"><Zap size={15} className="text-cyan-500" /><span className="text-xs text-cyan-700 dark:text-cyan-400">SMART — User Baru</span></div>
                                                    <p className="text-xl font-bold text-cyan-900 dark:text-cyan-300">{sel.smartNewCount || 0}</p>
                                                    <p className="text-[11px] text-cyan-600 dark:text-cyan-400/70 mt-0.5">{rm(sel.smartNewSen || 0)} · guna GPT sendiri</p>
                                                </div>
                                                <div className="bg-sky-50 dark:bg-sky-900/15 rounded-xl p-4 border border-sky-100 dark:border-sky-900/30">
                                                    <div className="flex items-center gap-1.5 mb-1"><RefreshCw size={15} className="text-sky-500" /><span className="text-xs text-sky-700 dark:text-sky-400">SMART — Renew</span></div>
                                                    <p className="text-xl font-bold text-sky-900 dark:text-sky-300">{sel.smartRenewCount || 0}</p>
                                                    <p className="text-[11px] text-sky-600 dark:text-sky-400/70 mt-0.5">{rm(sel.smartRenewSen || 0)} · sambung RM99</p>
                                                </div>
                                                {/* Renew Flow — SEMUA bayaran Flow Ultra RM100 (keputusan user 2026-09-15).
                                                    Cache LAMA tiada medan ini → || 0 sehingga Refresh pertama selepas deploy. */}
                                                <div className="bg-emerald-50 dark:bg-emerald-900/15 rounded-xl p-4 border border-emerald-100 dark:border-emerald-900/30">
                                                    <div className="flex items-center gap-1.5 mb-1"><RefreshCw size={15} className="text-emerald-500" /><span className="text-xs text-emerald-700 dark:text-emerald-400">Renew Flow</span></div>
                                                    <p className="text-xl font-bold text-emerald-900 dark:text-emerald-300">{sel.flowRenewCount || 0}</p>
                                                    <p className="text-[11px] text-emerald-600 dark:text-emerald-400/70 mt-0.5">{rm(sel.flowRenewSen || 0)} · akaun Flow RM100</p>
                                                </div>
                                                {/* Upgrade — SMART naik Premium (keputusan user 2026-10-09).
                                                    Cache LAMA tiada medan ini → || 0 sehingga Refresh pertama selepas deploy. */}
                                                <div className="bg-rose-50 dark:bg-rose-900/15 rounded-xl p-4 border border-rose-100 dark:border-rose-900/30">
                                                    <div className="flex items-center gap-1.5 mb-1"><TrendingUp size={15} className="text-rose-500" /><span className="text-xs text-rose-700 dark:text-rose-400">Upgrade</span></div>
                                                    <p className="text-xl font-bold text-rose-900 dark:text-rose-300">{sel.upgradeCount || 0}</p>
                                                    <p className="text-[11px] text-rose-600 dark:text-rose-400/70 mt-0.5">{rm(sel.upgradeSen || 0)} · SMART naik Premium</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Baris 3: Pelanggan (3 kotak) */}
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Pelanggan</p>
                                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                                <div className="bg-rose-50 dark:bg-rose-900/15 rounded-xl p-4 border border-rose-100 dark:border-rose-900/30">
                                                    <div className="flex items-center gap-1.5 mb-1"><UserMinus size={15} className="text-rose-500" /><span className="text-xs text-rose-700 dark:text-rose-400">Tak Renew</span></div>
                                                    <div className="flex items-baseline gap-2">
                                                        <span className="text-xl font-bold text-rose-900 dark:text-rose-300">{sel.notRenewed}</span>
                                                        {renewRate != null && <span className={`text-[11px] font-medium ${rrColor}`}>renew {renewRate}% {rrDot}</span>}
                                                    </div>
                                                    <button
                                                        onClick={() => { setFilterTier('expired'); bukaTab('users'); }}
                                                        className="mt-1.5 text-[11px] px-2 py-0.5 rounded border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/30"
                                                    >
                                                        Senarai Expired →
                                                    </button>
                                                </div>
                                                <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                                    <div className="flex items-center gap-1.5 mb-1"><Users size={15} className="text-slate-400" /><span className="text-xs text-slate-500">Signup Baru (Free)</span></div>
                                                    <p className="text-xl font-bold text-slate-800 dark:text-white">{sel.signups.toLocaleString()}</p>
                                                    {prev && <p className="text-[11px] text-slate-400 mt-0.5">{bizMonthShort(prev.month)}: {prev.signups}</p>}
                                                </div>
                                                <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                                    <div className="flex items-center gap-1.5 mb-1"><ShieldCheck size={15} className="text-indigo-500" /><span className="text-xs text-slate-500">Paid Aktif ({sel.month === bizMonthly[bizMonthly.length - 1].month ? 'semasa' : 'hujung bulan'})</span></div>
                                                    <p className="text-xl font-bold text-slate-800 dark:text-white">{sel.paidActiveEOM.toLocaleString()}</p>
                                                    {prev && <p className="text-[11px] text-slate-400 mt-0.5">{bizMonthShort(prev.month)}: {prev.paidActiveEOM}</p>}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Graf trend bulanan */}
                                        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Trend Bulanan (dari Jan 2026)</span>
                                                <div className="flex gap-1.5 flex-wrap">
                                                    {BIZ_METRICS.map(m => (
                                                        <button key={m.key} onClick={() => setBizGraphMetric(m.key)}
                                                            className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${bizGraphMetric === m.key ? 'text-white border-transparent' : 'text-slate-500 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                                            style={bizGraphMetric === m.key ? { background: m.color } : {}}>
                                                            {m.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <BizTrendChart monthly={bizMonthly} metricKey={bizGraphMetric} selMonth={bizSelMonth} />
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        {/* ═══ KALENDAR RENEWAL ═══ */}
                        <div className="space-y-3">
                            <div>
                                <h2 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <Calendar size={18} className="text-indigo-500" /> Kalendar Renewal
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">Setiap hari: 🟡 lesen bakal tamat · 🟢 dah bayar sambung · 🔴 tamat tak sambung. Dikira sekali dengan Ringkasan Bisnes — tekan <strong>Refresh</strong> di atas untuk kemas kini.</p>
                                <p className="text-[11px] text-slate-400 mt-1">Nilai RM: 🟢 duit sebenar diterima · 🟡🔴 <strong>anggaran</strong> harga sambung (Premium RM99 · Fighter RM799).</p>
                            </div>
                            {bizRenewalDaily == null ? (
                                <div className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-800 text-center text-sm text-slate-400">
                                    {bizLoading ? 'Memuatkan…' : 'Tekan Refresh Ringkasan Bisnes di atas untuk kira kalendar.'}
                                </div>
                            ) : (
                                <RenewalCalendar daily={bizRenewalDaily} />
                            )}
                        </div>

                        {/* Header + refresh */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <Zap size={18} className="text-indigo-500" /> Generation Pipeline Berjaya
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">Bilangan panggilan generate yang berjaya selesai — pecah Paid vs Free Trial.</p>
                            </div>
                            <button
                                onClick={() => fetchPipelineStats()}
                                disabled={pipelineLoading}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
                            >
                                <RefreshCw size={14} className={pipelineLoading ? 'animate-spin' : ''} /> Refresh
                            </button>
                        </div>

                        {/* HARI INI */}
                        <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-800">
                            <div className="flex items-center gap-2 mb-4">
                                <Calendar size={15} className="text-slate-400" />
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Hari Ini</span>
                                <span className="text-xs text-slate-400">({klDayKey()})</span>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="bg-amber-50 dark:bg-amber-900/15 rounded-lg p-4">
                                    <div className="flex items-center gap-1.5 mb-1"><Crown size={14} className="text-amber-500" /><span className="text-xs font-medium text-amber-700 dark:text-amber-400">Paid</span></div>
                                    <p className="text-2xl font-bold text-slate-800 dark:text-white">{pipelineToday ? pipelineToday.paidCalls.toLocaleString() : '—'}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">call · ≈ {pipelineToday ? (pipelineToday.paidCalls * 3).toLocaleString() : 0} video</p>
                                    {pipelineToday && pipelineModuleRows(pipelineToday.byModule, 'paidCalls').length > 0 && (
                                        <div className="mt-2.5 pt-2.5 border-t border-amber-200/70 dark:border-amber-800/40 space-y-1">
                                            {pipelineModuleRows(pipelineToday.byModule, 'paidCalls').map((r) => (
                                                <div key={r.id} className="flex items-center justify-between text-[11px] gap-2">
                                                    <span className="text-slate-500 dark:text-slate-400 truncate">{pipelineModuleLabel(r.id)}</span>
                                                    <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums">{r.count.toLocaleString()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="bg-emerald-50 dark:bg-emerald-900/15 rounded-lg p-4">
                                    <div className="flex items-center gap-1.5 mb-1"><Gift size={14} className="text-emerald-500" /><span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">Free Trial</span></div>
                                    <p className="text-2xl font-bold text-slate-800 dark:text-white">{pipelineToday ? pipelineToday.trialCalls.toLocaleString() : '—'}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">call · ≈ {pipelineToday ? (pipelineToday.trialCalls * 3).toLocaleString() : 0} video</p>
                                    {pipelineToday && pipelineModuleRows(pipelineToday.byModule, 'trialCalls').length > 0 && (
                                        <div className="mt-2.5 pt-2.5 border-t border-emerald-200/70 dark:border-emerald-800/40 space-y-1">
                                            {pipelineModuleRows(pipelineToday.byModule, 'trialCalls').map((r) => (
                                                <div key={r.id} className="flex items-center justify-between text-[11px] gap-2">
                                                    <span className="text-slate-500 dark:text-slate-400 truncate">{pipelineModuleLabel(r.id)}</span>
                                                    <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums">{r.count.toLocaleString()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                                    <div className="flex items-center gap-1.5 mb-1"><TrendingUp size={14} className="text-slate-400" /><span className="text-xs font-medium text-slate-500 dark:text-slate-400">Jumlah</span></div>
                                    <p className="text-2xl font-bold text-slate-800 dark:text-white">{pipelineToday ? (pipelineToday.paidCalls + pipelineToday.trialCalls).toLocaleString() : '—'}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">call · ≈ {pipelineToday ? ((pipelineToday.paidCalls + pipelineToday.trialCalls) * 3).toLocaleString() : 0} video</p>
                                    {pipelineToday && pipelineModuleRows(pipelineToday.byModule, 'sum').length > 0 && (
                                        <div className="mt-2.5 pt-2.5 border-t border-slate-200 dark:border-slate-700 space-y-1">
                                            {pipelineModuleRows(pipelineToday.byModule, 'sum').map((r) => (
                                                <div key={r.id} className="flex items-center justify-between text-[11px] gap-2">
                                                    <span className="text-slate-500 dark:text-slate-400 truncate">{pipelineModuleLabel(r.id)}</span>
                                                    <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums">{r.count.toLocaleString()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* JULAT TARIKH */}
                        <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-800">
                            <div className="flex flex-wrap items-center gap-3 mb-4">
                                <div className="flex items-center gap-2">
                                    <Calendar size={15} className="text-slate-400" />
                                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Julat Tarikh</span>
                                </div>
                                <div className="flex items-center gap-2 ml-auto">
                                    <input
                                        type="date"
                                        value={pipelineStart}
                                        max={pipelineEnd}
                                        onChange={e => setPipelineStart(e.target.value)}
                                        className="px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200"
                                    />
                                    <span className="text-slate-400 text-sm">→</span>
                                    <input
                                        type="date"
                                        value={pipelineEnd}
                                        min={pipelineStart}
                                        max={klDayKey()}
                                        onChange={e => setPipelineEnd(e.target.value)}
                                        className="px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200"
                                    />
                                    <button
                                        onClick={() => fetchPipelineStats(pipelineStart, pipelineEnd)}
                                        disabled={pipelineLoading}
                                        className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        Tunjuk
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3 mb-4">
                                {[
                                    { key: 'paid', label: 'Paid', field: 'paidCalls', value: pipelineRange ? pipelineRange.paid : null, cls: 'text-amber-600 dark:text-amber-400' },
                                    { key: 'trial', label: 'Free Trial', field: 'trialCalls', value: pipelineRange ? pipelineRange.trial : null, cls: 'text-emerald-600 dark:text-emerald-400' },
                                    { key: 'total', label: 'Jumlah', field: 'sum', value: pipelineRange ? pipelineRange.total : null, cls: 'text-slate-800 dark:text-white' },
                                ].map(col => {
                                    const mods = pipelineRange ? pipelineModuleRows(pipelineRange.byModule, col.field) : [];
                                    return (
                                        <div key={col.key}>
                                            <span className="text-xs text-slate-500">{col.label}</span>
                                            <p className={`text-xl font-bold ${col.cls}`}>{col.value == null ? '—' : col.value.toLocaleString()}</p>
                                            {mods.length > 0 && (
                                                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1">
                                                    {mods.map(r => (
                                                        <div key={r.id} className="flex items-center justify-between text-[11px] gap-2">
                                                            <span className="text-slate-500 dark:text-slate-400 truncate">{pipelineModuleLabel(r.id)}</span>
                                                            <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums">{r.count.toLocaleString()}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Liputan pecahan module — rekod byModule baru bermula 2026-07-25, jadi julat yang
                                merangkumi hari lebih awal ada pecahan tak lengkap. Beritahu, jangan senyap. */}
                            {pipelineRange && pipelineRange.rows.length > 0 && (pipelineRange.moduleDays || 0) < pipelineRange.rows.length && (
                                <p className="text-[11px] text-slate-400 -mt-2 mb-4">
                                    Pecahan module hanya meliputi {pipelineRange.moduleDays || 0} daripada {pipelineRange.rows.length} hari dalam julat ini — rekod pecahan bermula 25 Julai 2026.
                                </p>
                            )}

                            {pipelineRange && pipelineRange.rows.length > 0 ? (
                                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                                    {(() => {
                                        const max = Math.max(...pipelineRange.rows.map(r => r.total), 1);
                                        return pipelineRange.rows.map(r => (
                                            <div key={r.date} className="flex items-center gap-2">
                                                <span className="text-[11px] text-slate-400 w-12 shrink-0 font-mono">{r.date.slice(5)}</span>
                                                <div className="flex-1 h-5 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden flex">
                                                    <div className="h-full bg-amber-400" style={{ width: `${(r.paid / max) * 100}%` }} title={`Paid: ${r.paid}`}></div>
                                                    <div className="h-full bg-emerald-400" style={{ width: `${(r.trial / max) * 100}%` }} title={`Trial: ${r.trial}`}></div>
                                                </div>
                                                <span className="text-xs text-slate-600 dark:text-slate-300 w-12 text-right font-medium">{r.total.toLocaleString()}</span>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 text-center py-6">
                                    {pipelineLoading ? 'Memuatkan…' : 'Tiada data untuk julat tarikh ini.'}
                                </p>
                            )}

                            <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-500">
                                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block"></span>Paid</span>
                                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-400 inline-block"></span>Free Trial</span>
                            </div>
                        </div>

                        {/* Caveat */}
                        <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-900/30 rounded-lg px-4 py-3">
                            <AlertTriangle size={15} className="text-amber-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                                Rekod hanya bermula dari hari sistem log dipasang — tarikh sebelum itu tiada data dan tak boleh dipulihkan. Setiap call dikira bila generation <strong>berjaya selesai</strong> (1 call = 3 set = ≈ 3 video).
                            </p>
                        </div>
                    </div>
                )}

                {/* ═══ TAB: USERS ═══ */}
                {activeAdminTab === 'risk' && (
                    <div className="space-y-4">
                        {/* ═══ HEADER + REFRESH ═══ */}
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                                <h2 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <AlertTriangle size={18} className="text-rose-500" /> Risiko Pelanggan
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Siapa nak lari, siapa boleh upsell. Senyap {RISK_SILENT_DAYS} hari = bahaya.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => fetchRiskData()}
                                    disabled={riskLoading}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
                                >
                                    <RefreshCw size={14} className={riskLoading ? 'animate-spin' : ''} /> Refresh
                                </button>
                                {riskCachedAt && (
                                    <span className="text-[11px] text-slate-400 whitespace-nowrap">
                                        Dikemaskini: {new Date(riskCachedAt).toLocaleString('ms-MY', { timeZone: 'Asia/Kuala_Lumpur', dateStyle: 'short', timeStyle: 'short' })}
                                    </span>
                                )}
                            </div>
                        </div>

                        {!riskRows ? (
                            <div className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-800 text-center text-sm text-slate-400">
                                {riskLoading ? 'Mengira senarai risiko…' : 'Tekan Refresh untuk muat.'}
                            </div>
                        ) : (
                            <>
                                {/* ═══ KOTAK RINGKASAN — klik untuk tapis ═══ */}
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                    {[
                                        { k: 'exp7', label: 'Tamat 7 hari', n: (riskRows || []).filter(r => r.daysLeft >= 0 && r.daysLeft <= 7).length, cls: 'text-slate-800 dark:text-white' },
                                        { k: 'red', label: 'Bahaya', n: riskCounts?.red || 0, cls: 'text-rose-600 dark:text-rose-400' },
                                        { k: 'never', label: 'Tak pernah guna', n: riskCounts?.neverUsed || 0, cls: 'text-amber-600 dark:text-amber-400' },
                                        { k: 'green', label: 'Sihat — boleh upsell', n: riskCounts?.green || 0, cls: 'text-emerald-600 dark:text-emerald-400' },
                                    ].map(c => (
                                        <button
                                            key={c.k}
                                            onClick={() => setRiskFilter(c.k)}
                                            className={`text-left bg-white dark:bg-slate-900 rounded-xl p-4 border transition ${riskFilter === c.k ? 'border-indigo-400 dark:border-indigo-500' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}
                                        >
                                            <p className="text-[11px] text-slate-500 mb-1">{c.label}</p>
                                            <p className={`text-2xl font-bold ${c.cls}`}>{c.n}</p>
                                        </button>
                                    ))}
                                </div>

                                {/* ═══ PENAPIS + CARIAN + EKSPORT ═══ */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    {[
                                        { k: 'red', label: 'Bahaya' },
                                        { k: 'amber', label: 'Perlu perhatian' },
                                        { k: 'green', label: 'Sihat' },
                                        { k: 'never', label: 'Tak pernah guna' },
                                        { k: 'exp7', label: 'Tamat 7 hari' },
                                        { k: 'lapsed', label: 'Baru tamat' },
                                        { k: 'all', label: 'Semua' },
                                    ].map(f => (
                                        <button
                                            key={f.k}
                                            onClick={() => setRiskFilter(f.k)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${riskFilter === f.k
                                                ? 'bg-indigo-600 border-indigo-600 text-white'
                                                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-300'}`}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                    <div className="relative ml-auto">
                                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            value={riskSearch}
                                            onChange={e => setRiskSearch(e.target.value)}
                                            placeholder="Cari email / no. WhatsApp"
                                            className="pl-8 pr-3 py-1.5 w-56 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 flex-wrap">
                                    <button onClick={copyRiskNumbers} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-medium">
                                        <Copy size={13} /> Salin nombor WhatsApp
                                    </button>
                                    <button onClick={exportRiskCsv} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-medium">
                                        <FileText size={13} /> Eksport CSV
                                    </button>
                                    <button onClick={() => setWaPanelOpen(o => !o)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${waPanelOpen ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-slate-100 dark:bg-slate-800 border-transparent text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}>
                                        <MessageCircle size={13} /> Kempen WhatsApp
                                    </button>
                                    <span className="text-xs text-slate-400">{riskVisible.length} pelanggan dipapar</span>
                                    {riskCopied && <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">{riskCopied}</span>}
                                </div>

                                {/* ═══ KEMPEN WHATSAPP (Cloud API, mod Coexistence) ═══ */}
                                {waPanelOpen && (() => {
                                    const pool = riskVisible.filter(r => r.wa);
                                    const batch = Math.min(WA_BATCH, pool.length);
                                    return (
                                        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-3">
                                            <div className="flex items-start gap-2">
                                                <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />
                                                <p className="text-[11.5px] text-slate-600 dark:text-slate-300 leading-relaxed">
                                                    Mesej dihantar melalui nombor WhatsApp Business sedia ada anda. Kalau ramai penerima menyekat
                                                    atau melaporkan mesej ini, Meta akan memotong had penghantaran nombor tersebut — dan itu
                                                    menjejaskan saluran sokongan untuk <strong>semua</strong> pelanggan aktif anda.
                                                    Hantar hanya kepada senarai yang benar-benar bermakna, dan mulakan dengan kumpulan kecil.
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                                <div>
                                                    <label className="block text-[11px] text-slate-500 mb-1">Nama kempen</label>
                                                    <input value={waCampaignName} onChange={e => setWaCampaignName(e.target.value)}
                                                        placeholder={`Risiko: ${riskFilter}`}
                                                        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200" />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] text-slate-500 mb-1">Nama template (dari Meta)</label>
                                                    <input value={waTemplate} onChange={e => setWaTemplate(e.target.value)}
                                                        placeholder="peringatan_renewal"
                                                        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200 font-mono" />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] text-slate-500 mb-1">Bahasa template</label>
                                                    <select value={waLang} onChange={e => setWaLang(e.target.value)}
                                                        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200">
                                                        <option value="ms">ms — Melayu</option>
                                                        <option value="en">en — English</option>
                                                        <option value="en_US">en_US</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <p className="text-[11px] text-slate-500">
                                                Parameter dihantar automatik: <span className="font-mono">{'{{1}}'}</span> = nama pelanggan ·{' '}
                                                <span className="font-mono">{'{{2}}'}</span> = hari lagi sebelum lesen tamat.
                                                Kalau template anda tiada parameter, Meta akan abaikannya.
                                            </p>

                                            <div className="flex items-center gap-2 flex-wrap pt-1">
                                                <button onClick={() => sendWaCampaign(true)} disabled={waBusy || !batch}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50">
                                                    Semak dulu (tidak hantar)
                                                </button>
                                                <button onClick={() => sendWaCampaign(false)} disabled={waBusy || !batch}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
                                                    {waBusy ? 'Menghantar…' : `Hantar ${batch} mesej`}
                                                </button>
                                                <span className="text-[11px] text-slate-400">
                                                    {pool.length} ada nombor WhatsApp
                                                    {pool.length > WA_BATCH && ` · ${pool.length - WA_BATCH} lagi selepas batch ini`}
                                                    {riskVisible.length - pool.length > 0 && ` · ${riskVisible.length - pool.length} tiada nombor`}
                                                </span>
                                            </div>

                                            {waResult && (
                                                <div className={`rounded-lg p-3 text-xs ${waResult.error || waResult.aborted ? 'bg-rose-50 dark:bg-rose-900/15 text-rose-700 dark:text-rose-300' : 'bg-emerald-50 dark:bg-emerald-900/15 text-emerald-700 dark:text-emerald-300'}`}>
                                                    {waResult.error ? (
                                                        <p><strong>Gagal:</strong> {waResult.error}</p>
                                                    ) : waResult.dryRun ? (
                                                        <p><strong>Semakan sahaja — tiada mesej dihantar.</strong> {waResult.akanDihantar} akan dihantar
                                                            {waResult.dilangkau?.length ? `, ${waResult.dilangkau.length} dilangkau` : ''}.
                                                            Setakat ini {waResult.sentLastHour}/60 mesej dihantar dalam sejam lepas.</p>
                                                    ) : (
                                                        <div className="space-y-1">
                                                            <p><strong>{waResult.sent} dihantar</strong>{waResult.failed ? ` · ${waResult.failed} gagal` : ''}
                                                                {waResult.dilangkau?.length ? ` · ${waResult.dilangkau.length} dilangkau` : ''}
                                                                {waResult.baki ? ` · ${waResult.baki} lagi belum dihantar — klik sekali lagi` : ''}</p>
                                                            {waResult.nota && <p className="font-bold">{waResult.nota}</p>}
                                                            {waResult.errors?.length > 0 && (
                                                                <ul className="list-disc pl-4 opacity-80">
                                                                    {waResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e.to}: {e.detail}</li>)}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {riskTruncated > 0 && (
                                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                        ⚠️ {riskTruncated} baris paling tidak mendesak tidak disimpan (had saiz cache). Senarai ini menunjukkan yang paling penting dahulu.
                                    </p>
                                )}

                                {/* ═══ JADUAL ═══ */}
                                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50 dark:bg-slate-800/50 text-[11px] uppercase tracking-wide text-slate-500">
                                                <tr>
                                                    <th className="text-left font-semibold px-4 py-2.5">Pelanggan</th>
                                                    <th className="text-left font-semibold px-4 py-2.5">Tamat</th>
                                                    <th className="text-left font-semibold px-4 py-2.5">Guna</th>
                                                    <th className="text-left font-semibold px-4 py-2.5">Kali akhir</th>
                                                    <th className="text-left font-semibold px-4 py-2.5">Tindakan</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {riskVisible.length === 0 && (
                                                    <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">Tiada pelanggan dalam penapis ini.</td></tr>
                                                )}
                                                {riskVisible.map(r => {
                                                    const dot = r.risk === 'red' ? 'bg-rose-500' : r.risk === 'amber' ? 'bg-amber-400' : 'bg-emerald-500';
                                                    const silentDays = r.lastRunMs == null ? null : Math.floor((Date.now() - r.lastRunMs) / 86400000);
                                                    const wa = buildRenewalReminderDeeplink({
                                                        userWhatsapp: r.wa,
                                                        email: r.email,
                                                        expiresAt: new Date(r.expMs),
                                                        daysLeft: Math.max(0, r.daysLeft)
                                                    });
                                                    return (
                                                        <tr key={r.uid} className="border-t border-slate-100 dark:border-slate-800">
                                                            <td className="px-4 py-2.5">
                                                                <div className="flex items-start gap-2">
                                                                    <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${dot}`}></span>
                                                                    <div className="min-w-0">
                                                                        <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{r.email}</p>
                                                                        <p className="text-[11px] text-slate-400">{r.plan} · {r.reason}</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                                {r.lapsed
                                                                    ? <span className="text-xs text-rose-600 dark:text-rose-400">Tamat {Math.abs(r.daysLeft)} hari lalu</span>
                                                                    : <span className={`text-xs ${r.daysLeft <= 7 ? 'text-rose-600 dark:text-rose-400 font-medium' : 'text-slate-600 dark:text-slate-300'}`}>{r.daysLeft} hari lagi</span>}
                                                            </td>
                                                            <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">{(r.runs || 0).toLocaleString()}</td>
                                                            <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">
                                                                {silentDays == null ? 'Tak pernah' : silentDays === 0 ? 'Hari ini' : `${silentDays} hari lalu`}
                                                            </td>
                                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                                {wa ? (
                                                                    <a href={wa} target="_blank" rel="noreferrer"
                                                                        className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md font-medium text-white ${r.risk === 'green' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                                                                        <MessageCircle size={12} /> {r.risk === 'green' ? 'Upsell' : 'Hubungi'}
                                                                    </a>
                                                                ) : (
                                                                    <span className="text-[11px] text-slate-400">Tiada no. WhatsApp</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <p className="text-[11px] text-slate-400">
                                    🔴 Bahaya = tak pernah generate, atau senyap lebih {RISK_SILENT_DAYS} hari ·
                                    🟡 Perlu perhatian = senyap {RISK_WARN_DAYS}-{RISK_SILENT_DAYS} hari, atau guna kurang {RISK_LOW_RUNS} kali ·
                                    🟢 Sihat = aktif dalam {RISK_WARN_DAYS} hari
                                </p>
                            </>
                        )}
                    </div>
                )}

                {activeAdminTab === 'users' && (
                    <>
                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { icon: <Users size={16} className="text-indigo-500" />, label: 'Jumlah User', value: stats.total, color: '' },
                                { icon: <Zap size={16} className="text-slate-400" />, label: 'Free', value: stats.free, color: '' },
                                { icon: <Crown size={16} className="text-amber-500" />, label: 'Paid', value: stats.paid, color: 'text-amber-600' },
                                { icon: <ShieldCheck size={16} className="text-indigo-500" />, label: 'Admin / Mod', value: stats.admin, color: 'text-indigo-600' },
                            ].map((s, i) => (
                                <div key={i} className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                    <div className="flex items-center gap-2 mb-2">{s.icon}<span className="text-xs text-slate-500">{s.label}</span></div>
                                    <p className={`text-2xl font-bold ${s.color || 'text-slate-800 dark:text-white'}`}>{s.value}</p>
                                </div>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="flex flex-wrap items-center gap-3">
                            {/* min-w: kotak carian tak boleh dihimpit oleh kawalan lain pada baris
                                sama (flex-wrap). Tanpa ini ia mengecut ~130px → email panjang
                                terpotong, admin nampak hujung teks sahaja. */}
                            {/* Carian = FORM tekan-Enter/Cari (corak sama Rekod Jualan) — term
                                komited barulah masuk searchTerm dan mencetus query server. */}
                            <form
                                onSubmit={(e) => { e.preventDefault(); setSearchTerm(usersSearchInput.trim()); }}
                                className="flex items-center gap-2 w-full lg:w-auto lg:flex-1 lg:min-w-[420px]"
                            >
                                <div className="relative flex-1">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                    <input
                                        type="text"
                                        placeholder="Cari email, nama, WhatsApp, UID, atau license key…"
                                        value={usersSearchInput}
                                        onChange={e => setUsersSearchInput(e.target.value)}
                                        className="w-full pl-10 pr-10 py-3 lg:py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-base lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                                    />
                                    {(usersSearchInput || searchTerm) && (
                                        <button
                                            type="button"
                                            onClick={() => { setUsersSearchInput(''); setSearchTerm(''); }}
                                            aria-label="Kosongkan carian"
                                            title="Kosongkan carian"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-3 lg:py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Cari
                                </button>
                            </form>
                            {/* Tapisan tier + campaign DIPADAMKAN semasa mod carian (corak sama
                                Rekod Jualan) — hasil carian papar SEMUA padanan tanpa tapisan. */}
                            <div className={`flex items-center gap-1 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-1 ${searchTerm ? 'opacity-40 pointer-events-none' : ''}`}>
                                {['all', 'free', 'paid', 'admin', 'expired'].map(tier => (
                                    <button key={tier} onClick={() => setFilterTier(tier)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterTier === tier ? 'bg-indigo-500 text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                                        {tier === 'all' ? 'Semua' : tier.charAt(0).toUpperCase() + tier.slice(1)}
                                    </button>
                                ))}
                            </div>
                            <div className={`flex items-center gap-1.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 px-2 py-1 ${searchTerm ? 'opacity-40 pointer-events-none' : ''}`} title="Filter berdasarkan campaign">
                                <Tag size={14} className="text-slate-400" />
                                <select
                                    value={filterCampaign}
                                    onChange={e => setFilterCampaign(e.target.value)}
                                    className="bg-transparent text-xs font-medium text-slate-700 dark:text-slate-200 focus:outline-none cursor-pointer pr-1"
                                >
                                    <option value="all">Semua Campaign ({totalCounts.total})</option>
                                    <option value="none">— Tiada Tag — ({campaignCounts.none})</option>
                                    {campaigns.map(c => (
                                        <option key={c.id} value={c.id}>{c.name} ({campaignCounts.byId[c.id] || 0})</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="hidden lg:inline text-[10px] text-slate-400">
                                    Last updated: {lastRefreshedUsers ? formatRelativeTime(lastRefreshedUsers) : '—'}
                                </span>
                                <button
                                    onClick={() => {
                                        if (searchTerm) runUsersSmartSearch(searchTerm);
                                        else if (isHeavyFilter) fetchAllUsers();
                                        else fetchUsersPage(0, { campaignFilter: isCampaignFiltered ? filterCampaign : null });
                                        fetchTotalCounts();
                                    }}
                                    disabled={loading}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Refresh senarai pengguna"
                                >
                                    <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                                    Refresh
                                </button>
                            </div>
                            <button
                                onClick={async () => {
                                    if (!window.confirm('Backfill: bagi semua existing paid user akses semua modules (wildcard *). Teruskan?')) return;
                                    try {
                                        const fn = httpsCallable(functions, 'backfillAllowedModules');
                                        const result = await fn({});
                                        alert(`Selesai!\nUpdated: ${result.data.updated}\nDah wildcard: ${result.data.alreadyWildcard}\nFree users: ${result.data.empty}`);
                                        fetchUsers();
                                    } catch (err) {
                                        alert('Error: ' + err.message);
                                    }
                                }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 transition-colors text-xs font-bold"
                                title="Beri semua paid user akses wildcard ke semua modules"
                            >
                                Backfill Modules
                            </button>
                            <button
                                onClick={async () => {
                                    if (!window.confirm('Backfill: isi email yang hilang dari Firebase Auth. Teruskan?')) return;
                                    try {
                                        const fn = httpsCallable(functions, 'backfillMissingEmails');
                                        const result = await fn({});
                                        alert(`Selesai!\nUpdated: ${result.data.updated}\nDah ada email: ${result.data.alreadyHas}\nAuth takde email: ${result.data.authMissing}\nErrors: ${result.data.errors}`);
                                        fetchUsers();
                                    } catch (err) {
                                        alert('Error: ' + err.message);
                                    }
                                }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 transition-colors text-xs font-bold"
                                title="Isi email yang hilang dari Firebase Auth"
                            >
                                Backfill Emails
                            </button>
                            <button
                                onClick={async () => {
                                    if (backfillCountsLoading) return;
                                    if (!window.confirm('Rebuild counter campaign dari scratch?\n\nGuna kalau badge dropdown nampak salah (cth selepas signup baru / user dipadam).')) return;
                                    setBackfillCountsLoading(true);
                                    try {
                                        const result = await backfillCampaignCountsFn({});
                                        const d = result.data || {};
                                        alert(`Selesai!\nScan: ${d.totalUsers} user\nBucket: ${Object.keys(d.buckets || {}).length}\nStale reset: ${d.staleReset}`);
                                    } catch (err) {
                                        alert('Error: ' + (err?.message || 'unknown'));
                                    } finally {
                                        setBackfillCountsLoading(false);
                                    }
                                }}
                                disabled={backfillCountsLoading}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 hover:bg-sky-200 transition-colors text-xs font-bold disabled:opacity-50"
                                title="Sync semula counter campaign (badge dropdown) — guna bila drift"
                            >
                                {backfillCountsLoading ? <RefreshCw size={14} className="animate-spin" /> : null}
                                {backfillCountsLoading ? 'Syncing...' : 'Sync Counts'}
                            </button>
                            <button
                                onClick={() => setShowCampaignManager(true)}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 transition-colors text-xs font-bold"
                                title="Urus senarai campaign untuk tagging pengguna"
                            >
                                <Tag size={14} /> Campaigns
                            </button>
                            <button
                                onClick={async () => {
                                    if (backfillRunning) return;
                                    setBackfillRunning(true);
                                    try {
                                        const fn = httpsCallable(functions, 'adminBackfillReferrals');
                                        const result = await fn({});
                                        setBackfillPreview(result.data);
                                    } catch (err) {
                                        alert('Error scan: ' + (err?.message || 'unknown'));
                                    } finally {
                                        setBackfillRunning(false);
                                    }
                                }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 transition-colors text-xs font-bold disabled:opacity-50"
                                disabled={backfillRunning}
                                title="Scan paid user tanpa referredBy (era affiliate) — investigation tool, klik badge untuk attribute"
                            >
                                {backfillRunning ? <RefreshCw size={14} className="animate-spin" /> : null}
                                {backfillRunning ? 'Scanning...' : 'Scan Missing Referrals'}
                            </button>
                        </div>

                        {searchTerm && (
                            <div className="-mt-3 text-xs text-indigo-600 dark:text-indigo-400">
                                Menunjukkan semua pengguna sepadan <b>{searchTerm}</b> — tapisan tier &amp; campaign diabaikan. Carian ikut <b>permulaan</b> email/nama, atau padanan penuh WhatsApp/UID/license key.
                            </div>
                        )}

                        {error && <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-4"><p className="text-rose-600 text-sm">{error}</p></div>}

                        {/* Bulk Action Bar (muncul apabila ada checkbox ditandakan) */}
                        {selectedUsers.size > 0 && (
                            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-xl p-3 mb-4 flex items-center justify-between text-sm transition-all duration-300 shadow-sm animate-fade-in-up">
                                <div className="text-indigo-800 dark:text-indigo-200 font-medium flex items-center">
                                    <span className="bg-indigo-100 dark:bg-indigo-800 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded-md mr-2 text-xs">{selectedUsers.size}</span>
                                    pengguna dipilih (Bulk Selection)
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => {
                                            setBulkCampaignId('');
                                            setShowBulkAssignCampaign(true);
                                        }}
                                        className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white px-4 py-1.5 rounded-lg font-bold transition-all shadow-md active:scale-95"
                                    >
                                        <Tag size={14} /> Bulk Assign Campaign
                                    </button>
                                    <button
                                        onClick={() => {
                                            setBulkAllowedModules(['ai8saat']);
                                            setShowBulkEditModules(true);
                                        }}
                                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white px-4 py-1.5 rounded-lg font-bold transition-all shadow-md active:scale-95"
                                    >
                                        <Edit3 size={14} /> Bulk Edit Modules
                                    </button>
                                    <button
                                        onClick={handleBulkResetProfiles}
                                        className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600 text-white px-4 py-1.5 rounded-lg font-bold transition-all shadow-md active:scale-95"
                                    >
                                        <Monitor size={14} /> Reset Chrome Profiles
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Users Table */}
                        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                                            <th className="px-4 py-3 w-10 text-center">
                                                <input 
                                                    type="checkbox" 
                                                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                                    checked={pagedUsers.length > 0 && pagedUsers.every(u => selectedUsers.has(u.uid))}
                                                    onChange={handleToggleAllUsers}
                                                />
                                            </th>
                                            <th className="text-left px-4 py-3 font-medium text-slate-500">Email</th>
                                            <th
                                                className="text-center px-4 py-3 font-medium text-slate-500 select-none transition-colors cursor-pointer hover:text-indigo-500"
                                                title="Klik untuk sort (merentas semua pengguna)"
                                                onClick={() => toggleSort('tier')}
                                            >
                                                <div className="flex items-center justify-center gap-1">
                                                    Tier <span className={`material-symbols-outlined text-[14px] ${userSort.field === 'tier' ? 'text-indigo-500' : 'text-slate-300'}`}>{sortIcon('tier')}</span>
                                                </div>
                                            </th>
                                            <th className="text-center px-4 py-3 font-medium text-slate-500">License Key</th>
                                            <th
                                                className="text-center px-4 py-3 font-medium text-slate-500 select-none transition-colors cursor-pointer hover:text-indigo-500"
                                                title="Klik untuk sort (merentas semua pengguna)"
                                                onClick={() => toggleSort('expires')}
                                            >
                                                <div className="flex items-center justify-center gap-1">
                                                    Expiry <span className={`material-symbols-outlined text-[14px] ${userSort.field === 'expires' ? 'text-indigo-500' : 'text-slate-300'}`}>{sortIcon('expires')}</span>
                                                </div>
                                            </th>
                                            <th
                                                className="text-center px-4 py-3 font-medium text-slate-500 select-none transition-colors cursor-pointer hover:text-indigo-500"
                                                title="Klik untuk sort (merentas semua pengguna)"
                                                onClick={() => toggleSort('profiles')}
                                            >
                                                <div className="flex items-center justify-center gap-1">
                                                    Profiles <span className={`material-symbols-outlined text-[14px] ${userSort.field === 'profiles' ? 'text-indigo-500' : 'text-slate-300'}`}>{sortIcon('profiles')}</span>
                                                </div>
                                            </th>
                                            <th className="text-center px-4 py-3 font-medium text-slate-500">Modules</th>
                                            <th className="text-center px-4 py-3 font-medium text-slate-500">Campaign</th>
                                            <th className="text-center px-4 py-3 font-medium text-slate-500">Ejen Aff</th>
                                            <th className="text-center px-4 py-3 font-medium text-slate-500">Joined</th>
                                            <th className="text-center px-4 py-3 font-medium text-slate-500">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan={11} className="text-center py-12"><div className="flex items-center justify-center gap-2 text-slate-400"><RefreshCw size={16} className="animate-spin" /><span>Memuatkan...</span></div></td></tr>
                                        ) : filteredUsers.length === 0 ? (
                                            <tr><td colSpan={11} className="text-center py-12 text-slate-400">{usersScope === 'search' ? `Tiada pengguna sepadan "${searchTerm}". Carian ikut permulaan email/nama — cuba potong hujung term.` : 'Tiada pengguna dijumpai.'}</td></tr>
                                        ) : pagedUsers.map(user => {
                                            const isSelected = selectedUsers.has(user.uid);
                                            return (
                                            <tr
                                                id={`row-${user.uid}`}
                                                key={user.uid}
                                                onMouseDown={(e) => handleMouseDownRow(e, user.uid)}
                                                onMouseEnter={() => handleMouseEnterRow(user.uid)}
                                                className={`border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors ${isSelected ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}
                                            >
                                                <td data-drag-select="row" className="px-4 py-3 text-center cursor-pointer select-none">
                                                    <input
                                                        id={`chk-${user.uid}`}
                                                        type="checkbox"
                                                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer pointer-events-auto"
                                                        checked={isSelected}
                                                        readOnly // Let the row handle toggle
                                                    />
                                                </td>
                                                <td className="px-4 py-3" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-start gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-slate-800 dark:text-white text-xs truncate">{user.email}</p>
                                                            {user.whatsappNumber && (
                                                                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono">📱 {user.whatsappNumber}</p>
                                                            )}
                                                            <p className="text-[10px] text-slate-400 font-mono">{user.uid?.substring(0, 12)}...</p>
                                                        </div>
                                                        {isFullAdmin && (
                                                            <button
                                                                onClick={() => setEditingWhatsappUser({ uid: user.uid, email: user.email, whatsappNumber: user.whatsappNumber || '' })}
                                                                className="p-1 rounded text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors shrink-0"
                                                                title="Edit WhatsApp"
                                                            >
                                                                <Edit3 size={12} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center">{tierBadge(user)}</td>
                                                <td className="px-4 py-3 text-center text-xs font-mono text-slate-500">{user.license?.licenseKey || '-'}</td>
                                                <td className="px-4 py-3 text-center text-xs">
                                                    {user.license?.expiresAt ? (
                                                        <span className={new Date(user.license.expiresAt) < new Date() ? 'text-rose-500 font-semibold' : 'text-emerald-600 font-semibold'}>
                                                            {new Date(user.license.expiresAt).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-center text-xs text-slate-500">{user.license ? `${user.license.activeProfiles.length || 0} / ${user.license.maxProfiles}` : '-'}</td>
                                                <td className="px-4 py-3 text-center">
                                                    {user.license?.allowedModules?.length > 0 ? (
                                                        (() => {
                                                            const hasWildcard = user.license.allowedModules.includes('*');
                                                            const explicitModules = user.license.allowedModules.filter(m => m !== '*');
                                                            const wildcardModules = hasWildcard ? modulesList.filter(m => !m.isPremiumAddon).map(m => m.id) : [];
                                                            const allModuleIds = [...new Set([...wildcardModules, ...explicitModules])];
                                                            return (
                                                                <div className="flex flex-wrap justify-center gap-1 items-center">
                                                                    {hasWildcard && (
                                                                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400" title="Wildcard: akses semua modules (kecuali premium addons)">SEMUA</span>
                                                                    )}
                                                                    {allModuleIds.map(mid => {
                                                                        const mod = modulesList.find(m => m.id === mid);
                                                                        return <span key={mid} className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">{mod?.name || mid}</span>;
                                                                    })}
                                                                </div>
                                                            );
                                                        })()
                                                    ) : <span className="text-[10px] text-slate-300">—</span>}
                                                </td>
                                                <td className="px-4 py-3 text-center" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                                                    {isFullAdmin ? (
                                                        <select
                                                            value={user.campaign || ''}
                                                            disabled={updatingUser === user.uid}
                                                            onChange={(e) => handleUpdateUserCampaign(user.uid, e.target.value)}
                                                            className={`text-[10px] font-bold rounded-lg border px-2 py-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-colors ${
                                                                user.campaign
                                                                    ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800/50 text-purple-700 dark:text-purple-300'
                                                                    : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400'
                                                            }`}
                                                            title={user.campaign ? (campaigns.find(c => c.id === user.campaign)?.name || '(deleted)') : 'Tiada campaign'}
                                                        >
                                                            <option value="">— None —</option>
                                                            {campaigns.map(c => (
                                                                <option key={c.id} value={c.id}>{c.name}</option>
                                                            ))}
                                                            {user.campaign && !campaigns.find(c => c.id === user.campaign) && (
                                                                <option value={user.campaign}>(deleted: {user.campaign.substring(0, 6)}…)</option>
                                                            )}
                                                        </select>
                                                    ) : (
                                                        <span className="text-[10px] text-slate-400">
                                                            {user.campaign ? (campaigns.find(c => c.id === user.campaign)?.name || '—') : '—'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-center" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                                                    <div className="inline-flex items-center gap-1">
                                                        {user.referredBy ? (
                                                            <button
                                                                onClick={() => navigate(`/admin/affiliate/affiliates?ref=${encodeURIComponent(user.referredBy)}`)}
                                                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-[10px] font-mono font-bold transition-colors"
                                                                title={`Lihat profil ejen ${user.referredBy}`}
                                                            >
                                                                {user.referredBy}
                                                            </button>
                                                        ) : (
                                                            <span className="text-[10px] text-slate-300 dark:text-slate-600">—</span>
                                                        )}
                                                        {isFullAdmin && (
                                                            <button
                                                                onClick={() => {
                                                                    setEditingReferral({ uid: user.uid, email: user.email || '', currentRefCode: user.referredBy || '' });
                                                                    setReferralInput(user.referredBy || '');
                                                                    setReferralError('');
                                                                }}
                                                                className="p-1 rounded text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                                                                title="Edit ejen aff (manual fix)"
                                                            >
                                                                <Edit3 size={11} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center text-xs text-slate-500">{user.createdAt ? new Date(user.createdAt).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short' }) : '-'}</td>
                                                <td className="px-4 py-3 text-center" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                                                    {isFullAdmin ? (
                                                        <div className="flex items-center justify-center gap-1">
                                                            <button
                                                                onClick={() => handleApprovePayment(user)}
                                                                disabled={updatingUser === user.uid}
                                                                className="px-2 py-1 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-400 rounded-lg text-[10px] font-bold transition-colors disabled:opacity-50"
                                                                title="Aktifkan langganan manual (RM199 / 30 hari) + auto credit komisen affiliate"
                                                            >
                                                                <CreditCard size={12} className="inline mr-1" />Aktifkan
                                                            </button>
                                                            <button onClick={() => setEditingUser(user)} className="px-2 py-1 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 text-indigo-600 rounded-lg text-[10px] font-bold transition-colors"><Edit3 size={12} className="inline mr-1" />Edit</button>
                                                            <RowActionMenu>
                                                                {({ close }) => (
                                                                    <>
                                                                        <MenuItem
                                                                            icon={RefreshCw}
                                                                            disabled={updatingUser === user.uid}
                                                                            onClick={() => { handleResetUsage(user.uid); close(); }}
                                                                        >
                                                                            Reset Daily Usage
                                                                        </MenuItem>
                                                                        <MenuItem
                                                                            icon={Zap}
                                                                            disabled={updatingUser === user.uid}
                                                                            onClick={() => { handleResetPipeline(user.uid); close(); }}
                                                                        >
                                                                            Reset Pipeline Cap{user.dailyPipelineUsed ? ` (${user.dailyPipelineUsed}/100)` : ''}
                                                                        </MenuItem>
                                                                        {user.tier === 'trial' && (
                                                                            <MenuItem
                                                                                icon={Coins}
                                                                                disabled={updatingUser === user.uid}
                                                                                onClick={() => { handleResetTrialCoins(user.uid); close(); }}
                                                                            >
                                                                                Reset Trial Coins{typeof user.pipelineCoins === 'number' ? ` (${user.pipelineCoins}/5)` : ''}
                                                                            </MenuItem>
                                                                        )}
                                                                        <MenuItem
                                                                            icon={Key}
                                                                            onClick={() => { setAuthUser(user); setAuthLinkResult(''); setNewEmailInput(''); close(); }}
                                                                        >
                                                                            Auth / Keselamatan
                                                                        </MenuItem>
                                                                        {auth.currentUser?.email === SUPER_ADMIN_EMAIL && user.email !== SUPER_ADMIN_EMAIL && (
                                                                            <>
                                                                                <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
                                                                                <MenuItem
                                                                                    icon={Trash2}
                                                                                    variant="danger"
                                                                                    disabled={updatingUser === user.uid}
                                                                                    onClick={() => { openDeleteUserModal(user); close(); }}
                                                                                >
                                                                                    Delete User
                                                                                </MenuItem>
                                                                            </>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </RowActionMenu>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-slate-400 italic">View only</span>
                                                    )}
                                                </td>
                                            </tr>
                                        )})}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between gap-4 flex-wrap">
                                <p className="text-xs text-slate-400">
                                    {usersScope === 'search' ? (
                                        <>
                                            Menunjukkan <b>{filteredUsers.length}</b> pengguna sepadan carian "{searchTerm}"
                                            <span className="ml-2 text-emerald-600 dark:text-emerald-400">✓ Carian terus ke server — merentas semua pengguna</span>
                                        </>
                                    ) : usersScope === 'all' ? (
                                        <>
                                            Halaman <b>{allModePage + 1}</b> / {totalAllPages} — papar {pagedUsers.length} daripada <b>{filteredUsers.length}</b> ditapis
                                            {totalCounts.total > 0 && <> ({totalCounts.total} total)</>}
                                            <span className="ml-2 text-emerald-600 dark:text-emerald-400">✓ Merentas semua pengguna</span>
                                        </>
                                    ) : (
                                        <>
                                            Halaman {currentPage + 1} — papar {filteredUsers.length} pengguna
                                            {totalCounts.total > 0 && <> daripada <b>{totalCounts.total}</b> total</>}
                                        </>
                                    )}
                                </p>
                                {usersScope === 'all' && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(0)}
                                            disabled={loading || allModePage === 0}
                                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        >
                                            ← Pertama
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                            disabled={loading || allModePage === 0}
                                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Sebelum
                                        </button>
                                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 px-2">
                                            {allModePage + 1} / {totalAllPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalAllPages - 1, p + 1))}
                                            disabled={loading || allModePage >= totalAllPages - 1}
                                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Seterusnya →
                                        </button>
                                    </div>
                                )}
                                {usersScope === 'page' && (() => {
                                    const cf = isCampaignFiltered ? filterCampaign : null;
                                    const pageDenom = cf
                                        ? (campaignCounts.byId[cf] || 0)
                                        : totalCounts.total;
                                    const totalPages = Math.max(1, Math.ceil(pageDenom / PAGE_SIZE));
                                    return (
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => fetchUsersPage(0, { campaignFilter: cf })}
                                                disabled={loading || currentPage === 0}
                                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            >
                                                ← Pertama
                                            </button>
                                            <button
                                                onClick={() => currentPage > 0 && fetchUsersPage(currentPage - 1, { campaignFilter: cf })}
                                                disabled={loading || currentPage === 0}
                                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            >
                                                Sebelum
                                            </button>
                                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 px-2">
                                                {currentPage + 1}{pageDenom ? ` / ${totalPages}` : ''}
                                            </span>
                                            <button
                                                onClick={() => hasMore && fetchUsersPage(currentPage + 1, { campaignFilter: cf })}
                                                disabled={loading || !hasMore}
                                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            >
                                                Seterusnya →
                                            </button>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </>
                )}

                {/* ═══ TAB: REKOD JUALAN (orders — Billplz gateway) ═══ */}
                {activeAdminTab === 'sales' && (() => {
                    // KPI: dari server aggregation (tepat, ikut tapisan tarikh) — fallback ke kiraan
                    // baris yang dimuat kalau stats belum siap. Ini yang betulkan "jumlah kurang lapor".
                    const loadedPaid = salesRecords.filter(o => o.status === 'paid');
                    const num = (v, fb) => (v == null ? fb : v); // guna angka server; fallback per-field kalau null
                    const kpiPaidSen = num(salesStats?.paidSen, loadedPaid.reduce((s, o) => s + (o.paidAmountSen || o.amountSen || 0), 0));
                    const kpiPaidCount = num(salesStats?.paidCount, loadedPaid.length);
                    const kpiPendingCount = num(salesStats?.pendingCount, salesRecords.filter(o => o.status === 'pending').length);
                    const kpiFailedCount = num(salesStats?.failedCount, salesRecords.filter(o => o.status === 'failed').length);
                    const getOrderTasks = getSalesOrderTasks;
                    // ── 3 card sumber (2026-10-25): Semua · Billplz · Manual ──
                    // Semua = angka server (atau carian). Manual = resit manual dalam julat tarikh. Billplz = beza.
                    const manualDalamJulat = salesManual.rows.filter(o => dalamJulatTarikh(o, salesDateRange()));
                    const kadSumber = pecahanSumber(
                        { count: kpiPaidCount, sen: kpiPaidSen },
                        salesSearchTerm ? jumlahDibayar(salesRecords.filter(adalahOrderManual))
                            : salesManual.gagal ? null : jumlahDibayar(manualDalamJulat),
                    );
                    // Baris ikut card dipilih. Card Manual (bukan carian) guna senarai manual penuh — bukan
                    // page server 50 baris, supaya resit lama tak tersorok di belakang "Muat lagi".
                    const salesSourceManualView = !salesSearchTerm && salesSource === 'manual';
                    const barisSumber = salesSourceManualView ? manualDalamJulat
                        : salesRecords.filter(o => padanSumber(o, salesSource));
                    // Bilangan "belum settle" antara baris yang DIMUAT (bukan global — tugasan penghantaran
                    // tak boleh di-aggregate server-side; naik bila admin klik "Muat lagi").
                    const deliveryPendingCount = barisSumber.filter(o => o.status === 'paid' && getOrderTasks(o).some(t => !t.done)).length;
                    const preFiltered = salesFilter === 'all' ? barisSumber
                        : salesFilter === 'delivery_pending' ? barisSumber.filter(o => o.status === 'paid' && getOrderTasks(o).some(t => !t.done))
                        : barisSumber.filter(o => o.status === salesFilter);
                    // Sort ikut column (Jenis / Amaun / Penghantaran); dalam kumpulan sama, kekal tarikh terkini (stable sort atas data yang dah desc)
                    const jenisRank = (o) => { const j = jenisOrder(o); return j === 'Add-on' ? 3 : j === 'Upgrade' ? 2 : j === 'Sambung' ? 1 : 0; }; // Baru → Sambung → Upgrade → Add-on
                    const deliveryRank = (o) => {
                        const tasks = getOrderTasks(o);
                        if (!tasks.length) return 2;               // takda tugasan (—) — paling bawah
                        return tasks.some(t => !t.done) ? 0 : 1;   // belum settle atas, siap semua tengah
                    };
                    const sortVal = (o) => salesSort.col === 'jenis' ? jenisRank(o) : salesSort.col === 'amaun' ? (o.amountSen || 0) : deliveryRank(o);
                    // Sort tambahan: Tarikh (millis), Pakej (A→Z), Dalam GPT (belum masuk dulu), Ref (A→Z)
                    const tsMillis = (o) => { try { const ts = o.createdAt; const d = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null); return d ? d.getTime() : 0; } catch { return 0; } };
                    const gptRank = (o) => o.status !== 'paid' ? 2 : (o.lesenInGpt ? 1 : 0); // belum masuk (0) → dah masuk (1) → tiada checkbox (2)
                    const refKey = (o) => (o.refCode || '').trim();
                    const statusRank = (o) => o.status === 'paid' ? 0 : o.status === 'pending' ? 1 : o.status === 'failed' ? 2 : 3; // Dibayar → Menunggu → Gagal → lain-lain
                    const sinkEmpty = (aEmpty, bEmpty) => aEmpty && bEmpty ? 0 : aEmpty ? 1 : bEmpty ? -1 : null; // "—" sentiasa jatuh ke bawah, abai dir
                    const salesCmp = (a, b) => {
                        const { col, dir } = salesSort;
                        if (col === 'tarikh') return dir * (tsMillis(a) - tsMillis(b));
                        if (col === 'status') return dir * (statusRank(a) - statusRank(b));
                        if (col === 'pakej') { const pa = namaPakejOrder(a), pb = namaPakejOrder(b); const s = sinkEmpty(pa === '—', pb === '—'); return s !== null ? s : dir * pa.localeCompare(pb); }
                        if (col === 'gpt') { const s = sinkEmpty(gptRank(a) === 2, gptRank(b) === 2); return s !== null ? s : dir * (gptRank(a) - gptRank(b)); }
                        if (col === 'ref') { const ka = refKey(a), kb = refKey(b); const s = sinkEmpty(!ka, !kb); return s !== null ? s : dir * ka.localeCompare(kb); }
                        return dir * (sortVal(a) - sortVal(b));
                    };
                    const filtered = !salesSort ? preFiltered : [...preFiltered].sort(salesCmp);
                    // Klik: asc → desc → reset default
                    const toggleSalesSort = (col) => setSalesSort(prev =>
                        !prev || prev.col !== col ? { col, dir: 1 } : prev.dir === 1 ? { col, dir: -1 } : null
                    );
                    const sortArrow = (col) => salesSort?.col === col
                        ? (salesSort.dir === 1 ? <ChevronUp size={13} className="inline -mt-0.5" /> : <ChevronDown size={13} className="inline -mt-0.5" />)
                        : <ChevronDown size={13} className="inline -mt-0.5 opacity-25" />;
                    const fmtDate = (ts) => { try { const d = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null); return d ? d.toLocaleString('ms-MY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'; } catch { return '—'; } };
                    const statusPill = (st) => {
                        const map = {
                            paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
                            pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                            failed: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
                        };
                        const label = { paid: 'Dibayar', pending: 'Menunggu', failed: 'Gagal' };
                        return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[st] || 'bg-slate-100 text-slate-600'}`}>{label[st] || st}</span>;
                    };
                    return (
                        <div className="space-y-6">
                            {/* Header + actions */}
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <h2 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        <CreditCard size={18} className="text-indigo-500" /> Rekod Jualan
                                    </h2>
                                    <p className="text-xs text-slate-500 mt-0.5">Pesanan Billplz + renewal Flow yang dibayar manual (transfer).</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={copyLesenChatGPT} disabled={isCopyingLesen || !kpiPaidCount} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm font-semibold disabled:opacity-50" title="Salin lesen buyer paid yang belum ditanda 'Dalam GPT'">
                                        {isCopyingLesen ? <RefreshCw size={14} className="animate-spin" /> : <Copy size={14} />} Copy Lesen Belum Masuk
                                    </button>
                                    <button onClick={exportSalesCSV} disabled={salesExporting || salesLoading} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50" title="Eksport SEMUA rekod yang sepadan tapisan semasa">
                                        {salesExporting ? <RefreshCw size={14} className="animate-spin" /> : <FileText size={14} />} Export CSV
                                    </button>
                                    <button onClick={refreshSales} disabled={salesLoading} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50">
                                        <RefreshCw size={14} className={salesLoading ? 'animate-spin' : ''} /> Refresh
                                    </button>
                                </div>
                            </div>

                            {/* 3 card sumber (Semua / Billplz / Manual) — klik = tapis senarai. Angka "Semua" dari
                                server aggregation (tepat, ikut tapisan tarikh); Manual dikira dari resit manual; Billplz = beza. */}
                            <div className={`space-y-2 transition-opacity ${salesStatsLoading ? 'opacity-50' : ''}`}>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {[['all', 'Semua (Dibayar)'], ['billplz', 'Billplz'], ['manual', 'Manual']].map(([key, label]) => {
                                        const v = kadSumber[key];
                                        const aktif = salesSource === key;
                                        return (
                                            <button key={key} type="button" onClick={() => setSalesSource(key)}
                                                className={`text-left rounded-xl border-2 bg-white dark:bg-slate-900 p-4 transition ${aktif ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}
                                                title={v ? `Tunjuk rekod ${SUMBER_LABEL[key]} sahaja` : 'Rekod manual gagal dimuat — tekan Refresh'}>
                                                <p className={`text-[11px] font-semibold uppercase tracking-wider ${aktif ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500'}`}>{label}</p>
                                                <p className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1">
                                                    {v ? `RM${(v.sen / 100).toLocaleString('ms-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                                                </p>
                                                <p className="text-xs text-slate-500 mt-0.5">{v ? `${v.count} berjaya` : 'Gagal dimuat — tekan Refresh'}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-slate-500">
                                    Menunggu: <b className="text-amber-600 dark:text-amber-400">{kpiPendingCount}</b>
                                    <span className="mx-2 text-slate-300 dark:text-slate-600">·</span>
                                    Gagal: <b className="text-rose-600 dark:text-rose-400">{kpiFailedCount}</b>
                                </p>
                            </div>

                            {/* Kawalan tarikh + carian email */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <Calendar size={14} className="text-slate-400" />
                                    {[['all', 'Semua Masa'], ['month', 'Bulan Ini'], ['lastmonth', 'Bulan Lepas'], ['custom', 'Custom']].map(([key, label]) => (
                                        <button key={key} onClick={() => setSalesDatePreset(key)} className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition ${salesDatePreset === key ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}>{label}</button>
                                    ))}
                                    {salesDatePreset === 'custom' && (
                                        <div className="flex items-center gap-1.5">
                                            <input type="date" value={salesFrom} onChange={e => setSalesFrom(e.target.value)} className="px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200" />
                                            <span className="text-xs text-slate-400">→</span>
                                            <input type="date" value={salesTo} onChange={e => setSalesTo(e.target.value)} className="px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200" />
                                        </div>
                                    )}
                                </div>
                                <form onSubmit={(e) => { e.preventDefault(); setSalesSearchTerm(salesSearchInput.trim()); }} className="flex items-center gap-1.5 sm:ml-auto">
                                    <div className="relative">
                                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input value={salesSearchInput} onChange={e => setSalesSearchInput(e.target.value)} placeholder="Cari email / nama / WhatsApp / ID…" className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200 w-64" />
                                    </div>
                                    <button type="submit" className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700">Cari</button>
                                    {salesSearchTerm && (
                                        <button type="button" onClick={() => { setSalesSearchInput(''); setSalesSearchTerm(''); }} className="px-2 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs flex items-center gap-1"><XCircle size={13} /> Reset</button>
                                    )}
                                </form>
                            </div>
                            {salesSearchTerm && (
                                <div className="-mt-3 text-xs text-indigo-600 dark:text-indigo-400">Menunjukkan semua order untuk <b>{salesSearchTerm}</b> — tapisan status &amp; tarikh diabaikan.</div>
                            )}

                            {/* Filter pills (status) — dinyahaktif dalam mod carian email */}
                            <div className={`flex items-center gap-2 flex-wrap ${salesSearchTerm ? 'opacity-40 pointer-events-none' : ''}`}>
                                {[['all', 'Semua'], ['paid', 'Dibayar'], ['pending', 'Menunggu'], ['failed', 'Gagal']].map(([key, label]) => (
                                    <button key={key} onClick={() => setSalesFilter(key)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${salesFilter === key ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}>{label}</button>
                                ))}
                                <button onClick={() => setSalesFilter('delivery_pending')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 ${salesFilter === 'delivery_pending' ? 'bg-amber-500 text-white' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50'}`}>
                                    <Gift size={12} /> Belum settle{deliveryPendingCount > 0 ? ` (${deliveryPendingCount}${salesHasMore ? '+' : ''})` : ''}
                                </button>
                            </div>

                            {/* Table */}
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                                                <button onClick={() => toggleSalesSort('tarikh')} className={`inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400 transition ${salesSort?.col === 'tarikh' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-300'}`} title="Sort ikut tarikh (lama ↔ baru)">
                                                    Tarikh {sortArrow('tarikh')}
                                                </button>
                                            </th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300">Pelanggan</th>
                                            <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                                                <button onClick={() => toggleSalesSort('jenis')} className={`inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400 transition ${salesSort?.col === 'jenis' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-300'}`} title="Sort ikut jenis (Baru / Sambung / Add-on)">
                                                    Jenis {sortArrow('jenis')}
                                                </button>
                                            </th>
                                            <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                                                <button onClick={() => toggleSalesSort('pakej')} className={`inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400 transition ${salesSort?.col === 'pakej' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-300'}`} title="Sort ikut pakej (A → Z)">
                                                    Pakej {sortArrow('pakej')}
                                                </button>
                                            </th>
                                            <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                                                <button onClick={() => toggleSalesSort('amaun')} className={`inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400 transition ${salesSort?.col === 'amaun' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-300'}`} title="Sort ikut amaun (RM)">
                                                    Amaun {sortArrow('amaun')}
                                                </button>
                                            </th>
                                            <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                                                <button onClick={() => toggleSalesSort('status')} className={`inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400 transition ${salesSort?.col === 'status' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-300'}`} title="Sort ikut status (Dibayar → Menunggu → Gagal)">
                                                    Status {sortArrow('status')}
                                                </button>
                                            </th>
                                            <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                                                <button onClick={() => toggleSalesSort('penghantaran')} className={`inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400 transition ${salesSort?.col === 'penghantaran' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-300'}`} title="Sort ikut status penghantaran (belum settle dulu)">
                                                    Penghantaran {sortArrow('penghantaran')}
                                                </button>
                                            </th>
                                            <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                                                <button onClick={() => toggleSalesSort('gpt')} className={`inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400 transition ${salesSort?.col === 'gpt' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-300'}`} title="Sort ikut status GPT (belum masuk dulu)">
                                                    Dalam GPT {sortArrow('gpt')}
                                                </button>
                                            </th>
                                            <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                                                <button onClick={() => toggleSalesSort('ref')} className={`inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400 transition ${salesSort?.col === 'ref' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-300'}`} title="Sort ikut kod ref (A → Z)">
                                                    Ref {sortArrow('ref')}
                                                </button>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                        {salesLoading ? (
                                            <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                                        ) : filtered.length ? filtered.map(o => (
                                            <tr key={o.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                                <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">{fmtDate(o.createdAt)}</td>
                                                <td className="px-4 py-3">
                                                    <p className="text-slate-800 dark:text-white font-medium truncate max-w-[220px]">{o.email || '—'}</p>
                                                    {o.whatsappNumber && <p className="text-[11px] text-slate-400">{o.whatsappNumber}</p>}
                                                </td>
                                                <td className="px-4 py-3 text-xs whitespace-nowrap">
                                                    {(() => { const j = jenisOrder(o); return (
                                                    <span className={`px-2 py-0.5 rounded-full font-semibold ${j === 'Add-on' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : j === 'Upgrade' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' : j === 'Sambung' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>{j}</span>
                                                    ); })()}
                                                </td>
                                                <td className="px-4 py-3 text-xs font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">{namaPakejOrder(o)}</td>
                                                <td className="px-4 py-3 text-right font-semibold text-slate-800 dark:text-white whitespace-nowrap">
                                                    RM{((o.amountSen || 0) / 100).toFixed(2)}
                                                    {/* Resit manual (Extend days → Paid renewal) — bukan melalui Billplz. */}
                                                    {adalahOrderManual(o) && (
                                                        <div className="mt-1">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400" title={`Transfer manual — direkod melalui Extend days${o.manualBy ? ` oleh ${o.manualBy}` : ''}`}>Manual</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">{statusPill(o.status)}</td>
                                                <td className="px-4 py-3 text-xs">
                                                    {(() => {
                                                        const tasks = getOrderTasks(o);
                                                        if (!tasks.length) return <span className="text-slate-300 dark:text-slate-600">—</span>;
                                                        const canDeliver = o.status === 'paid'; // belum bayar = belum perlu hantar (chips kelabu read-only)
                                                        return (
                                                            <div className="flex flex-wrap gap-1">
                                                                {tasks.map(t => t.done ? (
                                                                    <span key={t.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-semibold"><Check size={11} /> {t.label}</span>
                                                                ) : canDeliver ? (
                                                                    <button key={t.key} onClick={() => markOrderTask(o.id, t.key)} className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500 text-white font-semibold hover:bg-amber-600 transition" title={`Tandakan ${t.label} sudah dihantar`}>
                                                                        {t.label}
                                                                    </button>
                                                                ) : (
                                                                    <span key={t.key} className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500 font-semibold">{t.label}</span>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {o.status === 'paid' ? (
                                                        <input type="checkbox" checked={!!o.lesenInGpt} onChange={() => toggleOrderGptFlag(o.id, !!o.lesenInGpt)} className="w-4 h-4 rounded accent-amber-500 cursor-pointer" title={o.lesenInGpt ? 'Dah masuk GPT — klik untuk buang tanda' : 'Belum masuk GPT — klik untuk tanda'} />
                                                    ) : (
                                                        <span className="text-slate-300 dark:text-slate-600">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{o.refCode || '—'}</td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-400">{salesSearchTerm ? `Tiada order untuk "${salesSearchTerm}".` : salesSourceManualView && salesManual.gagal ? 'Rekod manual gagal dimuat. Tekan Refresh.' : 'Tiada rekod untuk tapisan ini.'}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer: muat lagi / kiraan */}
                            {!salesSearchTerm && !salesLoading && (
                                <div className="flex items-center justify-center gap-3 pt-1">
                                    {salesSourceManualView ? (
                                        manualDalamJulat.length > 0 && <span className="text-xs text-slate-400">Semua rekod manual dimuat ({manualDalamJulat.length}).</span>
                                    ) : salesHasMore ? (
                                        <button onClick={loadMoreSales} disabled={salesLoadingMore} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
                                            {salesLoadingMore ? <Loader2 size={15} className="animate-spin" /> : <ChevronDown size={15} />} Muat lagi
                                        </button>
                                    ) : salesRecords.length > 0 ? (
                                        <span className="text-xs text-slate-400">Semua rekod dimuat ({salesRecords.length}).</span>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* ═══ TAB: PENDING PREMIUM (manual bank transfer submissions) ═══ */}
                {activeAdminTab === 'pending-premium' && (
                    <div className="max-w-4xl mx-auto space-y-4">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                                        <Inbox size={20} className="text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-800 dark:text-white">Pending Premium</h3>
                                        <p className="text-xs text-slate-500">Submission upgrade Premium yang menunggu pengesahan.</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="hidden md:inline text-[10px] text-slate-400 mr-1">
                                        Last updated: {lastRefreshedPending ? formatRelativeTime(lastRefreshedPending) : '—'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => loadPendingSubmissions()}
                                        disabled={refreshingPending}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Refresh senarai pending"
                                    >
                                        <RefreshCw size={12} className={refreshingPending ? 'animate-spin' : ''} />
                                        Refresh
                                    </button>
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs font-bold">
                                        <Clock size={12} />
                                        {pendingSubmissions.length} Menunggu
                                    </span>
                                </div>
                            </div>

                            {submissionsLoading ? (
                                <div className="py-12 flex items-center justify-center text-slate-400">
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                    <span className="text-sm">Memuat submissions...</span>
                                </div>
                            ) : pendingSubmissions.length === 0 ? (
                                <div className="py-12 text-center">
                                    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                                        <Check className="w-7 h-7 text-emerald-500" strokeWidth={3} />
                                    </div>
                                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Semua submission selesai!</p>
                                    <p className="text-xs text-slate-500 mt-1">Takda submission menunggu pengesahan.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {pendingSubmissions.map(sub => {
                                        const isProcessing = processingSubmissionId === sub.id;
                                        const pdf = isPdfProof(sub);
                                        return (
                                            <div key={sub.id} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4">
                                                <div className="flex flex-col sm:flex-row gap-4">
                                                    {/* Proof thumbnail (or PDF icon) */}
                                                    <div className="flex-shrink-0">
                                                        {pdf ? (
                                                            <a
                                                                href={sub.proofImageUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="block w-24 h-24 rounded-lg border-2 border-rose-200 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/20 hover:border-rose-400 transition flex flex-col items-center justify-center"
                                                                title="Buka PDF"
                                                            >
                                                                <FileText className="w-9 h-9 text-rose-600" />
                                                                <span className="text-[10px] font-bold text-rose-700 mt-1">PDF</span>
                                                            </a>
                                                        ) : sub.proofImageUrl ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => setLightboxUrl(sub.proofImageUrl)}
                                                                className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-slate-200 dark:border-slate-700 hover:border-emerald-400 transition group"
                                                                title="Klik untuk zoom"
                                                            >
                                                                <img src={sub.proofImageUrl} alt="Bukti" className="w-full h-full object-cover" />
                                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center">
                                                                    <Maximize2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition" />
                                                                </div>
                                                            </button>
                                                        ) : (
                                                            <div className="w-24 h-24 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                                                <span className="text-[10px] text-slate-500">Memuat...</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Customer info */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{sub.email || '—'}</p>
                                                                <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                                                                    <Clock size={11} />
                                                                    <span>Hantar: {formatSubmissionDate(sub.submittedAt)}</span>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                                {sub.type === 'renewal' ? (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold">
                                                                        🔄 RENEWAL
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold">
                                                                        🆕 BARU
                                                                    </span>
                                                                )}
                                                                {sub.flowAddon && (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-bold">
                                                                        🎁 +FLOW
                                                                    </span>
                                                                )}
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
                                                                    <Clock size={10} />
                                                                    PENDING
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-600 dark:text-slate-400">
                                                            <div className="flex items-center gap-1.5">
                                                                <MessageCircle size={11} className="text-emerald-500" />
                                                                <span className="font-mono">{sub.whatsappNumber || '— takda nombor —'}</span>
                                                            </div>
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-slate-400">UID:</span>
                                                                <span className="font-mono truncate">{sub.uid}</span>
                                                            </div>
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-slate-400">Amount:</span>
                                                                <span className="font-semibold">RM{((sub.amount || 19900) / 100).toFixed(2)}</span>
                                                                {sub.flowAddon && (
                                                                    <span className="text-[10px] text-amber-600 dark:text-amber-400">(RM{(((sub.baseSen || 19900)) / 100).toFixed(0)} + RM{((sub.flowAddonSen || 4900) / 100).toFixed(0)} Flow)</span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-slate-400">Fail:</span>
                                                                <span className="truncate">{sub.proofFileName || '—'}</span>
                                                            </div>
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="flex flex-wrap items-center gap-2 mt-3">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleActivateSubmission(sub)}
                                                                disabled={isProcessing}
                                                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white text-xs font-bold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
                                                            >
                                                                {isProcessing ? (
                                                                    <>
                                                                        <Loader2 size={12} className="animate-spin" />
                                                                        Memproses...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Check size={12} strokeWidth={3} />
                                                                        Aktifkan
                                                                    </>
                                                                )}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRejectSubmission(sub)}
                                                                disabled={isProcessing}
                                                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/20 dark:hover:bg-rose-900/30 text-rose-700 dark:text-rose-400 text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition"
                                                            >
                                                                <XCircle size={12} />
                                                                Reject
                                                            </button>
                                                            {sub.proofImageUrl && (
                                                                <a
                                                                    href={sub.proofImageUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 text-xs font-semibold transition"
                                                                >
                                                                    Buka {pdf ? 'PDF' : 'Gambar'}
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* ═══ EXPIRING SOON: license tamat dalam 30 hari ═══ */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center">
                                        <Clock size={20} className="text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-800 dark:text-white">License Tamat Tak Lama Lagi</h3>
                                        <p className="text-xs text-slate-500">User yang license akan tamat dalam 30 hari — push reminder via Email atau WhatsApp.</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={fetchExpiringSoon}
                                    disabled={expiringSoonLoading}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold disabled:opacity-50"
                                >
                                    <RefreshCw size={12} className={expiringSoonLoading ? 'animate-spin' : ''} />
                                    Refresh
                                </button>
                            </div>

                            {/* Filter pills (kiri) + kotak "Tamat dari" (kanan) — satu baris, compact */}
                            <div className="flex items-center flex-wrap gap-3 mb-4">
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        { id: 'all', label: 'Semua', count: expiringBase.length },
                                        { id: '1', label: '≤ 1 hari', count: expiringBase.filter(e => e.daysLeft >= 0 && e.daysLeft <= 1).length },
                                        { id: '7', label: '≤ 7 hari', count: expiringBase.filter(e => e.daysLeft >= 0 && e.daysLeft <= 7).length },
                                        { id: '14', label: '≤ 14 hari', count: expiringBase.filter(e => e.daysLeft >= 0 && e.daysLeft <= 14).length },
                                        { id: '21', label: '≤ 21 hari', count: expiringBase.filter(e => e.daysLeft >= 0 && e.daysLeft <= 21).length },
                                        { id: 'expired', label: 'Dah expired', count: expiringBase.filter(e => e.daysLeft < 0).length },
                                    ].map(pill => (
                                        <button
                                            key={pill.id}
                                            type="button"
                                            onClick={() => setExpiringFilter(pill.id)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                                                expiringFilter === pill.id
                                                    ? 'bg-purple-600 text-white'
                                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                            }`}
                                        >
                                            {pill.label}
                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${expiringFilter === pill.id ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700'}`}>
                                                {pill.count}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                {/* Kotak "Tamat dari" — compact, sebaris dengan pills */}
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <Calendar size={14} className="text-slate-400" />
                                    <span className="text-xs text-slate-500 whitespace-nowrap">Tamat dari</span>
                                    <input
                                        type="date"
                                        value={expiringFromDate}
                                        min={expiringMinDate || undefined}
                                        max={expiringMaxDate || undefined}
                                        onChange={e => setExpiringFromDate(e.target.value)}
                                        className="px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-purple-400"
                                    />
                                    {expiringFromDate && (
                                        <button
                                            type="button"
                                            onClick={() => setExpiringFromDate('')}
                                            title="Kosongkan tarikh"
                                            className="px-1.5 py-1 rounded-lg text-xs text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            </div>

                            {expiringSoonLoading ? (
                                <div className="py-8 flex items-center justify-center text-slate-400">
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                    <span className="text-sm">Memuat...</span>
                                </div>
                            ) : (() => {
                                const filtered = expiringBase.filter(e => {
                                    if (expiringFilter === 'all') return true;
                                    if (expiringFilter === '1') return e.daysLeft >= 0 && e.daysLeft <= 1;
                                    if (expiringFilter === '7') return e.daysLeft >= 0 && e.daysLeft <= 7;
                                    if (expiringFilter === '14') return e.daysLeft >= 0 && e.daysLeft <= 14;
                                    if (expiringFilter === '21') return e.daysLeft >= 0 && e.daysLeft <= 21;
                                    if (expiringFilter === 'expired') return e.daysLeft < 0;
                                    return true;
                                });
                                if (filtered.length === 0) {
                                    return (
                                        <div className="py-8 text-center text-xs text-slate-400">
                                            Takda user yang sepadan dengan filter ni.
                                        </div>
                                    );
                                }
                                return (
                                    <div className="overflow-x-auto -mx-1">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 dark:border-slate-700">
                                                    <th className="px-2 py-2 font-semibold">Email</th>
                                                    <th className="px-2 py-2 font-semibold">Tamat</th>
                                                    <th className="px-2 py-2 font-semibold">Hari Lagi</th>
                                                    <th className="px-2 py-2 font-semibold">Reminder</th>
                                                    <th className="px-2 py-2 font-semibold text-right">Tindakan</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filtered.map(item => {
                                                    const isExpired = item.daysLeft < 0;
                                                    const isUrgent = item.daysLeft >= 0 && item.daysLeft <= 1;
                                                    const isSoon = item.daysLeft >= 0 && item.daysLeft <= 7;
                                                    const sending = sendingReminderUid === item.uid;
                                                    // Reminder marked = ANY of 3 fields populated.
                                                    // Pick paling latest untuk display tarikh.
                                                    const candidateDates = [
                                                        item.lastReminderManualAt,
                                                        item.lastReminderSentAt1d,
                                                        item.lastReminderSentAt7d
                                                    ].filter(Boolean);
                                                    const lastSentDate = candidateDates.length > 0
                                                        ? new Date(Math.max(...candidateDates.map(d => d.getTime())))
                                                        : null;
                                                    return (
                                                        <tr key={item.uid} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                            <td className="px-2 py-2.5">
                                                                <p className="font-semibold text-slate-800 dark:text-white truncate max-w-[200px]">{item.email}</p>
                                                                <p className="text-[10px] text-slate-400 font-mono truncate max-w-[200px]">{item.uid}</p>
                                                            </td>
                                                            <td className="px-2 py-2.5 text-slate-500 whitespace-nowrap">
                                                                {item.expiresAt.toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                            </td>
                                                            <td className="px-2 py-2.5">
                                                                {isExpired ? (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold whitespace-nowrap">
                                                                        Tamat {Math.abs(item.daysLeft)}d
                                                                    </span>
                                                                ) : isUrgent ? (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold whitespace-nowrap">
                                                                        {item.daysLeft}d lagi
                                                                    </span>
                                                                ) : isSoon ? (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold whitespace-nowrap">
                                                                        {item.daysLeft}d lagi
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold whitespace-nowrap">
                                                                        {item.daysLeft}d lagi
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-2 py-2.5 whitespace-nowrap">
                                                                {lastSentDate ? (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold">
                                                                        <Check size={10} strokeWidth={3} />
                                                                        Dah · {lastSentDate.toLocaleDateString('ms-MY', { day: 'numeric', month: 'short' })}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-slate-300">—</span>
                                                                )}
                                                            </td>
                                                            <td className="px-2 py-2.5 text-right">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setReminderModal({ item, copyState: 'idle' })}
                                                                    disabled={sending}
                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white text-[11px] font-bold shadow-sm disabled:opacity-50"
                                                                    title="Buka modal reminder (copy nombor + ayat, hantar email / wasap)"
                                                                >
                                                                    {sending ? <Loader2 size={11} className="animate-spin" /> : <MessageCircle size={11} />}
                                                                    Reminder
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* ─── Sejarah Aktivasi (activated + rejected submissions) ─── */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center">
                                        <Clock size={20} className="text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-800 dark:text-white">Sejarah Submission</h3>
                                        <p className="text-xs text-slate-500">Submission yang dah selesai (activated atau rejected). Boleh copy ayat WhatsApp semula.</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="hidden md:inline text-[10px] text-slate-400 mr-1">
                                        Last updated: {lastRefreshedHistory ? formatRelativeTime(lastRefreshedHistory) : '—'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => loadHistorySubmissions()}
                                        disabled={refreshingHistory}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Refresh sejarah submission"
                                    >
                                        <RefreshCw size={12} className={refreshingHistory ? 'animate-spin' : ''} />
                                        Refresh
                                    </button>
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold">
                                        {submissionsHistory.length} Rekod
                                    </span>
                                </div>
                            </div>

                            {historyLoading ? (
                                <div className="py-8 flex items-center justify-center text-slate-400">
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    <span className="text-xs">Memuat sejarah...</span>
                                </div>
                            ) : submissionsHistory.length === 0 ? (
                                <div className="py-8 text-center text-xs text-slate-400">
                                    Belum ada rekod. Sejarah akan muncul selepas anda Aktifkan atau Reject submission.
                                </div>
                            ) : (
                                <div className="overflow-x-auto -mx-1">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 dark:border-slate-700">
                                                <th className="px-2 py-2 font-semibold">Email</th>
                                                <th className="px-2 py-2 font-semibold">Jenis</th>
                                                <th className="px-2 py-2 font-semibold">Status</th>
                                                <th className="px-2 py-2 font-semibold">License Key</th>
                                                <th className="px-2 py-2 font-semibold">Tarikh</th>
                                                <th className="px-2 py-2 font-semibold text-right">Tindakan</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {submissionsHistory.map(sub => {
                                                const isActivated = sub.status === 'activated';
                                                const isRenewal = sub.type === 'renewal';
                                                const justCopied = historyCopyId === sub.id;
                                                return (
                                                    <tr key={sub.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                        <td className="px-2 py-2.5">
                                                            <p className="font-semibold text-slate-800 dark:text-white truncate max-w-[180px]">{sub.email || '—'}</p>
                                                            <p className="text-[10px] text-slate-400 font-mono truncate max-w-[180px]">{sub.uid}</p>
                                                        </td>
                                                        <td className="px-2 py-2.5">
                                                            {isRenewal ? (
                                                                <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold whitespace-nowrap">
                                                                    🔄 Renewal
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold whitespace-nowrap">
                                                                    🆕 Baru
                                                                </span>
                                                            )}
                                                            {sub.flowAddon && (
                                                                <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-bold whitespace-nowrap" title="Termasuk add-on Flow Ultra (Sharing) RM49">
                                                                    🎁 Flow
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-2.5">
                                                            {isActivated ? (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                                                                    <Check size={10} strokeWidth={3} />
                                                                    ACTIVATED
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold">
                                                                    <XCircle size={10} />
                                                                    REJECTED
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-2.5">
                                                            {sub.licenseKey ? (
                                                                <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">{sub.licenseKey}</span>
                                                            ) : (
                                                                <span className="text-slate-300">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-2.5 text-slate-500 whitespace-nowrap">{formatHistoryDate(sub)}</td>
                                                        <td className="px-2 py-2.5 text-right">
                                                            {isActivated && sub.licenseKey ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleCopyHistoryRow(sub)}
                                                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition ${
                                                                        justCopied
                                                                            ? 'bg-emerald-600 text-white'
                                                                            : 'bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white'
                                                                    }`}
                                                                >
                                                                    {justCopied ? (
                                                                        <><Check size={11} strokeWidth={3} /> Disalin</>
                                                                    ) : (
                                                                        <><Copy size={11} /> Copy Ayat</>
                                                                    )}
                                                                </button>
                                                            ) : (
                                                                <span className="text-[10px] text-slate-300">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {historyHasMore && (
                                        <div className="flex justify-center mt-3">
                                            <button
                                                onClick={() => setHistoryLimit(n => n + 50)}
                                                disabled={submissionsLoading}
                                                className="px-4 py-1.5 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                                            >
                                                Load 50 lagi (semasa: {submissionsHistory.length})
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeAdminTab === 'email' && (
                    <EmailBlastTab />
                )}

                {activeAdminTab === 'flow' && (
                    <FlowUltraTab canManageSessions={hasPermission(userRole, PERMISSIONS.EDIT_CONFIG)} />
                )}

                {activeAdminTab === 'fbhealth' && (
                    <FbHealthTab />
                )}

                {activeAdminTab === 'robot' && (
                    <RobotTetapanTab />
                )}
                {activeAdminTab === 'flow-session-pilot' && hasPermission(userRole, PERMISSIONS.EDIT_CONFIG) && <FlowUltraTab canManageSessions initialSection="session-access" />}

                {activeAdminTab === 'kempen' && (
                    <KempenRekodView />
                )}


            </div>

            {/* ═══ CAMPAIGN MANAGER MODAL ═══ */}
            {showCampaignManager && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[85vh]">
                        <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-xl">
                                <Tag size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-slate-800 dark:text-white">Urus Campaign</h3>
                                <p className="text-xs text-slate-500">Tagging pengguna untuk grouping / marketing attribution</p>
                            </div>
                            <button onClick={() => { setShowCampaignManager(false); setEditingCampaign(null); }} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-5 flex-1 overflow-y-auto space-y-4">
                            {/* Existing list */}
                            {campaigns.length === 0 ? (
                                <p className="text-center text-sm text-slate-400 py-6">Belum ada campaign. Cipta yang pertama di bawah.</p>
                            ) : (
                                <div className="space-y-2">
                                    {campaigns.map(c => {
                                        const userCount = users.filter(u => u.campaign === c.id).length;
                                        const isEditing = editingCampaign?.id === c.id;
                                        return (
                                            <div key={c.id} className="border border-slate-200 dark:border-slate-700 rounded-xl p-3 bg-white dark:bg-slate-800/30">
                                                {isEditing ? (
                                                    <div className="space-y-2">
                                                        <input
                                                            type="text"
                                                            value={editingCampaign.name}
                                                            onChange={e => setEditingCampaign(prev => ({ ...prev, name: e.target.value }))}
                                                            placeholder="Nama campaign"
                                                            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-purple-500/30 outline-none"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={editingCampaign.description || ''}
                                                            onChange={e => setEditingCampaign(prev => ({ ...prev, description: e.target.value }))}
                                                            placeholder="Description (optional)"
                                                            className="w-full px-3 py-2 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-purple-500/30 outline-none"
                                                        />
                                                        <div className="flex gap-2 justify-end">
                                                            <button onClick={() => setEditingCampaign(null)} className="px-3 py-1 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Batal</button>
                                                            <button onClick={handleSaveCampaign} className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg flex items-center gap-1"><Save size={12} /> Simpan</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{c.name}</p>
                                                            {c.description && <p className="text-[11px] text-slate-500 truncate">{c.description}</p>}
                                                            <p className="text-[10px] text-slate-400 mt-0.5">{userCount} pengguna</p>
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                setTargetCampaignForModules({ id: c.id, name: c.name });
                                                                // Pre-fill ikut preset existing campaign (kalau ada)
                                                                setCampaignModulesPick(
                                                                    Array.isArray(c.defaultAllowedModules) && c.defaultAllowedModules.length > 0
                                                                        ? [...c.defaultAllowedModules]
                                                                        : ['ai8saat']
                                                                );
                                                                setCampaignPresetDuration(
                                                                    Number.isInteger(c.defaultDurationDays) ? c.defaultDurationDays : 30
                                                                );
                                                                setCampaignPresetMaxProfiles(
                                                                    Number.isInteger(c.defaultMaxProfiles) ? c.defaultMaxProfiles : 3
                                                                );
                                                                setCampaignPresetApplyExisting(false);
                                                                setShowSetCampaignModulesModal(true);
                                                            }}
                                                            className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                                                            title="Edit preset campaign (default modul + duration + maxProfiles)"
                                                        >
                                                            <Settings size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingCampaign({ id: c.id, name: c.name, description: c.description || '' })}
                                                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                                                            title="Edit"
                                                        >
                                                            <Edit3 size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteCampaign(c.id, c.name)}
                                                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors"
                                                            title="Padam"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Add new */}
                            {editingCampaign && !editingCampaign.id ? (
                                <div className="border border-dashed border-purple-300 dark:border-purple-700 rounded-xl p-3 bg-purple-50/50 dark:bg-purple-900/10 space-y-2">
                                    <input
                                        type="text"
                                        value={editingCampaign.name}
                                        onChange={e => setEditingCampaign(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Nama campaign (cth: FB-Ads-April-2026)"
                                        autoFocus
                                        className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-purple-500/30 outline-none"
                                    />
                                    <input
                                        type="text"
                                        value={editingCampaign.description || ''}
                                        onChange={e => setEditingCampaign(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Description (optional)"
                                        className="w-full px-3 py-2 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-purple-500/30 outline-none"
                                    />
                                    <div className="flex gap-2 justify-end">
                                        <button onClick={() => setEditingCampaign(null)} className="px-3 py-1 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Batal</button>
                                        <button onClick={handleSaveCampaign} className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg flex items-center gap-1"><Plus size={12} /> Cipta</button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setEditingCampaign({ name: '', description: '' })}
                                    className="w-full border border-dashed border-slate-300 dark:border-slate-600 hover:border-purple-400 dark:hover:border-purple-600 text-slate-500 hover:text-purple-600 dark:hover:text-purple-400 text-sm font-medium py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                                >
                                    <Plus size={16} /> Tambah Campaign Baru
                                </button>
                            )}
                        </div>

                        <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-end">
                            <button onClick={() => { setShowCampaignManager(false); setEditingCampaign(null); }} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl transition-colors">Tutup</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ EDIT CAMPAIGN PRESET MODAL ═══ */}
            {showSetCampaignModulesModal && targetCampaignForModules && (() => {
                // Eligible counts for "Apply juga ke user existing" preview
                const inCampaign = users.filter(u => u.campaign === targetCampaignForModules.id && u.tier === 'paid');
                const activeCount = inCampaign.filter(u => {
                    if (!u.license?.expiresAt) return false;
                    const exp = new Date(u.license.expiresAt);
                    return exp > new Date();
                }).length;
                const expiredCount = inCampaign.length - activeCount;
                return (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !campaignModulesSaving && setShowSetCampaignModulesModal(false)}>
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl">
                                    <Settings size={20} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-slate-800 dark:text-white truncate">Edit Preset Campaign</h3>
                                    <p className="text-xs text-slate-500 truncate">📌 {targetCampaignForModules.name}</p>
                                </div>
                                <button onClick={() => !campaignModulesSaving && setShowSetCampaignModulesModal(false)} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="p-5 flex-1 overflow-y-auto space-y-4">
                                {/* Info note */}
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-3 text-xs text-blue-800 dark:text-blue-300">
                                    Preset ini akan auto-isi modal Aktifkan bila admin pilih campaign ni untuk user baru.
                                </div>

                                {/* Module checklist */}
                                <div>
                                    <h4 className="font-semibold text-slate-800 dark:text-white mb-2 text-sm flex items-center gap-2">
                                        Default Modul Akses
                                        <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 px-2 py-0.5 rounded-full">{campaignModulesPick.length} dipilih</span>
                                    </h4>
                                    <div className="grid grid-cols-2 gap-2">
                                        {modulesList.map(mod => (
                                            <label key={mod.id} className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-all ${
                                                campaignModulesPick.includes(mod.id)
                                                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/50'
                                                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:border-emerald-300 dark:hover:border-emerald-700'
                                            }`}>
                                                <input
                                                    type="checkbox"
                                                    className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                                    checked={campaignModulesPick.includes(mod.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setCampaignModulesPick(prev => [...prev, mod.id]);
                                                        } else {
                                                            setCampaignModulesPick(prev => prev.filter(id => id !== mod.id));
                                                        }
                                                    }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-xs font-bold truncate ${campaignModulesPick.includes(mod.id) ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-300'}`}>
                                                        {mod.marketingName || mod.name || mod.id}
                                                    </p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Duration + maxProfiles */}
                                <div>
                                    <h4 className="font-semibold text-slate-800 dark:text-white mb-2 text-sm">Setting Default</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">Tempoh license (hari)</label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={365}
                                                value={campaignPresetDuration}
                                                onChange={(e) => {
                                                    const v = parseInt(e.target.value, 10);
                                                    setCampaignPresetDuration(Number.isInteger(v) && v > 0 ? v : 30);
                                                }}
                                                disabled={campaignModulesSaving}
                                                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-white disabled:opacity-50"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">Max profile slot</label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={10}
                                                value={campaignPresetMaxProfiles}
                                                onChange={(e) => {
                                                    const v = parseInt(e.target.value, 10);
                                                    setCampaignPresetMaxProfiles(Number.isInteger(v) && v > 0 ? v : 3);
                                                }}
                                                disabled={campaignModulesSaving}
                                                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-white disabled:opacity-50"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Apply to existing checkbox */}
                                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                                    <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                        campaignPresetApplyExisting
                                            ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'
                                            : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 hover:border-amber-300'
                                    }`}>
                                        <input
                                            type="checkbox"
                                            className="mt-0.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                            checked={campaignPresetApplyExisting}
                                            onChange={(e) => setCampaignPresetApplyExisting(e.target.checked)}
                                            disabled={campaignModulesSaving || activeCount === 0}
                                        />
                                        <div className="flex-1">
                                            <p className="text-xs font-bold text-slate-800 dark:text-white">
                                                Apply juga ke {activeCount} user aktif dalam campaign ni
                                            </p>
                                            {expiredCount > 0 && (
                                                <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-1">
                                                    ⚠️ {expiredCount} user expired akan di-skip (mereka kena renew + diaktifkan balik secara manual)
                                                </p>
                                            )}
                                            {activeCount === 0 && (
                                                <p className="text-[10px] text-slate-500 mt-1">
                                                    Takda user aktif dalam campaign ni — preset akan apply ke user baru sahaja
                                                </p>
                                            )}
                                            <p className="text-[10px] text-slate-500 mt-1">
                                                Hanya <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">allowedModules</code> + <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">maxProfiles</code> di-update. <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">durationDays</code> tidak (sebab license dah ada tarikh tamat tetap).
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-2">
                                <button
                                    onClick={() => !campaignModulesSaving && setShowSetCampaignModulesModal(false)}
                                    disabled={campaignModulesSaving}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors disabled:opacity-50"
                                >
                                    Batal
                                </button>
                                <button
                                    onClick={handleSaveCampaignPreset}
                                    disabled={campaignModulesSaving || campaignModulesPick.length === 0}
                                    className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-xl transition-all shadow-md shadow-emerald-200 dark:shadow-none flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {campaignModulesSaving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                                    {campaignModulesSaving ? 'Menyimpan...' : 'Simpan Preset'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ═══ BULK ASSIGN CAMPAIGN MODAL ═══ */}
            {showBulkAssignCampaign && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                        <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-xl">
                                <Tag size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-800 dark:text-white">Bulk Assign Campaign</h3>
                                <p className="text-xs text-slate-500">{selectedUsers.size} pengguna dipilih</p>
                            </div>
                            <button onClick={() => setShowBulkAssignCampaign(false)} className="ml-auto p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-5 space-y-3">
                            <label className="block text-xs font-bold text-slate-500 uppercase">Pilih Campaign</label>
                            <select
                                value={bulkCampaignId}
                                onChange={e => setBulkCampaignId(e.target.value)}
                                className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-purple-500/30 outline-none"
                            >
                                <option value="">— None (kosongkan tag) —</option>
                                {campaigns.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                            {campaigns.length === 0 && (
                                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                    Tiada campaign lagi. Tutup modal ni, klik "Campaigns" di atas untuk cipta.
                                </p>
                            )}
                            <p className="text-[11px] text-slate-500 leading-relaxed">
                                Campaign hanya untuk marking / grouping. Tiada kesan pada akses, license, atau billing.
                            </p>
                        </div>

                        <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-2">
                            <button onClick={() => setShowBulkAssignCampaign(false)} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors">Batal</button>
                            <button
                                onClick={handleBulkAssignCampaign}
                                className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-xl transition-all shadow-md flex items-center gap-2"
                            >
                                <Save size={16} /> Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ BULK EDIT MODULES MODAL ═══ */}
            {showBulkEditModules && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                        <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl">
                                <Edit3 size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-800 dark:text-white">Bulk Edit Modules</h3>
                                <p className="text-xs text-slate-500">{selectedUsers.size} pengguna dipilih</p>
                            </div>
                            <button onClick={() => setShowBulkEditModules(false)} className="ml-auto p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-5 flex-1 overflow-y-auto">
                            <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl p-3 flex gap-3 text-amber-800 dark:text-amber-400">
                                <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                                <div className="text-sm">
                                    <p className="font-bold mb-1">AMARAN: OVERWRITE</p>
                                    <p className="opacity-90 leading-relaxed text-xs">Tindakan ini akan <b>menggantikan (overwrite)</b> keseluruhan akses modul asal bagi senarai {selectedUsers.size} pengguna terpilih dengan pilihan terbaharu di bawah.</p>
                                </div>
                            </div>
                            
                            <h4 className="font-semibold text-slate-800 dark:text-white mb-3 flex items-center gap-2">Pilih Modul Dibenarkan <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 px-2 py-0.5 rounded-full">{bulkAllowedModules.length}</span></h4>
                            <div className="grid grid-cols-2 gap-2">
                                {modulesList.map(mod => (
                                    <label key={mod.id} className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-all ${
                                        bulkAllowedModules.includes(mod.id) 
                                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 dark:border-indigo-500/50' 
                                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:border-indigo-300 dark:hover:border-indigo-700'
                                    }`}>
                                        <input 
                                            type="checkbox"
                                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                            checked={bulkAllowedModules.includes(mod.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setBulkAllowedModules(prev => [...prev, mod.id]);
                                                } else {
                                                    setBulkAllowedModules(prev => prev.filter(id => id !== mod.id));
                                                }
                                            }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-xs font-bold truncate ${bulkAllowedModules.includes(mod.id) ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-slate-300'}`}>{mod.name}</p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-2">
                            <button onClick={() => setShowBulkEditModules(false)} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors">Batal</button>
                            <button 
                                onClick={handleBulkUpdateModules} 
                                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-all shadow-md shadow-indigo-200 dark:shadow-none flex items-center gap-2"
                            >
                                <Save size={16} /> Simpan
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ EDIT WHATSAPP MODAL ═══ */}
            {editingWhatsappUser && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => !savingWhatsapp && setEditingWhatsappUser(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm border border-slate-200 dark:border-slate-800 overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-5 py-3 text-white flex items-center gap-2">
                            <Edit3 size={16} />
                            <h3 className="font-bold text-base">Edit WhatsApp</h3>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Email <span className="text-slate-300 normal-case font-normal">(tak boleh edit — guna butang Auth)</span></label>
                                <input
                                    type="text"
                                    value={editingWhatsappUser.email}
                                    readOnly
                                    className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg text-xs bg-slate-50 dark:bg-slate-800 text-slate-500 font-mono cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">UID <span className="text-slate-300 normal-case font-normal">(permanent, tak boleh ubah)</span></label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={editingWhatsappUser.uid}
                                        readOnly
                                        className="flex-1 p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg text-xs bg-slate-50 dark:bg-slate-800 text-slate-500 font-mono cursor-not-allowed"
                                    />
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(editingWhatsappUser.uid); }}
                                        className="px-3 rounded-lg text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                        title="Salin UID"
                                    >
                                        <Copy size={14} />
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase mb-1">No. WhatsApp</label>
                                <input
                                    type="tel"
                                    autoFocus
                                    placeholder="+60123456789"
                                    defaultValue={editingWhatsappUser.whatsappNumber || ''}
                                    onChange={e => setEditingWhatsappUser(prev => ({ ...prev, _new: e.target.value }))}
                                    onKeyDown={e => { if (e.key === 'Enter' && !savingWhatsapp) handleSaveWhatsapp(); }}
                                    className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-emerald-500 outline-none font-mono"
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Kosongkan untuk buang nombor. Format: +60xxxxxxxxx.</p>
                            </div>
                        </div>
                        <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700 flex gap-2 justify-end">
                            <button onClick={() => setEditingWhatsappUser(null)} disabled={savingWhatsapp} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50">Batal</button>
                            <button onClick={handleSaveWhatsapp} disabled={savingWhatsapp} className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg text-sm font-bold hover:brightness-110 transition-all shadow-md disabled:opacity-50 flex items-center gap-1.5">
                                {savingWhatsapp && <RefreshCw size={12} className="animate-spin" />}
                                Simpan
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ BACKFILL SCAN — INVESTIGATION TOOL MODAL ═══ */}
            {backfillPreview && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setBackfillPreview(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-slate-200 dark:border-slate-800 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 text-white">
                            <h3 className="font-bold text-lg flex items-center gap-2"><Search size={16} /> Missing Referrals — Investigation Tool</h3>
                            <p className="text-emerald-100 text-xs mt-0.5">
                                Era affiliate (sejak {backfillPreview.affiliateLaunch ? new Date(backfillPreview.affiliateLaunch).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}) • {backfillPreview.totalCandidates} paid user tiada referredBy
                            </p>
                        </div>
                        <div className="p-6 space-y-4 overflow-y-auto flex-1">
                            {/* Summary stats */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                    <p className="text-2xl font-black text-amber-700 dark:text-amber-300">{backfillPreview.withClicksCount} <span className="text-xs font-bold uppercase">klien perlu review</span></p>
                                    <p className="text-[10px] text-amber-700/70 dark:text-amber-300/70 mt-0.5">Ada klik affiliate dalam 7 hari sebelum klien bayar</p>
                                </div>
                                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                                    <p className="text-2xl font-black text-slate-700 dark:text-slate-300">{backfillPreview.noClicksCount} <span className="text-xs font-bold uppercase">bukan affiliate</span></p>
                                    <p className="text-[10px] text-slate-500 mt-0.5">Tiada klik affiliate — kemungkinan organic/direct signup</p>
                                </div>
                            </div>

                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed space-y-1.5">
                                <p><strong>Cara guna:</strong> Klien era affiliate yang masih ada cookie akan AUTO-ATTRIBUTE bila login next. Untuk yang tinggal di sini, klik mana-mana badge ejen → buka modal pencil edit pre-filled. Verify dengan ejen kalau ada complain.</p>
                                <p><strong>⚠ Penting:</strong> Klik count tu adalah klik <strong>dari semua pengunjung internet</strong> dalam window 7 hari, BUKAN klik klien ini sahaja. Sistem `referral_clicks` tak rekod siapa yang klik. Jadi anggap ni "calon" sahaja, bukan bukti.</p>
                                <p><strong>Sort:</strong> Senarai disusun ikut <strong>klik pertama paling awal</strong> (first-touch attribution priority). Badge merah = self-referral (block).</p>
                            </div>

                            {/* With clicks list */}
                            {backfillPreview.withClicks?.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold uppercase text-amber-700 dark:text-amber-300 mb-2">Klien perlu review ({backfillPreview.withClicks.length})</h4>
                                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                        <table className="w-full text-xs">
                                            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500">
                                                <tr>
                                                    <th className="text-left px-3 py-2 font-medium w-[200px]">Klien</th>
                                                    <th className="text-left px-3 py-2 font-medium">Calon ejen (klik untuk attribute)</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                {backfillPreview.withClicks.map(r => (
                                                    <tr key={r.uid}>
                                                        <td className="px-3 py-2 align-top">
                                                            <div className="truncate max-w-[200px] font-medium" title={r.email}>{r.email || r.uid.slice(0, 8)}</div>
                                                            <div className="text-[10px] text-slate-400 mt-0.5">
                                                                Bayar: {r.paidAt ? new Date(r.paidAt).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short' }) : '—'}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <div className="flex flex-wrap gap-1">
                                                                {Array.isArray(r.candidates) && r.candidates.map((c, i) => (
                                                                    <button
                                                                        key={i}
                                                                        onClick={() => {
                                                                            if (c.isSelfRef) {
                                                                                alert('Self-referral diblok — ejen tak boleh refer dirinya sendiri.');
                                                                                return;
                                                                            }
                                                                            // Keep investigation modal open underneath — pencil edit overlays via render order
                                                                            setEditingReferral({ uid: r.uid, email: r.email, currentRefCode: '' });
                                                                            setReferralInput(c.refCode);
                                                                            setReferralError('');
                                                                        }}
                                                                        title={c.isSelfRef ? 'Self-referral — tak boleh attribute' : `Attribute ke ${c.agentEmail || c.refCode}\nKlik pertama: ${c.firstClickAt ? new Date(c.firstClickAt).toLocaleString('ms-MY') : '-'}\nKlik terakhir: ${c.lastClickAt ? new Date(c.lastClickAt).toLocaleString('ms-MY') : '-'}\nJumlah klik (semua pengunjung): ${c.clickCount}`}
                                                                        className={`px-2 py-1.5 rounded text-[10px] font-bold transition-colors text-left flex flex-col leading-tight ${
                                                                            c.isSelfRef
                                                                                ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 cursor-not-allowed line-through'
                                                                                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-purple-100 hover:text-purple-700 dark:hover:bg-purple-900/30 cursor-pointer'
                                                                        }`}
                                                                    >
                                                                        <span className="font-mono">{c.refCode}{c.agentEmail && <span className="opacity-70 ml-1">{c.agentEmail.split('@')[0]}</span>}</span>
                                                                        <span className="opacity-70 text-[9px] font-normal mt-0.5">
                                                                            klik 1: {c.firstClickAt ? new Date(c.firstClickAt).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short' }) : '-'} • {c.clickCount}x
                                                                        </span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {backfillPreview.totalCandidates === 0 && (
                                <div className="p-6 text-center text-slate-500 text-sm">
                                    Tiada paid user era affiliate yang tiada referredBy. Sistem dah up-to-date 🎉
                                </div>
                            )}

                            {backfillPreview.noClicksCount > 0 && (
                                <p className="text-[10px] text-slate-400 italic">
                                    {backfillPreview.noClicksCount} klien tiada klik affiliate dalam 7 hari sebelum bayar — kemungkinan organik/direct, bukan rujukan ejen. Tak ditunjuk di sini.
                                </p>
                            )}
                        </div>
                        <div className="px-6 py-3 bg-slate-50 dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700 flex justify-end">
                            <button
                                onClick={() => setBackfillPreview(null)}
                                className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                            >
                                Tutup
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ EDIT REFERRAL (EJEN AFF) MODAL — manual fix ═══ */}
            {editingReferral && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => !savingReferral && setEditingReferral(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 text-white">
                            <h3 className="font-bold text-lg flex items-center gap-2"><Edit3 size={16} /> Edit Ejen Aff</h3>
                            <p className="text-purple-100 text-xs truncate mt-0.5">{editingReferral.email || editingReferral.uid}</p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase mb-2">refCode atau custom slug</label>
                                <input
                                    type="text"
                                    autoFocus
                                    className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-purple-500 outline-none font-mono"
                                    placeholder="AFR-XXXXXX atau custom-slug"
                                    value={referralInput}
                                    onChange={(e) => { setReferralInput(e.target.value); setReferralError(''); }}
                                    disabled={savingReferral}
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Sistem akan resolve slug → canonical refCode automatik.</p>
                            </div>
                            {editingReferral.currentRefCode && (
                                <div className="text-[11px] text-slate-500">
                                    Semasa: <span className="font-mono font-bold text-purple-600">{editingReferral.currentRefCode}</span>
                                </div>
                            )}
                            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">
                                <strong>Nota:</strong> Sistem akan set <code className="font-mono">referredBy</code>, create/update referral doc, dan auto-credit komisen kalau user paid (manual approval). Self-referral akan diblok.
                            </div>
                            {referralError && (
                                <div className="p-2.5 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg text-xs text-rose-600 dark:text-rose-400">
                                    {referralError}
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-2">
                            <button
                                onClick={() => setEditingReferral(null)}
                                disabled={savingReferral}
                                className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors disabled:opacity-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={async () => {
                                    const refCode = referralInput.trim();
                                    if (!refCode) { setReferralError('Sila masukkan refCode atau slug.'); return; }
                                    setSavingReferral(true);
                                    setReferralError('');
                                    try {
                                        const fn = httpsCallable(functions, 'adminSetUserReferredBy');
                                        const result = await fn({ uid: editingReferral.uid, refCode });
                                        const d = result.data;
                                        if (d.noOp) {
                                            const noOpMsg = [`Ejen sama (${d.refCode}) — tiada perubahan attribution.`];
                                            if (d.commissionCredited) {
                                                noOpMsg.push(`✅ Komisen retroactive: +RM${(d.commissionAmountSen / 100).toFixed(2)} (sebelum ni terlepas)`);
                                            } else if (d.commissionReason && d.commissionReason !== 'not-paid') {
                                                noOpMsg.push(`Komisen: tak credit (${d.commissionReason})`);
                                            }
                                            alert(noOpMsg.join('\n'));
                                            setEditingReferral(null);
                                            fetchUsers();
                                            return;
                                        }
                                        const msg = [
                                            `✅ ${editingReferral.email || editingReferral.uid} → ${d.refCode}`,
                                            `Ejen baru: ${d.agentEmail || '-'}`,
                                        ];
                                        if (d.oldRefCode) {
                                            msg.push(`Ejen asal: ${d.oldRefCode}`);
                                            if (d.reversedSen > 0) {
                                                msg.push(`Reversed dari ejen asal: -RM${(d.reversedSen / 100).toFixed(2)}`);
                                            }
                                            if (d.paidSenStillOwed > 0) {
                                                msg.push(`⚠ AMARAN: RM${(d.paidSenStillOwed / 100).toFixed(2)} dah dibayar keluar kepada ejen asal — sila claw back manual!`);
                                            }
                                        }
                                        if (d.commissionCredited) {
                                            msg.push(`Komisen ke ejen baru: +RM${(d.commissionAmountSen / 100).toFixed(2)}`);
                                        } else if (d.commissionReason) {
                                            msg.push(`Komisen baru: tak credit (${d.commissionReason})`);
                                        }
                                        alert(msg.join('\n'));
                                        // Remove attributed user from investigation list (if open) so admin sees fresh list
                                        const attributedUid = editingReferral.uid;
                                        setBackfillPreview(prev => {
                                            if (!prev) return prev;
                                            const filtered = (prev.withClicks || []).filter(r => r.uid !== attributedUid);
                                            return {
                                                ...prev,
                                                withClicks: filtered,
                                                withClicksCount: filtered.length,
                                                totalCandidates: Math.max(0, (prev.totalCandidates || 0) - 1)
                                            };
                                        });
                                        setEditingReferral(null);
                                        fetchUsers();
                                    } catch (err) {
                                        setReferralError(err?.message || 'Gagal simpan.');
                                    } finally {
                                        setSavingReferral(false);
                                    }
                                }}
                                disabled={savingReferral || !referralInput.trim()}
                                className="px-5 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl text-sm font-bold hover:brightness-110 transition-all shadow-md disabled:opacity-50"
                            >
                                {savingReferral ? 'Menyimpan...' : 'Simpan'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ EDIT CLIENT MODAL ═══ */}
            {editingUser && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col border border-slate-200 dark:border-slate-800">
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-white">
                            <h3 className="font-bold text-lg">Edit Client</h3>
                            <p className="text-indigo-100 text-xs truncate">{editingUser.email || editingUser.uid}</p>
                        </div>
                        <div className="p-6 space-y-5 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase mb-2">No. WhatsApp</label>
                                <input
                                    type="tel"
                                    className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none font-mono"
                                    placeholder="+60123456789"
                                    defaultValue={editingUser.whatsappNumber || ''}
                                    onChange={e => setEditingUser(prev => ({ ...prev, _newWhatsapp: e.target.value }))}
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Format: +60xxxxxxxxx. Kami guna untuk hantar reminder renewal pakej.</p>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase mb-2">Tarikh Tamat</label>
                                <input type="date" className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                                    value={(() => {
                                        const d = editingUser._newExpiry
                                            ? new Date(editingUser._newExpiry)
                                            : (editingUser.license?.expiresAt ? new Date(editingUser.license.expiresAt) : null);
                                        return d && !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
                                    })()}
                                    onChange={e => {
                                        if (!e.target.value) { setEditingUser(prev => ({ ...prev, _newExpiry: null, _addDays: '' })); return; }
                                        const d = new Date(e.target.value); d.setHours(23, 59, 59);
                                        setEditingUser(prev => ({ ...prev, _newExpiry: d, _addDays: '' }));
                                    }} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase mb-2">Tambah Tempoh (hari)</label>
                                <input type="number" min="1" max="3650"
                                    className="w-32 p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                                    placeholder="cth: 30"
                                    value={editingUser._addDays ?? ''}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        const n = parseInt(raw, 10);
                                        if (!raw || !Number.isInteger(n) || n < 1) {
                                            setEditingUser(prev => ({ ...prev, _addDays: raw, _newExpiry: null }));
                                            return;
                                        }
                                        const cur = editingUser.license?.expiresAt ? new Date(editingUser.license.expiresAt) : null;
                                        const now = new Date();
                                        const base = (cur && !isNaN(cur.getTime()) && cur.getTime() > now.getTime()) ? new Date(cur) : now;
                                        const d = new Date(base);
                                        d.setDate(d.getDate() + n);
                                        d.setHours(23, 59, 59, 0);
                                        setEditingUser(prev => ({ ...prev, _addDays: n, _newExpiry: d }));
                                    }} />
                                <p className="text-[10px] text-slate-400 mt-1">Taip bilangan hari untuk panjangkan lesen. Jika lesen masih aktif, hari ditambah atas tarikh tamat sedia ada; jika sudah tamat, dikira dari hari ini. Tekan Simpan untuk kunci. Tiada bayaran atau komisen direkodkan.</p>
                                {editingUser._newExpiry && Number.isInteger(editingUser._addDays) && (
                                    <p className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 mt-1">
                                        Tarikh tamat baharu: {new Date(editingUser._newExpiry).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase mb-2">Max Profiles</label>
                                <div className="flex gap-2 flex-wrap items-center">
                                    {[3, 5, 10, 20].map(c => (
                                        <button key={c} onClick={() => setEditingUser(prev => ({ ...prev, _newMaxProfiles: c }))}
                                            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border ${(editingUser._newMaxProfiles || editingUser.license?.maxProfiles) === c ? 'bg-purple-600 text-white border-purple-600' : 'bg-white dark:bg-slate-800 text-slate-600 border-slate-200 dark:border-slate-700 hover:border-purple-400'}`}>
                                            {c}
                                        </button>
                                    ))}
                                    <input type="number" min="1"
                                        value={editingUser._newMaxProfiles || editingUser.license?.maxProfiles || 3}
                                        onChange={e => {
                                            const n = parseInt(e.target.value, 10);
                                            setEditingUser(prev => ({ ...prev, _newMaxProfiles: Number.isFinite(n) ? Math.max(1, n) : 1 }));
                                        }}
                                        className="w-24 px-3 py-2 rounded-xl text-xs font-bold border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-center focus:ring-2 focus:ring-purple-500 outline-none"
                                        title="Taip angka sendiri" />
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1">Klik preset, atau taip angka sendiri (minimum 1).</p>
                            </div>
                            {/* SLOT TAMBAHAN — polisi 2026-09-15: kuota selepas renew/naik pakej = default pakej + slot tambahan
                                (utils/kuotaPakej.js). ⛔ Slot yang diberi di luar pakej WAJIB direkod di sini — kalau hanya
                                Max Profiles dinaikkan, bayaran seterusnya menulis default pakej + slotTambahan dan slot itu HILANG. */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase mb-2">Slot Tambahan</label>
                                {(() => {
                                    const direkod = bacaSlotTambahan(editingUser.license);
                                    const slot = editingUser._newSlotTambahan !== undefined ? editingUser._newSlotTambahan : direkod;
                                    const jumlah = editingUser._newMaxProfiles || editingUser.license?.maxProfiles || 3;
                                    // +1 / −1 ubah slot DAN Max Profiles serentak (beri / tarik balik satu slot).
                                    const langkah = (d) => setEditingUser(prev => {
                                        const s = prev._newSlotTambahan !== undefined ? prev._newSlotTambahan : (bacaSlotTambahan(prev.license) ?? 0);
                                        const j = prev._newMaxProfiles || prev.license?.maxProfiles || 3;
                                        if (s + d < 0 || s + d > HAD_SLOT_TAMBAHAN || j + d < 1) return prev;
                                        return { ...prev, _newSlotTambahan: s + d, _newMaxProfiles: j + d };
                                    });
                                    const kelasButang = 'px-3 py-2 rounded-xl text-xs font-bold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-purple-400';
                                    return (
                                        <>
                                            <div className="flex gap-2 items-center flex-wrap">
                                                <button type="button" onClick={() => langkah(-1)} className={kelasButang} title="Tarik balik satu slot (Max Profiles turun 1)">−1</button>
                                                <input type="number" min="0" max={HAD_SLOT_TAMBAHAN}
                                                    value={slot ?? ''}
                                                    placeholder="—"
                                                    onChange={e => {
                                                        const raw = e.target.value;
                                                        if (raw === '') { setEditingUser(prev => ({ ...prev, _newSlotTambahan: undefined })); return; }
                                                        const n = parseInt(raw, 10);
                                                        if (!Number.isInteger(n)) return;
                                                        setEditingUser(prev => ({ ...prev, _newSlotTambahan: Math.min(HAD_SLOT_TAMBAHAN, Math.max(0, n)) }));
                                                    }}
                                                    className="w-20 px-3 py-2 rounded-xl text-xs font-bold border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-center focus:ring-2 focus:ring-purple-500 outline-none"
                                                    title="Taip terus untuk membetulkan rekod sahaja (Max Profiles tidak berubah)" />
                                                <button type="button" onClick={() => langkah(1)} className={kelasButang} title="Beri satu slot (Max Profiles naik 1)">+1</button>
                                            </div>
                                            <p className="text-[10px] text-slate-400 mt-1">
                                                Slot di atas default pakej (add-on FLOWNITY / slot yang admin bagi). Tekan +1 / −1 untuk beri atau tarik balik slot — Max Profiles ikut berubah. Taip terus hanya untuk membetulkan rekod.
                                            </p>
                                            <p className="text-[10px] text-slate-500 mt-1">
                                                Default pakej ikut kiraan: <span className="font-semibold">{slot !== null ? jumlah - slot : '—'}</span> · Bila renew / naik pakej: kuota = default pakej (Premium 3 · Fighter 6) + slot tambahan.
                                            </p>
                                            {slot === null && (
                                                <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 mt-1">Belum direkod — renew seterusnya ikut peraturan lama (ambil nombor tertinggi).</p>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                            {/* MODULE ACCESS CHECKBOXES */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase mb-2">Akses Module</label>
                                {(() => {
                                    const currentAllowed = editingUser._newAllowedModules ?? editingUser.license?.allowedModules ?? [];
                                    const isWildcard = currentAllowed.includes('*');
                                    return isWildcard ? (
                                        <div className="mb-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg text-[11px] text-emerald-700 dark:text-emerald-300 flex items-center justify-between gap-2">
                                            <span>✓ Wildcard aktif — akses semua modules (kecuali premium addons)</span>
                                            <button
                                                onClick={() => {
                                                    const explicit = modulesList.filter(m => !m.isPremiumAddon).map(m => m.id);
                                                    setEditingUser(prev => ({ ...prev, _newAllowedModules: explicit }));
                                                }}
                                                className="text-[10px] font-bold text-emerald-800 dark:text-emerald-200 underline whitespace-nowrap"
                                            >
                                                Tukar Manual
                                            </button>
                                        </div>
                                    ) : null;
                                })()}
                                <div className="grid grid-cols-2 gap-2">
                                    {modulesList.map(mod => {
                                        const currentAllowed = editingUser._newAllowedModules ?? editingUser.license?.allowedModules ?? [];
                                        const isWildcard = currentAllowed.includes('*');
                                        const isChecked = currentAllowed.includes(mod.id) || (isWildcard && !mod.isPremiumAddon);
                                        const isDisabled = isWildcard && !mod.isPremiumAddon;
                                        return (
                                            <label key={mod.id} className={`flex items-start gap-2 p-2 rounded-xl border transition-colors border-slate-200 dark:border-slate-700 ${isDisabled ? 'opacity-60 cursor-not-allowed bg-slate-50 dark:bg-slate-800/30' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>
                                                <input
                                                    type="checkbox"
                                                    className="mt-0.5 rounded text-indigo-600 border-slate-300 focus:ring-indigo-500"
                                                    checked={isChecked}
                                                    disabled={isDisabled}
                                                    onChange={(e) => {
                                                        const newAllowed = new Set(currentAllowed);
                                                        if (e.target.checked) newAllowed.add(mod.id);
                                                        else newAllowed.delete(mod.id);
                                                        setEditingUser(prev => ({ ...prev, _newAllowedModules: Array.from(newAllowed) }));
                                                    }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{mod.name}</p>
                                                    {mod.description && <p className="text-[9px] text-slate-400 leading-tight mt-0.5" title={mod.description}>{mod.description}</p>}
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                            {/* ★ [2026-09-26] Rekod claim ciri percuma PRO Studio — baca sahaja. */}
                            <AdminRekodCiriPercuma uid={editingUser.uid} />
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase">Active Profiles ({editingUser.license?.activeProfiles?.length || 0})</label>
                                    <button onClick={async () => {
                                        try { await updateDoc(doc(db, 'licenses', editingUser.uid), { activeProfiles: [] }); setEditingUser({ ...editingUser, license: { ...editingUser.license, activeProfiles: [] } }); fetchUsers(); } catch (e) { alert('Gagal reset.'); }
                                    }} className="text-[10px] text-rose-500 hover:text-rose-700 font-bold">Reset All</button>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 max-h-40 overflow-y-auto">
                                    {editingUser.license?.activeProfiles?.length > 0 ? (
                                        <div className="divide-y divide-slate-100 dark:divide-slate-700">
                                            {editingUser.license?.activeProfiles.map((profile, idx) => (
                                                <div key={profile} className="flex items-center justify-between px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span className="text-[10px] font-bold text-slate-300">{idx + 1}</span>
                                                        <span className="text-[11px] font-mono text-slate-600 dark:text-slate-400 truncate">{profile.substring(0, 25)}...</span>
                                                    </div>
                                                    <button onClick={() => handleRemoveProfile(editingUser.uid, profile)} className="p-1 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded transition-colors"><X size={12} /></button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-4 text-center text-slate-400 text-xs italic">Tiada profile aktif</div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700 flex justify-between">
                            <button onClick={() => handleDeleteLicense(editingUser.uid, editingUser.license?.licenseKey)}
                                className="flex items-center gap-1.5 px-4 py-2 text-rose-600 text-sm font-bold hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-colors border border-rose-200 dark:border-rose-800">
                                <Trash2 size={14} /> Padam License
                            </button>
                            <div className="flex gap-2">
                                <button onClick={() => setEditingUser(null)} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-100 rounded-xl transition-colors">Batal</button>
                                <button onClick={() => {
                                    const d = editingUser._newExpiry || (editingUser.license?.expiresAt ? new Date(editingUser.license.expiresAt) : null);
                                    const mp = editingUser._newMaxProfiles || editingUser.license?.maxProfiles;
                                    const am = editingUser._newAllowedModules || editingUser.license?.allowedModules || ['ai8saat'];
                                    const wa = editingUser._newWhatsapp !== undefined ? editingUser._newWhatsapp : editingUser.whatsappNumber;
                                    // Slot tambahan: undefined = tidak diubah. Semak SEBELUM tulis — nilai rosak
                                    // di sini menjadikan kuota renew seterusnya salah (kuotaPakej.js).
                                    const st = editingUser._newSlotTambahan;
                                    if (st !== undefined && (!Number.isInteger(st) || st < 0 || st > HAD_SLOT_TAMBAHAN)) {
                                        alert(`Slot tambahan tidak sah (0–${HAD_SLOT_TAMBAHAN}).`);
                                        return;
                                    }
                                    const stAkhir = st !== undefined ? st : bacaSlotTambahan(editingUser.license);
                                    if (stAkhir !== null && Number.isInteger(mp) && mp < stAkhir + 1) {
                                        alert('Max Profiles mesti sekurang-kurangnya slot tambahan + 1.');
                                        return;
                                    }
                                    if (d) {
                                        handleUpdateClient(editingUser.uid, d, mp, am, wa, st);
                                    } else {
                                        alert('Sila masukkan tarikh tamat.');
                                    }
                                }} className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-sm font-bold hover:brightness-110 transition-all shadow-md">Simpan</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* ═══ AUTH LINKS MODAL ═══ */}
            {authUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                            <div>
                                <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2"><Key size={16} className="text-sky-500" /> Auth Actions</h3>
                                <p className="text-xs text-slate-500 font-mono mt-1">{authUser.email}</p>
                            </div>
                            <button onClick={() => setAuthUser(null)} className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-200 transition-colors"><X size={16} /></button>
                        </div>
                        
                        <div className="p-6 space-y-6">
                            {/* Password Reset */}
                            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 rounded-xl border border-indigo-100 dark:border-indigo-800/30">
                                <h4 className="text-sm font-bold text-indigo-800 dark:text-indigo-300 mb-2 flex items-center gap-1.5"><Key size={14}/> Reset Password</h4>
                                <p className="text-xs text-indigo-600/80 dark:text-indigo-400 mb-3">Jana secure link untuk user tukar password sendiri tanpa perlu terima kod emel Firebase.</p>
                                <button 
                                    onClick={() => handleGenerateAuthLink('password', authUser.email)}
                                    disabled={authLinkLoading}
                                    className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                    {authLinkLoading && authLinkType === 'password' ? <RefreshCw size={14} className="animate-spin" /> : <Link2 size={14} />} Jana Link Reset Password
                                </button>
                                
                                {authLinkResult && authLinkType === 'password' && !authLinkResult.startsWith('Ralat') && (
                                    <div className="mt-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-indigo-200 dark:border-indigo-700 flex items-center gap-2">
                                        <input type="text" readOnly value={authLinkResult} className="flex-1 bg-transparent text-xs font-mono outline-none text-slate-700 dark:text-slate-300" />
                                        <button onClick={() => { navigator.clipboard.writeText(authLinkResult); alert('Disalin!'); }} className="p-1.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 rounded hover:bg-indigo-200 transition-colors"><Copy size={12} /></button>
                                    </div>
                                )}
                            </div>
                            
                            {/* Force Email Change */}
                            <div className="p-4 bg-rose-50 dark:bg-rose-900/10 rounded-xl border border-rose-100 dark:border-rose-800/30">
                                <h4 className="text-sm font-bold text-rose-800 dark:text-rose-300 mb-2 flex items-center gap-1.5"><Mail size={14}/> Paksa Tukar Email</h4>
                                <p className="text-xs text-rose-600/80 dark:text-rose-400 mb-3">Tukar email user ini terus secara rasmi dalam sistem. Sila pastikan email baru adalah betul dan unik.</p>
                                <input 
                                    type="email" 
                                    placeholder="Masukkan email baru..."
                                    value={newEmailInput}
                                    onChange={e => setNewEmailInput(e.target.value)}
                                    className="w-full px-3 py-2 mb-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-rose-500/30"
                                />
                                <button 
                                    onClick={() => handleGenerateAuthLink('email', authUser.email)}
                                    disabled={authLinkLoading || !newEmailInput.includes('@')}
                                    className="w-full py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                    {authLinkLoading && authLinkType === 'email' ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Tukar Email Sekarang
                                </button>
                            </div>
                            
                            {/* Error Alert */}
                            {authLinkResult && authLinkResult.startsWith('Ralat') && (
                                <div className="p-3 bg-red-50 text-red-600 rounded-lg border border-red-200 text-xs font-bold text-center">
                                    {authLinkResult}
                                </div>
                            )}
                            {/* Success Message for Email Change */}
                            {authLinkResult && authLinkType === 'email' && !authLinkResult.startsWith('Ralat') && (
                                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-200 text-xs font-bold text-center">
                                    {authLinkResult}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ DELETE USER CONFIRM MODAL (super-admin only) ═══ */}
            {deleteUserTarget && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={closeDeleteUserModal}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full p-6 border-2 border-rose-300 dark:border-rose-800" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                                <AlertTriangle size={20} className="text-rose-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">Delete User Sepenuhnya</h3>
                                <p className="text-xs text-slate-500">Tindakan ini TIDAK BOLEH di-undo</p>
                            </div>
                        </div>

                        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg p-3 mb-4 text-xs text-rose-700 dark:text-rose-300 space-y-1">
                            <p className="font-bold">Apa yang akan dibuang:</p>
                            <ul className="list-disc list-inside space-y-0.5">
                                <li>Akaun Firebase Auth (email dibebaskan)</li>
                                <li>User doc + products/transcriptions/spaces</li>
                                <li>License, subscription, affiliate profile</li>
                                <li>Referral records (yang melibatkan user)</li>
                                <li>License keys yang diaktifkan akan di-reset</li>
                            </ul>
                            <p className="pt-2 font-bold">Selepas ini, email boleh didaftar semula (guna link aff atau tidak).</p>
                        </div>

                        <div className="mb-4">
                            <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">User:</p>
                            <p className="font-mono text-sm font-bold text-slate-800 dark:text-white break-all">{deleteUserTarget.email}</p>
                            <p className="font-mono text-[10px] text-slate-400 break-all">UID: {deleteUserTarget.uid}</p>
                        </div>

                        <div className="mb-4">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                                Untuk confirm, taip email user di bawah:
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmInput}
                                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                                placeholder={deleteUserTarget.email}
                                disabled={deleteLoading}
                                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-mono bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-rose-400 disabled:opacity-50"
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={closeDeleteUserModal}
                                disabled={deleteLoading}
                                className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleConfirmDeleteUser}
                                disabled={deleteLoading || deleteConfirmInput.trim().toLowerCase() !== (deleteUserTarget.email || '').trim().toLowerCase()}
                                className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {deleteLoading ? (
                                    <><RefreshCw size={14} className="animate-spin" /> Memadam...</>
                                ) : (
                                    <><Trash2 size={14} /> Delete Sepenuhnya</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ Pre-Activate Modal: admin pilih campaign + modul + duration + maxProfiles sebelum aktifkan ═══ */}
            {activatePendingModal && (() => {
                const sub = activatePendingModal.submission;
                const isManualMode = activatePendingModal.mode === 'manual';
                // Loading state: manual mode pakai updatingUser, pending mode pakai processingSubmissionId
                const isProcessing = isManualMode
                    ? updatingUser === sub.uid
                    : processingSubmissionId === sub.id;
                const hasCampaigns = campaigns.length > 0;
                const canSubmit = hasCampaigns
                    && !!activatePendingModal.campaignId
                    && activatePendingModal.allowedModules.length > 0
                    && Number.isInteger(activatePendingModal.priceSen)
                    && activatePendingModal.priceSen >= 100
                    && !isProcessing;
                return (
                    <div
                        className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4"
                        onClick={() => { if (!isProcessing) setActivatePendingModal(null); }}
                    >
                        <div
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                                        {sub.type === 'renewal' ? 'Renew Premium' : 'Aktifkan Premium'}
                                    </h3>
                                    {sub.type === 'renewal' && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold">
                                            🔄 RENEWAL
                                        </span>
                                    )}
                                    {sub.flowAddon && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-bold">
                                            🎁 +FLOW
                                        </span>
                                    )}
                                    {isManualMode && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold">
                                            MANUAL
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActivatePendingModal(null)}
                                    disabled={isProcessing}
                                    className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
                                >
                                    <X size={18} className="text-slate-500" />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-5 space-y-4">
                                {/* User summary */}
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                                    <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">
                                        {sub.email || '—'}
                                    </p>
                                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1 flex-wrap">
                                        <span>
                                            RM{((sub.amount || 19900) / 100).toFixed(2)}
                                            {sub.flowAddon && (
                                                <span className="ml-1 text-amber-600 dark:text-amber-400">(RM{((sub.baseSen || 19900) / 100).toFixed(0)} + RM{((sub.flowAddonSen || 4900) / 100).toFixed(0)} Flow)</span>
                                            )}
                                        </span>
                                        {sub.submittedAt && (
                                            <>
                                                <span>·</span>
                                                <span>Hantar: {formatSubmissionDate(sub.submittedAt)}</span>
                                            </>
                                        )}
                                        {isManualMode && (
                                            <>
                                                <span>·</span>
                                                <span className="text-purple-600 dark:text-purple-400 font-semibold">Aktifkan tanpa submission</span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Flow add-on provisioning reminder */}
                                {sub.flowAddon && (
                                    <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3">
                                        <p className="text-sm font-bold text-amber-800 dark:text-amber-300">
                                            🎁 User beli add-on Flow Ultra (Sharing) — RM{((sub.flowAddonSen || 4900) / 100).toFixed(0)}
                                        </p>
                                        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                                            Selepas aktifkan, beri akses akaun Flow (Sharing) kepada user secara manual via WhatsApp.
                                        </p>
                                    </div>
                                )}

                                {/* Empty campaigns guard */}
                                {!hasCampaigns ? (
                                    <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-4">
                                        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                                            ⚠️ Belum ada campaign.
                                        </p>
                                        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                                            Anda mesti cipta sekurang-kurangnya 1 campaign sebelum boleh Aktifkan user. Setiap user mesti ada tag campaign untuk segmentation.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setActivatePendingModal(null);
                                                setShowCampaignManager(true);
                                            }}
                                            className="mt-3 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold"
                                        >
                                            Buka Campaign Manager
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        {/* Pakej — tentukan harga + tempoh + profil + komisen affiliate */}
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                                Pakej <span className="text-rose-500">*</span>
                                            </label>
                                            <select
                                                value={activatePendingModal.packageId}
                                                onChange={(e) => handleSelectPackage(e.target.value)}
                                                disabled={isProcessing}
                                                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                                            >
                                                {ACTIVATION_PACKAGES.map(p => (
                                                    <option key={p.id} value={p.id}>{p.label}</option>
                                                ))}
                                            </select>
                                            {activatePendingModal.packageId === 'custom' && (
                                                <div className="mt-2">
                                                    <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">
                                                        Harga sebenar dibayar
                                                    </label>
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-sm text-slate-500">RM</span>
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            step="0.01"
                                                            value={activatePendingModal.customPriceRM}
                                                            onChange={(e) => handleCustomPriceChange(e.target.value)}
                                                            disabled={isProcessing}
                                                            placeholder="cth 168.00"
                                                            className="w-32 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-white disabled:opacity-50"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            <p className="text-[10px] text-slate-500 mt-1">
                                                {Number.isInteger(activatePendingModal.priceSen) && activatePendingModal.priceSen >= 100 ? (
                                                    <>Harga: <span className="font-semibold text-slate-700 dark:text-slate-300">RM{(activatePendingModal.priceSen / 100).toFixed(2)}</span> · {activatePendingModal.durationDays} hari · {activatePendingModal.maxProfiles} profil{activatePendingModal.kuotaNota ? ` (${activatePendingModal.kuotaNota})` : ''} · komisen ejen ikut harga ni</>
                                                ) : (
                                                    <span className="text-rose-500">Sila isi harga custom yang sah.</span>
                                                )}
                                            </p>
                                        </div>

                                        {/* Campaign dropdown */}
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                                Pilih Campaign <span className="text-rose-500">*</span>
                                            </label>
                                            <select
                                                value={activatePendingModal.campaignId}
                                                onChange={(e) => handleSelectCampaign(e.target.value)}
                                                disabled={isProcessing}
                                                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                                            >
                                                <option value="">— Pilih Campaign —</option>
                                                {campaigns.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                            {activatePendingModal.campaignId && (() => {
                                                const c = campaigns.find(x => x.id === activatePendingModal.campaignId);
                                                const hasPreset = c && (
                                                    (Array.isArray(c.defaultAllowedModules) && c.defaultAllowedModules.length > 0)
                                                    || Number.isInteger(c.defaultDurationDays)
                                                    || Number.isInteger(c.defaultMaxProfiles)
                                                );
                                                return (
                                                    <p className="text-[10px] text-slate-500 mt-1">
                                                        {hasPreset
                                                            ? '✓ Preset campaign telah auto-isi modul/setting di bawah'
                                                            : 'Campaign ini takda preset — sila set modul/setting manual di bawah'}
                                                    </p>
                                                );
                                            })()}
                                        </div>

                                        {/* Module checklist */}
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                                Modul Akses <span className="text-rose-500">*</span>
                                            </label>
                                            <div className="space-y-1.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                                                {modulesList.length === 0 ? (
                                                    <p className="text-xs text-slate-500">Memuat modul...</p>
                                                ) : (
                                                    modulesList.map(mod => {
                                                        const checked = activatePendingModal.allowedModules.includes(mod.id);
                                                        return (
                                                            <label key={mod.id} className="flex items-center gap-2 cursor-pointer hover:bg-white dark:hover:bg-slate-800 rounded px-1.5 py-1">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={() => handleToggleActivateModule(mod.id)}
                                                                    disabled={isProcessing}
                                                                    className="rounded text-emerald-600 focus:ring-emerald-500"
                                                                />
                                                                <span className="text-sm text-slate-700 dark:text-slate-200 flex-1">
                                                                    {mod.name || mod.marketingName || mod.id}
                                                                </span>
                                                                {mod.badge && (
                                                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700">
                                                                        {mod.badge}
                                                                    </span>
                                                                )}
                                                            </label>
                                                        );
                                                    })
                                                )}
                                            </div>
                                            <p className="text-[10px] text-slate-500 mt-1">
                                                {activatePendingModal.allowedModules.length} modul dipilih
                                            </p>
                                        </div>

                                        {/* Advanced settings collapse */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => setActivatePendingModal(prev => ({ ...prev, showAdvanced: !prev.showAdvanced }))}
                                                className="flex items-center gap-1 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                                            >
                                                <span>{activatePendingModal.showAdvanced ? '▼' : '▶'}</span>
                                                <span>Setting Lanjutan</span>
                                            </button>
                                            {activatePendingModal.showAdvanced && (
                                                <div className="mt-2 space-y-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                                            Tempoh license (hari)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={365}
                                                            value={activatePendingModal.durationDays}
                                                            onChange={(e) => {
                                                                const v = parseInt(e.target.value, 10);
                                                                setActivatePendingModal(prev => ({ ...prev, durationDays: Number.isInteger(v) && v > 0 ? v : 30 }));
                                                            }}
                                                            disabled={isProcessing}
                                                            className="w-24 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-white disabled:opacity-50"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                                            Max profile slot
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={HAD_KUOTA}
                                                            value={activatePendingModal.maxProfiles}
                                                            onChange={(e) => {
                                                                const v = parseInt(e.target.value, 10);
                                                                setActivatePendingModal(prev => ({ ...prev, maxProfiles: Number.isInteger(v) && v > 0 ? v : 3, kuotaNota: 'diubah manual' }));
                                                            }}
                                                            disabled={isProcessing}
                                                            className="w-24 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-white disabled:opacity-50"
                                                        />
                                                        <p className="text-[10px] text-slate-500 mt-1">
                                                            Diisi automatik: default pakej + slot tambahan. Nombor yang diubah di sini untuk kelulusan ini sahaja — slot tambahan kekal diurus di Edit Client.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200 dark:border-slate-700">
                                <button
                                    type="button"
                                    onClick={() => setActivatePendingModal(null)}
                                    disabled={isProcessing}
                                    className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
                                >
                                    Batal
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmActivate}
                                    disabled={!canSubmit}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white text-sm font-bold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isProcessing ? (
                                        <>
                                            <Loader2 size={14} className="animate-spin" />
                                            Memproses...
                                        </>
                                    ) : (
                                        <>
                                            <Check size={14} strokeWidth={3} />
                                            {Number.isInteger(activatePendingModal.priceSen) && activatePendingModal.priceSen >= 100
                                                ? `Aktifkan (RM${(activatePendingModal.priceSen / 100).toFixed(2)})`
                                                : 'Aktifkan'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ═══ Reminder Modal: copy nombor + ayat, hantar email / wasap utk row Expiring Soon ═══ */}
            {reminderModal && (() => {
                const item = reminderModal.item;
                const sending = sendingReminderUid === item.uid;
                const dayLeft = Math.max(item.daysLeft, 0);
                const isExpired = item.daysLeft < 0;
                const normalizedNum = normalizeWaNumber(item.whatsappNumber);
                const reminderText = buildRenewalReminderMessage({
                    email: item.email,
                    expiresAt: item.expiresAt,
                    daysLeft: isExpired ? 0 : dayLeft
                });
                const waLink = buildRenewalReminderDeeplink({
                    userWhatsapp: item.whatsappNumber,
                    email: item.email,
                    expiresAt: item.expiresAt,
                    daysLeft: isExpired ? 0 : dayLeft
                });

                const copyToClipboard = async (text, type) => {
                    try {
                        if (navigator.clipboard && window.isSecureContext) {
                            await navigator.clipboard.writeText(text);
                        } else {
                            const ta = document.createElement('textarea');
                            ta.value = text;
                            ta.style.position = 'fixed';
                            ta.style.opacity = '0';
                            document.body.appendChild(ta);
                            ta.focus();
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                        }
                        setReminderModal(prev => prev ? { ...prev, copyState: type } : prev);
                        setTimeout(() => {
                            setReminderModal(prev => prev ? { ...prev, copyState: 'idle' } : prev);
                        }, 2000);
                    } catch (err) {
                        console.error('[Reminder] copy failed:', err);
                        window.prompt(`Copy manual:`, text);
                    }
                };

                return (
                    <div
                        className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4"
                        onClick={() => setReminderModal(null)}
                    >
                        <div
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center">
                                        <MessageCircle size={18} className="text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-800 dark:text-white">Hantar Reminder</h3>
                                        <p className="text-[11px] text-slate-500">Copy nombor + ayat, atau hantar terus</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setReminderModal(null)}
                                    className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                                >
                                    <X size={18} className="text-slate-500" />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-5 space-y-4">
                                {/* User summary */}
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                                    <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">
                                        📧 {item.email}
                                    </p>
                                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1 flex-wrap">
                                        <span>📅 Tamat: {item.expiresAt.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                        <span>·</span>
                                        <span className={isExpired ? 'text-rose-600 font-semibold' : (dayLeft <= 1 ? 'text-rose-600 font-semibold' : 'text-amber-600 font-semibold')}>
                                            {isExpired ? `Dah tamat ${Math.abs(item.daysLeft)}d` : `${dayLeft} hari lagi`}
                                        </span>
                                    </div>
                                </div>

                                {/* Nombor WhatsApp */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                        Nombor WhatsApp
                                    </label>
                                    {normalizedNum ? (
                                        <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                                            <span className="font-mono text-sm text-slate-800 dark:text-white flex-1">
                                                {normalizedNum}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => copyToClipboard(normalizedNum, 'number')}
                                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                                                    reminderModal.copyState === 'number'
                                                        ? 'bg-emerald-600 text-white'
                                                        : 'bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200'
                                                }`}
                                            >
                                                {reminderModal.copyState === 'number' ? (
                                                    <><Check size={12} strokeWidth={3} /> Disalin</>
                                                ) : (
                                                    <><Copy size={12} /> Salin</>
                                                )}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-xs text-amber-700 dark:text-amber-300">
                                            ⚠️ User takda nombor WhatsApp dalam profile.
                                        </div>
                                    )}
                                </div>

                                {/* Ayat Reminder */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                        Ayat Reminder
                                    </label>
                                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 mb-2 max-h-64 overflow-y-auto">
                                        <pre className="text-[11px] text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">
{reminderText}
                                        </pre>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => copyToClipboard(reminderText, 'text')}
                                        className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition ${
                                            reminderModal.copyState === 'text'
                                                ? 'bg-emerald-600 text-white'
                                                : 'bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200'
                                        }`}
                                    >
                                        {reminderModal.copyState === 'text' ? (
                                            <><Check size={12} strokeWidth={3} /> Ayat Disalin</>
                                        ) : (
                                            <><Copy size={12} /> Copy Ayat Lengkap</>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Footer */}
                            {/* BARU: Manual mark section — sentiasa explicit, takda auto-mark */}
                            <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30">
                                {item.lastReminderManualAt || item.lastReminderSentAt1d || item.lastReminderSentAt7d ? (
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
                                            <Check size={14} strokeWidth={3} />
                                            <span className="font-semibold">Dah ditandakan sebagai sudah remind.</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleMarkReminderManually(item.uid, true)}
                                            disabled={sending}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-bold disabled:opacity-50"
                                        >
                                            <X size={12} />
                                            Buang tanda — silap tadi?
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            💡 Lepas selesai blast manual via WhatsApp, klik button kanan ni untuk tandakan.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => handleMarkReminderManually(item.uid, false)}
                                            disabled={sending}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50"
                                        >
                                            {sending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={3} />}
                                            Saya dah hantar reminder
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200 dark:border-slate-700 flex-wrap">
                                <button
                                    type="button"
                                    onClick={() => setReminderModal(null)}
                                    className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                                >
                                    Tutup
                                </button>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        await handleSendReminderEmail(item.uid);
                                        setReminderModal(null);
                                    }}
                                    disabled={sending}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold disabled:opacity-50"
                                >
                                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                                    Hantar Email
                                </button>
                                {waLink ? (
                                    <a
                                        href={waLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold"
                                    >
                                        <MessageCircle size={14} />
                                        Buka WhatsApp
                                    </a>
                                ) : (
                                    <span
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-400 text-sm font-bold cursor-not-allowed"
                                        title="Takda nombor WhatsApp"
                                    >
                                        <MessageCircle size={14} />
                                        Buka WhatsApp
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ═══ Activated Modal: muncul selepas Aktifkan — Copy Wasap + Buka WhatsApp ═══ */}
            {activatedModal && (() => {
                const waNum = normalizeWaNumber(activatedModal.whatsappNumber);
                const waUrl = waNum ? buildActivationDeeplink({
                    customerWhatsapp: activatedModal.whatsappNumber,
                    email: activatedModal.email,
                    licenseKey: activatedModal.licenseKey,
                    expiresAt: activatedModal.expiresAt,
                    durationDays: activatedModal.durationDays || 30,
                    isRenewal: activatedModal.isRenewal === true
                }) : null;
                return (
                    <div
                        className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4"
                        onClick={() => setActivatedModal(null)}
                    >
                        <div
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-center mb-3">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center shadow-lg">
                                    <Check className="w-8 h-8 text-white" strokeWidth={3} />
                                </div>
                            </div>
                            <h3 className="text-center text-lg font-extrabold text-slate-800 dark:text-white mb-1">Akaun Diaktifkan 🎉</h3>
                            <p className="text-center text-xs text-slate-500 mb-4">{activatedModal.email}</p>

                            <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-3 mb-4 space-y-1.5">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-slate-500">License Key:</span>
                                    <span className="font-mono font-bold text-slate-800 dark:text-white">{activatedModal.licenseKey}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-slate-500">Luput:</span>
                                    <span className="font-semibold text-slate-700 dark:text-slate-300">
                                        {new Date(activatedModal.expiresAt).toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-slate-500">WhatsApp:</span>
                                    <span className={`font-mono ${waNum ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                        {waNum ? `+${waNum}` : '— takda nombor —'}
                                    </span>
                                </div>
                            </div>

                            {/* Action buttons */}
                            <div className="space-y-2">
                                <button
                                    type="button"
                                    onClick={handleCopyWasapMessage}
                                    className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition ${
                                        copyState === 'copied'
                                            ? 'bg-emerald-600 text-white'
                                            : 'bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white'
                                    }`}
                                >
                                    {copyState === 'copied' ? (
                                        <><Check size={14} strokeWidth={3} /> Ayat Disalin!</>
                                    ) : (
                                        <><Copy size={14} /> Copy Ayat WhatsApp</>
                                    )}
                                </button>

                                {waUrl ? (
                                    <a
                                        href={waUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white shadow-md transition"
                                    >
                                        <MessageCircle size={14} fill="currentColor" />
                                        Buka WhatsApp Customer
                                    </a>
                                ) : (
                                    <div className="w-full py-2.5 rounded-xl text-center text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                        Customer tiada nombor WhatsApp — copy ayat di atas dan paste manual.
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={() => setActivatedModal(null)}
                                    className="w-full py-2.5 rounded-xl font-semibold text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                                >
                                    Tutup
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ═══ Lightbox: fullscreen image preview untuk bukti pembayaran ═══ */}
            {lightboxUrl && (
                <div
                    className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4 animate-[fadeIn_0.15s_ease-out]"
                    onClick={() => setLightboxUrl(null)}
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
                        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                        aria-label="Tutup"
                    >
                        <X size={20} />
                    </button>
                    <img
                        src={lightboxUrl}
                        alt="Bukti pembayaran"
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
};

export default AdminView;
